// Каталог грамматических тем тренажёра (.scratch/grammar-trainer). ~35
// ключевых тем вместо 228 тегов уроков (половина из них — лексика). Какие
// правила курса тренирует тема и какой урок её открывает — в реестре
// lib/grammar/rules.ts (rule.topic); логика — lib/grammar/line.ts.
//
// У каждой темы: правило (короткими абзацами), формула, разобранные примеры
// для карточки правила (строятся движком — всегда согласованы с заданиями) и
// генератор заданий трёх уровней сложности: 1 — один аффикс, 2 — ловушки
// посложнее (отрицание, связки, чередования), 3 — цепочка или предложение.
import { MINI_TOPICS } from './mini-topics'
import { ERROR_TOPICS } from './ru-errors'
import {
  CASE_RU,
  DERIVED_RU,
  PRONOUNS,
  Word,
  abilityForm,
  causativeForm,
  compoundVerbForm,
  copulaForm,
  derivedForm,
  isOddPronoun,
  pronounForm,
  nounForm,
  passiveVerbForm,
  PERSON_RU,
  POSS_RU,
  predicate,
  questionParticle,
  TENSE_RU,
  verbForm,
  verbQuestion,
  type AbilityKind,
  type Case,
  type Derived,
  type NounLike,
  type Person,
  type PronounCase,
  type Tense,
  type WordForm,
  markLastVowel,
  mv,
  type VerbLike,
} from './morph'
import { ADJECTIVES, CAUS_VERBS, NOUNS, VERBS, type Gender, type Noun, type Verb } from './lexicon'
import {
  affix,
  breakdownFrom,
  capitalize,
  example,
  isPassed,
  passedOnly,
  plainLabels,
  slotsFrom,
  translate,
  withRules,
  type AffixExercise,
  type Exercise,
  type GenCtx,
  type RuleExample,
  type TransformExercise,
  sentenceRu,
} from './exercise'
import { RULE_BY_ID, knownItems, knownWords, lessonRank, wordKey } from './line'
import { RULES } from './rules'
import { cell, type RuleStep, type TableCell } from './rule-table'
import { GOV_CASE_RULE, GOV_CELL, GOV_POSTS, GOV_SAME, GOV_TRAPS, govExercise, govPool, govTranslate, govWordsKnown, passedGovVerbs, type GovCase, type GovVerb } from './government'
import { TENSE_CELL, TENSE_NAME, markersOf, passedTenses, tenseExercise, tenseItem, tenseTranslate } from './tense-markers'
import type { DtSuffix } from './voiceless'
import { abilityPhraseRu, compoundPhraseRu, passivePhraseRu, predicateRu, verbInfRu, verbPhraseRu, type PredicateWord } from './ru-phrase'
import { fillEnding, translateEnding } from './drill-kinds'
import { CORE_TOPICS_2 } from './topics-core-2'

export type Level = 'A1' | 'A2' | 'B1'
// 2 — e/a, 4 — i/ı/u/ü.
export type HarmonyMode = 2 | 4
export type Difficulty = 1 | 2 | 3

export type Topic = {
  id: string
  level: Level
  title: string
  formula: string
  // Пункты объяснения; у пункта может быть таблица (lib/grammar/rule-table).
  rule: RuleStep[]
  // Основа основ (гармония гласных): открыта сразу, без урока.
  alwaysOpen?: boolean
  // Мини-тема: задания написаны по уроку, без генератора (lib/grammar/mini-topics.ts).
  mini?: boolean
  // Тема модуля «Ошибки русскоговорящих» (lib/grammar/ru-errors.ts): своего
  // правила в реестре у неё нет, открывается правилом opensWith.
  errors?: boolean
  opensWith?: string
  // Наглядная таблица гласных в объяснении — сразу после формулы, в той же
  // карточке. afterRule больше не используется (оставлен для совместимости).
  visual?: { kind: HarmonyMode; afterRule: number }
  // Разминка перед разбором с подсказкой: сортировка гласных и «найди последнюю».
  warmup?: HarmonyMode
  // Разминка на глухие согласные (d → t) — там же, для аффиксов на D.
  dtWarmup?: DtSuffix
  // Своя игра на скорость по теме: путь страницы.
  speedGame?: string
  // ctx — пройденные правила (lib/grammar/line.ts): примеры и задания только
  // из них. Без ctx — всё, как при тренировке темы заранее.
  examples: (ctx?: GenCtx) => RuleExample[]
  generate: (d: Difficulty, ctx?: GenCtx) => Exercise
  // Цель разговора с AI (вариант 6): что тренируем и как это распознать.
  chatGoal?: { focus: string; detect: string }
}

// ── Случайность ──────────────────────────────────────────────────────────────

const pick = <T,>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)]
const person = (except: Person[] = []): Person => pick(([0, 1, 2, 3, 4, 5] as Person[]).filter((p) => !except.includes(p)))

const PRON = ['Ben', 'Sen', 'O', 'Biz', 'Siz', 'Onlar'] as const
const PRON_LOW = ['ben', 'sen', 'o', 'biz', 'siz', 'onlar'] as const

const places = NOUNS.filter((n) => n.place)
const objects = NOUNS.filter((n) => n.object)
const company = NOUNS.filter((n) => n.company)
const softNouns = NOUNS.filter((n) => n.soft)
const vowelFinal = NOUNS.filter((n) => /[aeıioöuü]$/.test(n.tr))
const hardFinal = NOUNS.filter((n) => /[çfhkpsşt]$/.test(n.tr))
const people = NOUNS.filter((n) => n.person)
const states = ADJECTIVES.filter((a) => a.state)
const predicates: (NounLike & { ru: string })[] = [...people, ...states]
const withComplement = VERBS.filter((v) => v.complement)
const vowelStemVerbs = VERBS.filter((v) => /[aeıioöuü]$/.test(v.stem))
const hardStemVerbs = VERBS.filter((v) => /[çfhkpsşt]$/.test(v.stem))
const aoristExceptions = VERBS.filter((v) => v.aorist === 'ir')

// ── Только пройденное (.scratch/grammar-unify, тикет 03) ─────────────────────
// Вариант берём, только если его правило (id реестра rules.ts) пройдено.

// Глаголы, у которых дополнение в пройденном падеже: «Ben eve ___» ждёт урока
// дательного (lexicon.ts, complement.rule), — и из словаря ученика (тикет 08).
const known = (verbs: readonly Verb[], ctx?: GenCtx) => knownWords(passedOnly(verbs, (v) => v.complement?.rule, ctx), ctx)

// Лица, чьё окончание уже объяснено: ruleOf — урок окончания этого лица.
const personsBy = (ruleOf: (p: Person) => string | undefined, ctx?: GenCtx, from: Person[] = [0, 1, 2, 3, 4, 5]) => passedOnly(from, ruleOf, ctx)

// «Я — …»: sen / siz — в уроке 5 (nasılsın, nasılsınız), остальные — с таблицей лиц.
// «Я», «ты», «вы» — урок 4 (.scratch/a1-pace), «он», «мы», «они» — урок всех лиц.
export const copulaRule = (p: Person) => (p === 0 || p === 1 || p === 4 ? 'sen-siz' : 'personal-suffix-to-be')
// «Мой / твой» (adım, adın) показаны в уроке 4, остальные — в уроке притяжательных.
const possRule = (p: Person) => (p <= 1 ? 'adim-adin-preview' : 'possessive-suffix-intro')
// Притяжательное перед падежом: у «его» и «их» появляется n (evinde, evlerinde).
const possCaseRule = (p: Person) => (p === 2 || p === 5 ? 'possessive-n-buffer' : 'possessive-suffix-intro')
// Лицо, которое переезжает на mi или на değil; «он» — без окончания.
// «Ты» и «вы» на mi / değil объяснены вместе с самим правилом (уроки 7 и 13,
// .scratch/a1-pace), «я» на değil тоже; остальные лица — в уроке лиц.
const MI_INTRO = 'question-particle-mi-intro'
const miPersonRule = (p: Person) => (p === 2 ? undefined : p === 1 || p === 4 ? 'question-mi-sen-siz' : 'question-mi-person')
const degilPersonRule = (p: Person) => (p === 2 ? undefined : p === 0 || p === 1 || p === 4 ? 'negation-degil' : 'degil-person')
// Время пассива: урок, где оно разобрано.
const PASSIVE_RULE: Record<'present' | 'aorist' | 'past', string> = {
  past: 'passive-voice-il-in',
  aorist: 'passive-voice-signage',
  present: 'passive-voice-formal-news',
}
// Отрицание в -yor (sevmiyorum): в уроке 14 — только на слух, объяснено позже.
const NEG_YOR = 'present-negative-miyor'

// Урок, где объяснено отрицание времени: до него отрицания нет ни в ответе,
// ни в исходной фразе трансформации.
const NEG_RULE: Partial<Record<Tense, string>> = {
  present: NEG_YOR,
  past: 'past-tense-di-negative-question',
  future: 'future-tense-negative',
  aorist: 'aorist-negative-mez',
  necessity: 'obligation-mali-meli-review',
  evidential: 'evidential-past-mis-negative-question',
  pluperfect: 'pluperfect-misti-narrative',
  habitualPast: 'habitual-past-irdi-nostalgia',
  futurePast: 'future-in-past-acakti-unfulfilled',
}

// Базовое правило времени (реестр, у правила формы есть номер в каталоге): его
// несёт каждое задание темы-времени, отрицание и вопрос добавляют свои
// (NEG_RULE, miPersonRule). По тегу правила тренировка одного правила
// отбирает задания темы, поэтому без тега задание не найти.
const TENSE_BASE: Record<Tense, string> = {
  present: 'present-continuous-yor',
  past: 'past-tense-di',
  future: 'future-tense-ecek-acak',
  aorist: 'aorist-ir-intro',
  evidential: 'evidential-past-mis-affirmative',
  necessity: 'obligation-mali-meli',
  conditional: 'conditional-sa-advice-keske',
  realConditional: 'conditional-sa-real',
  ability: 'ability-abilir-ebilir',
  pastContinuous: 'past-continuous-yordu',
  pluperfect: 'pluperfect-misti',
  habitualPast: 'habitual-past-irdi',
  futurePast: 'future-in-past-acakti',
  unrealConditional: 'unreal-conditional-saydi',
  optative: 'optative-eyim-elim',
  imperative: 'imperative-full',
}
// То же для падежей: тема падежа несёт его правило в каждом задании, время
// рамки (frame.rule) и притяжательное перед падежом - сверх него.
const CASE_BASE: Record<Case, string> = {
  loc: 'locative-case',
  dat: 'dative-case-e-a',
  abl: 'ablative-case-intro',
  acc: 'case-system-review-accusative',
  gen: 'genitive-intro',
  ins: 'instrumental-ile',
}

// Примеры карточки правила: пример с правилом, которое ещё не пройдено, не
// показываем. Пара [пример, id правила] — пример на это правило.
const examplesFor = (ctx: GenCtx | undefined, list: (RuleExample | [RuleExample, string])[]): RuleExample[] =>
  list.filter((e) => !Array.isArray(e) || isPassed(ctx, e[1])).map((e) => (Array.isArray(e) ? e[0] : e))

// Родительный падеж русских названий — для контекста изафета («дверь дома»).
const RU_GEN: Record<string, string> = {
  ev: 'дома', okul: 'школы', oda: 'комнаты', otel: 'отеля', müze: 'музея', hastane: 'больницы',
  lokanta: 'ресторана', market: 'магазина', ofis: 'офиса', bahçe: 'сада', araba: 'машины',
  otobüs: 'автобуса', istasyon: 'станции',
}
const owners = NOUNS.filter((n) => RU_GEN[n.tr])

// ── Сборка заданий ───────────────────────────────────────────────────────────

const labels = (...xs: (string | false | undefined)[]) => xs.filter(Boolean).join(' · ')

// Русское притяжательное, согласованное с родом перевода: «моя школа», «мои часы».
const POSS_BY_GENDER: Record<Gender, string[]> = {
  m: ['мой', 'твой', 'его/её', 'наш', 'ваш', 'их'],
  f: ['моя', 'твоя', 'его/её', 'наша', 'ваша', 'их'],
  n: ['моё', 'твоё', 'его/её', 'наше', 'ваше', 'их'],
  pl: ['мои', 'твои', 'его/её', 'наши', 'ваши', 'их'],
}
const possRu = (p: Person, g: Gender = 'm') => POSS_BY_GENDER[g][p]
// «Я вижу своё яблоко»: в винительном у предметов женского рода «мою, твою»,
// у «я» при «я вижу, я беру» по-русски «свой» (тикет 21: было «Я вижу это
// яблоко. (моё яблоко)»).
const POSS_ACC: Record<Gender, string[]> = {
  m: ['свой', 'твой', 'его/её', 'наш', 'ваш', 'их'],
  f: ['свою', 'твою', 'его/её', 'нашу', 'вашу', 'их'],
  n: ['своё', 'твоё', 'его/её', 'наше', 'ваше', 'их'],
  pl: ['свои', 'твои', 'его/её', 'наши', 'ваши', 'их'],
}

// Подсказка «кто + что делает» без попытки спрягать русский глагол:
// «они + „знать ответ“».
const who = (p: Person, what: string) => `${PERSON_RU[p]} + "${what}"`
// Основа и перевод со словарной формой: "bul - находить (bulmak)", чтобы было
// видно, что окончания клеятся к слову без -mak (решение 08.10).
const verbBase = (v: Verb) => ({ tr: v.stem, ru: `${v.ru} (${derivedForm(v, 'infinitive').word})` })

function verbSentence(v: Verb, tense: Tense, p: Person, opts: { neg?: boolean; q?: boolean } = {}): string {
  const vp = opts.q ? verbQuestion(v, tense, p, !!opts.neg) : verbForm(v, tense, p, !!opts.neg).word
  return [PRON[p], v.complement?.tr, vp].filter(Boolean).join(' ') + (opts.q ? '?' : '.')
}

// Турецкий опускает местоимение — ответ без него тоже верный.
function withoutPronoun(sentence: string): string {
  return capitalize(sentence.split(' ').slice(1).join(' '))
}

function verbHint(v: Verb): string {
  return v.complement ? `${v.ru} ${v.complement.ru}` : v.ru
}

// Разбор глагольной формы ответа (для вопроса — вместе с частицей).
function verbBreakdown(v: Verb, tense: Tense, p: Person, opts: { neg?: boolean; q?: boolean }) {
  const personOnMi = opts.q && tense !== 'past' && tense !== 'conditional' && p !== 5
  const form = verbForm(v, tense, personOnMi ? 2 : p, !!opts.neg)
  const parts = breakdownFrom(form)
  if (opts.q) parts.push({ text: ' ', label: '', why: '' }, ...breakdownFrom(questionParticle(form.word, personOnMi ? p : null)))
  return parts
}

function transformVerb(
  topicId: string,
  v: Verb,
  from: { tense: Tense; p: Person; neg?: boolean; q?: boolean },
  to: { tense: Tense; p: Person; neg?: boolean; q?: boolean },
  instruction: string,
): TransformExercise {
  const answer = verbSentence(v, to.tense, to.p, to)
  return withRules<TransformExercise>({
    kind: 'transform',
    topicId,
    source: verbSentence(v, from.tense, from.p, from),
    sourceRu: sentenceRu(verbPhraseRu(v, from.tense, from.p, { negative: from.neg, question: from.q }) ?? verbHint(v)),
    instruction,
    answer,
    variants: [withoutPronoun(answer)],
    breakdown: verbBreakdown(v, to.tense, to.p, to),
  }, TENSE_BASE[to.tense], from.tense !== to.tense && TENSE_BASE[from.tense], v.complement?.rule)
}

function verbAffix(topicId: string, v: Verb, tense: Tense, p: Person, neg = false): AffixExercise {
  const form = verbForm(v, tense, p, neg)
  const ctx = v.complement ? { tr: `${PRON[p]} ${v.complement.tr} ___.`, ru: verbPhraseRu(v, tense, p, { negative: neg }) ?? who(p, verbHint(v)) } : undefined
  return withRules(affix(topicId, form, verbBase(v), labels(PRON_LOW[p], TENSE_RU[tense], neg && 'отрицание'), ctx), TENSE_BASE[tense], v.complement?.rule)
}

// Задание на тему-время: 1 — утверждение, 2 — отрицание/трудные основы,
// 3 — трансформация из другого времени. Отрицание — после его урока (NEG_RULE).
const NO_STATIVE = new Set<Tense>(['futurePast', 'optative', 'imperative', 'necessity', 'conditional', 'realConditional', 'unrealConditional'])

function tenseGenerator(
  topicId: string,
  tense: Tense,
  opts: {
    from: Tense
    instruction: string
    d2Verbs?: () => Verb[]
    persons?: Person[]
    allowNegative?: boolean
  },
) {
  const persons = opts.persons ?? [0, 1, 2, 3, 4, 5]
  return (d: Difficulty, ctx?: GenCtx): Exercise => {
    const p = pick(persons)
    // «Собирался знать», «давайте не будем знать»: глаголы состояния не
    // годятся для намерения, побуждения и долженствования.
    const verbs = known(NO_STATIVE.has(tense) ? VERBS.filter((v) => !v.stative) : VERBS, ctx)
    const canNeg = opts.allowNegative !== false && isPassed(ctx, NEG_RULE[tense])
    // Урок отрицания и уроки после него: отрицание почти всегда.
    const focusNeg = canNeg && !!NEG_RULE[tense] && !!ctx?.focus?.has(NEG_RULE[tense]!)
    if (d === 1) {
      const neg = focusNeg && Math.random() < 0.75
      return withRules(verbAffix(topicId, pick(verbs), tense, p, neg), neg && NEG_RULE[tense])
    }
    if (d === 2) {
      const d2 = opts.d2Verbs?.()
      const pool = d2 ? verbs.filter((v) => d2.includes(v)) : verbs
      const neg = canNeg && Math.random() < (focusNeg ? 0.75 : 0.5)
      return withRules(verbAffix(topicId, pick(pool.length ? pool : verbs), tense, p, neg), neg && NEG_RULE[tense])
    }
    const v = pick(known(withComplement, ctx))
    const neg = canNeg && isPassed(ctx, NEG_RULE[opts.from]) && Math.random() < 0.3
    // Исходник с отрицанием: ученик должен понять, что отрицание сохраняется.
    const instruction = neg ? `${opts.instruction}, отрицание сохраните` : opts.instruction
    return withRules(transformVerb(topicId, v, { tense: opts.from, p, neg }, { tense, p, neg }, instruction), neg && NEG_RULE[tense], neg && NEG_RULE[opts.from])
  }
}

// Контексты падежей: предложение с пропуском, перевод и слова, с которыми
// фраза осмысленна («беру» — только то, что можно взять в руки). rule — время
// глагола в рамке: «Yarın ___ gideceğiz» ждёт урока будущего.
// ru - перевод фразы со словом задания; пока у слова нет русской формы, остаётся подпись-вопрос.
type CaseFrame = { tr: string; ru: (w: Noun) => string; pool: () => Noun[]; rule?: string }
const RU_ON: Record<string, string> = { masa: 'на столе', dolap: 'на шкафу', yatak: 'на кровати' }
const RU_IN: Record<string, string> = { çanta: 'в сумке', dolap: 'в шкафу' }
const RU_BEFORE: Record<string, string> = { ev: 'домом', okul: 'школой', otel: 'отелем', müze: 'музеем', hastane: 'больницей', lokanta: 'рестораном', market: 'магазином', ofis: 'офисом', bahçe: 'садом', istasyon: 'станцией', oda: 'комнатой' }
const RU_AT: Record<string, string> = { masa: 'на столе', dolap: 'в шкафу', çanta: 'в сумке', kapı: 'у двери' }
const NOT_PORTABLE = new Set(['dağ', 'köprü', 'ağaç', 'deniz', 'ev', 'otobüs', 'yatak', 'dolap', 'kapı', 'pencere', 'masa', 'araba'])
const relatives = NOUNS.filter((x) => ['anne', 'baba', 'kardeş', 'arkadaş', 'çocuk'].includes(x.tr))
const TRANSPORT = new Set(['otobüs', 'taksi', 'tren', 'araba'])

const CASE_FRAMES: Record<Case, CaseFrame[]> = {
  loc: [
    // Без глагола: годится с урока "где" (28), когда глаголов ещё мало.
    { tr: 'Anahtar ___.', ru: (w) => `Ключ ${w.ruLoc ?? RU_AT[w.tr] ?? '(где?)'}.`, pool: () => [...places.filter((x) => !['deniz', 'göl', 'köy', 'sokak'].includes(x.tr)), ...NOUNS.filter((x) => ['masa', 'dolap', 'çanta', 'kapı'].includes(x.tr))] },
    { tr: '___ bekliyorum.', ru: (w) => `Я жду ${w.ruLoc ?? '(где?)'}.`, pool: () => places, rule: 'present-continuous-yor' },
    { tr: '___ kahve içiyoruz.', ru: (w) => `Мы пьём кофе ${w.ruLoc ?? '(где?)'}.`, pool: () => places.filter((x) => !['göl', 'hastane', 'istasyon', 'eczane', 'banka', 'cami', 'banyo'].includes(x.tr)), rule: 'present-continuous-yor' },
  ],
  dat: [
    { tr: '___ gidiyorum.', ru: (w) => `Я иду ${w.ruDat ?? '(куда?)'}.`, pool: () => places, rule: 'present-continuous-yor' },
    { tr: 'Yarın ___ gideceğiz.', ru: (w) => `Завтра мы пойдём ${w.ruDat ?? '(куда?)'}.`, pool: () => places, rule: 'future-tense-ecek-acak' },
    { tr: 'Akşam ___ dönüyorum.', ru: (w) => `Вечером я возвращаюсь ${w.ruDat ?? '(куда?)'}.`, pool: () => places.filter((x) => !['göl', 'meydan', 'deniz'].includes(x.tr)), rule: 'present-continuous-yor' },
    { tr: 'Bu akşam ___ gidiyoruz.', ru: (w) => `Сегодня вечером мы идём ${w.ruDat ?? '(куда?)'}.`, pool: () => places.filter((x) => !['banyo', 'salon', 'mutfak'].includes(x.tr)), rule: 'present-continuous-yor' },
  ],
  abl: [
    { tr: '___ geliyorum.', ru: (w) => `Я иду ${w.ruAbl ?? '(откуда?)'}.`, pool: () => places, rule: 'present-continuous-yor' },
    { tr: 'Saat beşte ___ çıktım.', ru: (w) => `В пять я вышел ${w.ruAbl ?? '(откуда?)'}.`, pool: () => places.filter((x) => !['sokak', 'göl', 'köy'].includes(x.tr)), rule: 'past-tense-di' },
    { tr: 'Şimdi ___ çıkıyorum.', ru: (w) => `Сейчас я выхожу ${w.ruAbl ?? '(откуда?)'}.`, pool: () => places.filter((x) => !['sokak', 'göl', 'köy', 'deniz', 'meydan'].includes(x.tr)), rule: 'present-continuous-yor' },
    { tr: 'Annem ___ geliyor.', ru: (w) => `Мама идёт ${w.ruAbl ?? '(откуда?)'}.`, pool: () => places.filter((x) => !['göl', 'banyo', 'salon'].includes(x.tr)), rule: 'present-continuous-yor' },
  ],
  acc: [
    { tr: '___ görüyorum.', ru: (w) => `Я вижу ${w.ruAcc ?? '(что? - конкретное)'}.`, pool: () => objects, rule: 'present-continuous-yor' },
    { tr: '___ alıyorum.', ru: (w) => `Я беру ${w.ruAcc ?? '(что? - вот это)'}.`, pool: () => objects.filter((x) => !NOT_PORTABLE.has(x.tr)), rule: 'present-continuous-yor' },
  ],
  gen: [
    { tr: '___ kapısı açık.', ru: (w) => `Дверь ${w.ruGen ?? RU_GEN[w.tr] ?? '(чего?)'} открыта.`, pool: () => owners },
    // «Где именно» — послелоги урока родительного: masanın üstünde, evin önünde.
    { tr: 'Anahtar ___ üstünde.', ru: (w) => `Ключ ${RU_ON[w.tr] ?? 'на (чём?)'}.`, pool: () => NOUNS.filter((x) => ['masa', 'dolap', 'yatak'].includes(x.tr)), rule: 'spatial-postpositions' },
    { tr: 'Telefon ___ içinde.', ru: (w) => `Телефон ${RU_IN[w.tr] ?? 'в (чём?)'}.`, pool: () => NOUNS.filter((x) => ['çanta', 'dolap'].includes(x.tr)), rule: 'spatial-postpositions' },
    { tr: '___ önünde bekliyorum.', ru: (w) => `Я жду перед ${RU_BEFORE[w.tr] ?? '(чем?)'}.`, pool: () => owners.filter((x) => x.place), rule: 'spatial-postpositions' },
    { tr: '___ yanında oturuyorum.', ru: (w) => `Я сижу рядом ${w.ruIns ?? '(с кем?)'}.`, pool: () => relatives, rule: 'spatial-postpositions' },
  ],
  ins: [
    { tr: '___ geliyorum.', ru: (w) => `Я приезжаю ${w.ruIns ?? '(с кем? на чём?)'}.`, pool: () => company, rule: 'present-continuous-yor' },
    { tr: '___ konuşuyorum.', ru: (w) => `Я разговариваю ${w.ruIns ?? '(с кем?)'}.`, pool: () => relatives, rule: 'present-continuous-yor' },
    { tr: '___ gidiyorum.', ru: (w) => `${TRANSPORT.has(w.tr) ? 'Я еду' : 'Я иду'} ${w.ruIns ?? '(с кем? на чём?)'}.`, pool: () => company, rule: 'present-continuous-yor' },
    { tr: 'Bugün ___ geliyoruz.', ru: (w) => `Сегодня мы ${TRANSPORT.has(w.tr) ? 'приезжаем' : 'приходим'} ${w.ruIns ?? '(с кем? на чём?)'}.`, pool: () => company, rule: 'present-continuous-yor' },
  ],
}

// Контраст винительного: «Kitap okuyorum» — читаю книгу вообще, занят чтением;
// «Kitabı okuyorum» — читаю ту самую книгу. Пары взяты готовыми: нужны слова,
// которые в турецком стоят при глаголе без окончания.
const ACC_CONTRAST: { w: NounLike & { ru: string }; after: string; bare: string; def: string }[] = [
  { w: { tr: 'kitap', soft: 'kitab', ru: 'книга' }, after: ' okuyorum', bare: 'Читаю книгу - вообще, занят чтением.', def: 'ту самую книгу, о которой шла речь' },
  { w: { tr: 'çay', ru: 'чай' }, after: ' içiyorum', bare: 'Пью чай - вообще, не воду.', def: 'тот самый чай, который вам принесли' },
  { w: { tr: 'yemek', soft: 'yemeğ', ru: 'еда' }, after: ' yapıyorum', bare: 'Готовлю еду - вообще, занят готовкой.', def: 'то самое блюдо, которое задумали' },
  { w: { tr: 'mektup', soft: 'mektub', ru: 'письмо' }, after: ' yazıyorum', bare: 'Пишу письмо - вообще, занят письмом.', def: 'то самое письмо, которое обещали' },
  { w: { tr: 'ekmek', soft: 'ekmeğ', ru: 'хлеб' }, after: ' alıyorum', bare: 'Покупаю хлеб - вообще, как всегда.', def: 'тот самый хлеб, который выбрали' },
  { w: { tr: 'film', ru: 'фильм' }, after: ' izliyorum', bare: 'Смотрю фильм - вообще, занят просмотром.', def: 'тот самый фильм, о котором говорили' },
]

function accusativeContrast(): TransformExercise {
  const it = pick(ACC_CONTRAST)
  const form = nounForm(it.w, { case: 'acc' })
  return withRules<TransformExercise>({
    kind: 'transform',
    topicId: 'accusative',
    source: `${capitalize(it.w.tr)}${it.after}.`,
    sourceRu: it.bare,
    instruction: `Скажите про ${it.def} - добавьте окончание`,
    answer: `${capitalize(form.word)}${it.after}.`,
    variants: [],
    breakdown: breakdownFrom(form),
  }, CASE_BASE.acc)
}

// possExtra — правило, без которого третьей ступени нет вовсе (винительный
// после притяжательного разобран своим уроком: evimi, evini).
// typed - темы A1 (где, куда, откуда, с кем): ещё вписать окончание, превратить и перевести.
function caseGenerator(topicId: string, c: Case, trickyPool: () => Noun[], possExtra?: string, typed = false) {
  const gen = (d: Difficulty, gctx?: GenCtx): Exercise => {
    const frame = pick(passedOnly(CASE_FRAMES[c], (f) => f.rule, gctx))
    // Смена согласной (mutfak → mutfağa) - правило A2: до него таких слов в -e / -ı нет.
    const soft = (c === 'dat' || c === 'acc') && !isPassed(gctx, 'consonant-softening')
    const pool = knownWords(frame.pool(), gctx).filter((x) => !(soft && x.soft))
    const ctxOf = (w: Noun) => ({ tr: frame.tr, ru: frame.ru(w) })
    // Печатные виды - только там, где у слова есть русская форма во фразе
    // (иначе вместо перевода осталась бы подпись-вопрос «(где?)»).
    const typedPool = typed ? pool.filter((w) => !frame.ru(w).includes('(')) : []
    if (typedPool.length && d === 2 && half()) {
      const w = pick(typedPool)
      const form = nounForm(w, { case: c })
      return withRules(fillEnding(topicId, { word: w.tr, ru: frame.ru(w), answer: fillFrame(frame.tr, form.word), form, ask: CASE_RU[c] }), CASE_BASE[c], frame.rule)
    }
    // Третья ступень: притяжательное + падеж (A2), пока его нет - превратить или перевести.
    const possOk = isPassed(gctx, possExtra) && personsBy(possCaseRule, gctx).length > 0
    if (typedPool.length && d === 3 && (!possOk || half())) {
      const w = pick(typedPool)
      return half() ? caseTransform(topicId, c, w, frame, gctx) : caseTranslate(topicId, c, w, frame)
    }
    if (d === 1) {
      const w = pick(pool)
      return withRules(affix(topicId, nounForm(w, { case: c }), w, CASE_RU[c], ctxOf(w)), CASE_BASE[c], frame.rule)
    }
    if (d === 2) {
      const tricky = pool.filter((x) => trickyPool().includes(x))
      // Трудных слов в начале курса два-три: иногда берём и простые, чтобы шаг не повторялся.
      const w = pick(tricky.length && (tricky.length >= 4 || Math.random() < 0.6) ? tricky : pool)
      return withRules(affix(topicId, nounForm(w, { case: c }), w, CASE_RU[c], ctxOf(w)), CASE_BASE[c], frame.rule)
    }
    // Притяжательное + падеж — только лица, чьё окончание уже пройдено; нет
    // таких — ступень ниже.
    const persons = isPassed(gctx, possExtra) ? personsBy(possCaseRule, gctx) : []
    if (!persons.length) return gen(2, gctx)
    const w = pick(pool)
    const p = pick(persons)
    const ex = affix(topicId, nounForm(w, { poss: p, case: c }), w, labels(POSS_RU[p], CASE_RU[c]), {
      tr: frame.tr,
      ru: c === 'acc' && w.ruAcc?.startsWith('эт')
        ? frame.ru({ ...w, ruAcc: w.ruAcc.replace(/^\S+/, POSS_ACC[w.g ?? 'm'][p]) })
        : `${frame.ru(w)} (${possRu(p, w.g)} ${w.ru})`,
    })
    return withRules(ex, CASE_BASE[c], frame.rule, possCaseRule(p), possExtra)
  }
  return gen
}

// Перевод с русского: «Я жду в парке.» - Park + ta bekliyorum.
function caseTranslate(topicId: string, c: Case, w: Noun, frame: CaseFrame): TransformExercise {
  const form = nounForm(w, { case: c })
  return withRules(translateEnding(topicId, { ru: frame.ru(w), answer: fillFrame(frame.tr, form.word), form }), CASE_BASE[c], frame.rule)
}

// Превратить: «где» в «куда» (Parkta bekliyorum - Parka gidiyorum), «куда» в
// «откуда» - после урока прошлого падежа; иначе исправить фразу, где слову
// не хватает окончания.
function caseTransform(topicId: string, c: Case, w: Noun, frame: CaseFrame, ctx?: GenCtx): TransformExercise {
  const to = nounForm(w, { case: c })
  const yor = isPassed(ctx, 'present-continuous-yor')
  const plainStem = !w.soft || isPassed(ctx, 'consonant-softening')
  const make = (source: string, sourceRu: string, instruction: string, answer: string, ...rules: (string | undefined)[]) =>
    withRules<TransformExercise>({ kind: 'transform', topicId, source, sourceRu, instruction, answer, variants: [], breakdown: breakdownFrom(to), baseRu: w.ru }, CASE_BASE[c], ...rules)
  if (c === 'dat' && w.place && w.ruLoc && w.ruDat && yor && isPassed(ctx, 'locative-case') && half()) {
    const from = nounForm(w, { case: 'loc' }).word
    return make(`${capitalize(from)} bekliyorum.`, `Я жду ${w.ruLoc}.`, 'Скажите, что идёте туда (gidiyorum)', `${capitalize(to.word)} gidiyorum.`, 'present-continuous-yor', 'locative-case')
  }
  if (c === 'abl' && w.place && w.ruDat && w.ruAbl && yor && plainStem && isPassed(ctx, 'dative-case-e-a') && half()) {
    const from = nounForm(w, { case: 'dat' }).word
    return make(`${capitalize(from)} gidiyorum.`, `Я иду ${w.ruDat}.`, 'Скажите, что идёте оттуда (geliyorum)', `${capitalize(to.word)} geliyorum.`, 'present-continuous-yor', 'dative-case-e-a')
  }
  return make(fillFrame(frame.tr, w.tr), frame.ru(w), `Исправьте: слову не хватает окончания "${CASE_ASK[c]}"`, fillFrame(frame.tr, to.word), frame.rule)
}


// Производные слова из готового списка (-li/-siz, -lik/-ci): у них значение
// не выводится из правила, поэтому пары подобраны вручную.
type DerivItem = { base: NounLike & { ru: string }; suffix: string; label: string; ru: string; phrase?: { tr: string; ru: string } }

// nounForm не знает словообразовательных аффиксов — собираем движком напрямую.
function derivForm(item: DerivItem): WordForm {
  const w = new Word(item.base.tr, { soft: item.base.soft, front: item.base.front })
  w.add(item.suffix, item.label)
  return w.done()
}

// ── Темы ─────────────────────────────────────────────────────────────────────

const n = (tr: string) => NOUNS.find((x) => x.tr === tr)!
const adj = (tr: string) => ADJECTIVES.find((x) => x.tr === tr)!
const vb = (stem: string) => VERBS.find((x) => x.stem === stem)!

// ── Таблицы в объяснении ─────────────────────────────────────────────────────

const ALL: Person[] = [0, 1, 2, 3, 4, 5]
const WHO = ['ben\nя', 'sen\nты', 'o\nон / она', 'biz\nмы', 'siz\nвы', 'onlar\nони'] as const
// Строки таблицы «лицо → формы»: одна колонка на каждую функцию.
const byPerson = (cols: ((p: Person) => TableCell)[], persons: Person[] = ALL): TableCell[][] =>
  persons.map((p) => [WHO[p], ...cols.map((c) => c(p))])
// Подписи строк гармонии: «e i → i». Гласные размечены явно — в таблице
// VowelText не красит одиночные буквы (там «o» — это «он»).
const H4 = ['[e] [i] - [i]', '[a] [ı] - [ı]', '[o] [u] - [u]', '[ö] [ü] - [ü]'] as const
const H2 = ['[e] [i] [ö] [ü]', '[a] [ı] [o] [u]'] as const

// Ячейка-пример для темы «Какой падеж просит глагол»: слово с окончанием,
// хвост фразы и перевод второй строкой. Формы строит движок — таблица не
// может разойтись с заданиями.
const govCell = (word: NounLike, c: Case, after: string, ru: string): TableCell => [cell(nounForm(word, { case: c }), '', after), `\n${ru}`]

const govExampleCell = (v: GovVerb): TableCell => {
  const f = v.frames[0]
  // У послелога пример короткий («dersten sonra»), у глагола — с его формой.
  const after = v.post ? ` ${v.head}` : f.tr.split('___')[1].replace(/\.$/, '')
  const ru = v.post ? (v.trap?.says ?? f.ru) : f.ru.replace(/\.$/, '')
  return [cell(nounForm(f.word, { case: v.c }), '', after), `\n${ru}`]
}


// Урок каждого аффикса (id реестра rules.ts): -li — A1, -siz — A2, -ci — B1.
const SUFFIX_RULE: Record<string, string> = {
  lI: 'adjective-suffix-li',
  sIz: 'suffix-li-siz-productivity',
  lIk: 'suffix-lik-abstract-again',
  CI: 'productive-suffixes-ci-lik-sal-das',
}

const LI_SIZ: DerivItem[] = [
  { base: { tr: 'şeker', ru: 'сахар' }, suffix: 'lI', label: 'с ...', ru: 'с сахаром', phrase: { tr: '___ çay', ru: 'чай с сахаром' } },
  { base: { tr: 'şeker', ru: 'сахар' }, suffix: 'sIz', label: 'без ...', ru: 'без сахара', phrase: { tr: '___ kahve', ru: 'кофе без сахара' } },
  { base: { tr: 'süt', ru: 'молоко' }, suffix: 'lI', label: 'с ...', ru: 'с молоком', phrase: { tr: '___ kahve', ru: 'кофе с молоком' } },
  { base: { tr: 'süt', ru: 'молоко' }, suffix: 'sIz', label: 'без ...', ru: 'без молока' },
  { base: { tr: 'para', ru: 'деньги' }, suffix: 'lI', label: 'с ...', ru: 'платный', phrase: { tr: '___ otopark', ru: 'платная парковка' } },
  { base: { tr: 'para', ru: 'деньги' }, suffix: 'sIz', label: 'без ...', ru: 'бесплатный', phrase: { tr: '___ giriş', ru: 'бесплатный вход' } },
  { base: n('bahçe'), suffix: 'lI', label: 'с ...', ru: 'с садом', phrase: { tr: '___ ev', ru: 'дом с садом' } },
  { base: n('çocuk'), suffix: 'lI', label: 'с ...', ru: 'с детьми', phrase: { tr: '___ aile', ru: 'семья с детьми' } },
  { base: n('renk'), suffix: 'lI', label: 'с ...', ru: 'цветной', phrase: { tr: '___ kalem', ru: 'цветной карандаш' } },
  { base: n('renk'), suffix: 'sIz', label: 'без ...', ru: 'бесцветный' },
  { base: n('kalp'), suffix: 'sIz', label: 'без ...', ru: 'бессердечный' },
  { base: { tr: 'iş', ru: 'работа' }, suffix: 'sIz', label: 'без ...', ru: 'безработный' },
  { base: n('ev'), suffix: 'sIz', label: 'без ...', ru: 'бездомный', phrase: { tr: '___ kedi', ru: 'бездомная кошка' } },
  { base: { tr: 'tuz', ru: 'соль' }, suffix: 'lI', label: 'с ...', ru: 'солёный', phrase: { tr: '___ su', ru: 'солёная вода' } },
  { base: { tr: 'şans', ru: 'удача' }, suffix: 'lI', label: 'с ...', ru: 'удачливый' },
  { base: { tr: 'şans', ru: 'удача' }, suffix: 'sIz', label: 'без ...', ru: 'невезучий' },
  { base: { tr: 'akıl', ru: 'ум' }, suffix: 'lI', label: 'с ...', ru: 'умный', phrase: { tr: '___ telefon', ru: 'смартфон ("умный телефон")' } },
]

const LIK_CI: DerivItem[] = [
  { base: n('göz'), suffix: 'lIk', label: '-lik', ru: 'очки' },
  { base: n('kitap'), suffix: 'lIk', label: '-lik', ru: 'книжная полка' },
  { base: adj('iyi'), suffix: 'lIk', label: '-lik', ru: 'доброта' },
  { base: adj('güzel'), suffix: 'lIk', label: '-lik', ru: 'красота' },
  { base: adj('mutlu'), suffix: 'lIk', label: '-lik', ru: 'счастье' },
  { base: adj('hasta'), suffix: 'lIk', label: '-lik', ru: 'болезнь' },
  { base: n('arkadaş'), suffix: 'lIk', label: '-lik', ru: 'дружба' },
  { base: adj('genç'), suffix: 'lIk', label: '-lik', ru: 'молодость' },
  { base: n('çocuk'), suffix: 'lIk', label: '-lik', ru: 'детство' },
  { base: n('ayakkabı'), suffix: 'lIk', label: '-lik', ru: 'обувница' },
  { base: n('kitap'), suffix: 'CI', label: '-ci', ru: 'книжный магазин / продавец книг' },
  { base: n('balık'), suffix: 'CI', label: '-ci', ru: 'рыбак / рыбный магазин' },
  { base: n('çay'), suffix: 'CI', label: '-ci', ru: 'продавец чая' },
  { base: n('kahve'), suffix: 'CI', label: '-ci', ru: 'кофейня' },
  { base: { tr: 'iş', ru: 'работа' }, suffix: 'CI', label: '-ci', ru: 'рабочий' },
  { base: n('yol'), suffix: 'CI', label: '-ci', ru: 'пассажир' },
  { base: { tr: 'süt', ru: 'молоко' }, suffix: 'CI', label: '-ci', ru: 'молочник' },
  { base: { tr: 'gazete', ru: 'газета' }, suffix: 'CI', label: '-ci', ru: 'журналист' },
  { base: n('ayakkabı'), suffix: 'CI', label: '-ci', ru: 'сапожник' },
]

// ── Гармония гласных ─────────────────────────────────────────────────────────
// Слова без исключений (saat и т. п. — не сюда): гармония видна по буквам.
const FRONT_V = 'eiöü'
const ROUND_V = 'oöuü'
const harmonyNouns = NOUNS.filter((x) => !x.front && x.tr.length <= 9)
const vowelsOf = (s: string) => [...s].filter((ch) => 'aeıioöuü'.includes(ch))
const oneVowel = harmonyNouns.filter((x) => vowelsOf(x.tr).length === 1)
// Разные ряды гласных в одном слове (kitap, otobüs): смотреть надо на ПОСЛЕДНЮЮ.
const mixedVowels = harmonyNouns.filter((x) => {
  const vs = vowelsOf(x.tr)
  return vs.some((v) => FRONT_V.includes(v)) && vs.some((v) => !FRONT_V.includes(v))
})

// Слова для разминки и игры на скорость: «найди последнюю гласную», «-ler или -lar».
export const HARMONY_WORDS: { tr: string; ru: string; mixed: boolean }[] = harmonyNouns.map((x) => ({
  tr: x.tr,
  ru: x.ru,
  mixed: mixedVowels.includes(x),
}))

function harmonyWhy2(word: string, v: 'e' | 'a'): string {
  const hv = vowelsOf(word).at(-1)!
  return `последняя гласная в "${markLastVowel(word)}" - "${mv(hv)}", она ${FRONT_V.includes(hv) ? 'передняя (e, i, ö, ü)' : 'задняя (a, ı, o, u)'}, поэтому в аффиксе "${mv(v)}"`
}

function harmonyWhy4(word: string, v: string): string {
  const hv = vowelsOf(word).at(-1)!
  const row = FRONT_V.includes(hv) ? 'передняя' : 'задняя'
  const lips = ROUND_V.includes(hv) ? 'округлая (губы трубочкой)' : 'неокруглая'
  return `последняя гласная в "${markLastVowel(word)}" - "${mv(hv)}": ${row} и ${lips}, поэтому "${mv(v)}"`
}

// «-ler / -lar» с развёрнутой подсказкой про гармонию.
function pluralHarmony(topicId: string, w: Noun): AffixExercise {
  const ex = affix(topicId, nounForm(w, { plural: true }), w, '-ler / -lar ("много")')
  const s = ex.slots[ex.slots.length - 1]
  s.why = harmonyWhy2(w.tr, s.answer.includes('e') ? 'e' : 'a')
  return ex
}

// «Ev mi?» — частица mi отдельным словом; слоты: слово, пробел, частица.
function questionHarmony(topicId: string, w: Noun, plural = false): AffixExercise {
  const form = plural ? nounForm(w, { plural: true }) : { morphs: [{ text: w.tr, label: '', options: [], why: '' }], word: w.tr }
  const q = questionParticle(form.word, null).morphs[0]
  const slots = [
    ...slotsFrom(form).map((s, i) => (i === 0 ? s : { ...s, why: harmonyWhy2(w.tr, s.answer.includes('e') ? 'e' : 'a') })),
    { answer: ' ', options: [], label: '', why: '' },
    { answer: q.text, options: q.options, label: 'частица mi', why: `mi - отдельное слово, но гармонирует с "${markLastVowel(form.word)}": ${harmonyWhy4(form.word, q.text[1])}` },
  ]
  const answer = `${form.word} ${q.text}`
  return {
    kind: 'affix',
    topicId,
    base: w.tr,
    baseRu: w.ru,
    task: plural ? 'много · вопрос' : 'вопрос',
    context: { tr: '___?', ru: plural ? (w.ruPl ? `Это ${w.ruPl}?` : `Это ${w.ru}? (во множественном числе)`) : `Это ${w.ru}?` },
    slots,
    answer,
  }
}

function derivExercise(topicId: string, items: DerivItem[], d: Difficulty, ctx?: GenCtx): AffixExercise {
  const passed = knownItems(passedOnly(items, (i) => SUFFIX_RULE[i.suffix], ctx), (i) => [i.base.tr], ctx)
  const pool = d === 3 ? passed.filter((i) => i.phrase) : passed
  const it = pick(pool.length ? pool : passed)
  const form = derivForm(it)
  return withRules(affix(topicId, form, it.base, `${it.label} - "${it.ru}"`, d === 3 && it.phrase ? it.phrase : undefined), SUFFIX_RULE[it.suffix])
}

// Сравнения «A прил.-ее, чем B» — только осмысленные тройки.
const COMPARISONS: [string, string, string][] = [
  ['ev', 'araba', 'büyük'],
  ['araba', 'otobüs', 'küçük'],
  ['tren', 'otobüs', 'hızlı'],
  ['taksi', 'otobüs', 'pahalı'],
  ['otobüs', 'taksi', 'ucuz'],
  ['kedi', 'köpek', 'küçük'],
  ['kitap', 'defter', 'pahalı'],
  ['kahve', 'çay', 'pahalı'],
  ['çay', 'kahve', 'ucuz'],
  ['deniz', 'göl', 'büyük'],
  ['telefon', 'bilgisayar', 'küçük'],
  ['bilgisayar', 'telefon', 'pahalı'],
  ['otel', 'ev', 'büyük'],
  ['müze', 'okul', 'eski'],
  ['hastane', 'market', 'büyük'],
  ['araba', 'otobüs', 'hızlı'],
]

// Контексты деепричастий/причастий — подобраны вручную, чтобы фраза была осмысленной.
// Урок каждой формы (id реестра rules.ts): контекст с непройденной формой не берём.
const DERIVED_RULE: Partial<Record<Derived, string>> = {
  converbIp: 'converb-ip-sequence',
  converbErek: 'converb-erek-manner',
  converbInce: 'converb-ince-time',
  converbMeden: 'converb-meden-once-before',
  converbDiktan: 'converb-diktan-sonra-after',
  converbKen: 'converb-ken-simultaneity',
  participleEn: 'agent-participle-en-an',
  participleDik: 'participle-dik-relative-clause',
  converbDiginde: 'converb-diginde-time',
  converbEne: 'converb-ene-kadar',
  converbIrMez: 'converb-ir-mez',
  converbEli: 'converb-eli-beri',
  converbDikca: 'converb-dikca',
  participleEcek: 'participle-ecek-noun-clause',
  verbalNounMe: 'participle-gereken',
}

// Контекст формы: глагол из VERBS, форма, чья (poss) и падеж формы (case:
// geldiğini, bildiğime). rule — если у контекста свой урок, не урок формы
// (-meden без önce — «не сделав»); label — своя подпись задания.
// needs - правило, без которого фраза-носитель непонятна (-yordu в «herkes uyuyordu»).
type DerivedContext = { v: string; kind: Derived; poss?: Person; case?: Case; rule?: string; needs?: string; label?: string; tr: string; ru: string }

const CONVERB_SEQ: DerivedContext[] = [
  { v: 'gel', kind: 'converbIp', tr: 'Eve ___ yemek yiyorum.', ru: 'Прихожу домой и ем.' },
  { v: 'git', kind: 'converbIp', tr: 'Markete ___ ekmek aldım.', ru: 'Я сходил в магазин и купил хлеб.' },
  { v: 'oku', kind: 'converbIp', tr: 'Kitap ___ uyuyorum.', ru: 'Читаю книгу и засыпаю.' },
  { v: 'otur', kind: 'converbIp', tr: 'Burada ___ çay içtik.', ru: 'Мы сели здесь и выпили чаю.' },
  { v: 'al', kind: 'converbIp', tr: 'Bileti ___ trene bindik.', ru: 'Мы взяли билет и сели в поезд.' },
  { v: 'dinle', kind: 'converbErek', tr: 'Müzik ___ yürüyorum.', ru: 'Гуляю, слушая музыку.' },
  { v: 'koş', kind: 'converbErek', tr: 'Okula ___ geldim.', ru: 'Я прибежал в школу (бегом).' },
  { v: 'konuş', kind: 'converbErek', tr: 'Türkçe ___ öğreniyorum.', ru: 'Учу турецкий, разговаривая.' },
  { v: 'gül', kind: 'converbErek', tr: 'Bana ___ baktı.', ru: 'Он посмотрел на меня, смеясь.' },
  { v: 'düşün', kind: 'converbErek', tr: '___ cevap verdi.', ru: 'Он отвечал, раздумывая.' },
]

const CONVERB_TIME: DerivedContext[] = [
  { v: 'gel', kind: 'converbInce', tr: 'Eve ___ seni ararım.', ru: 'Когда приду домой, позвоню тебе.' },
  { v: 'dön', kind: 'converbInce', tr: 'Tatilden ___ görüşürüz.', ru: 'Когда вернёшься из отпуска, увидимся.' },
  { v: 'bitir', kind: 'converbInce', tr: 'İşi ___ çıkıyorum.', ru: 'Как только закончу работу, выхожу.' },
  { v: 'bul', kind: 'converbInce', tr: 'Anahtarı ___ kapıyı açtım.', ru: 'Как только нашёл ключ, открыл дверь.' },
  { v: 'yap', kind: 'converbKen', tr: 'Yemek ___ müzik dinlerim.', ru: 'Когда готовлю, слушаю музыку.' },
  { v: 'yürü', kind: 'converbKen', tr: 'Parkta ___ onu gördüm.', ru: 'Гуляя в парке, я его увидел.' },
  { v: 'bekle', kind: 'converbKen', tr: 'Otobüs ___ kitap okuyorum.', ru: 'Пока жду автобус, читаю книгу.' },
  { v: 'çalış', kind: 'converbKen', tr: 'Ofiste ___ çay içerim.', ru: 'Работая в офисе, пью чай.' },
  { v: 'ye', kind: 'converbMeden', tr: 'Yemek ___ önce ellerini yıka.', ru: 'Помой руки перед едой (до того как поешь).' },
  { v: 'çık', kind: 'converbMeden', tr: 'Evden ___ önce ışığı kapat.', ru: 'Перед тем как выйти из дома, выключи свет.' },
  { v: 'uyu', kind: 'converbMeden', tr: '___ önce kitap okurum.', ru: 'Перед сном читаю книгу.' },
  { v: 'bitir', kind: 'converbDiktan', tr: 'İşi ___ sonra çay içerim.', ru: 'Закончив работу, пью чай.' },
  { v: 'gel', kind: 'converbDiktan', tr: 'Eve ___ sonra yemek yaptım.', ru: 'Придя домой, я приготовил еду.' },
  { v: 'oku', kind: 'converbDiktan', tr: 'Kitabı ___ sonra filmi izledim.', ru: 'Прочитав книгу, я посмотрел фильм.' },
  { v: 'git', kind: 'converbDiktan', tr: 'O ___ sonra ben geldim.', ru: 'Я пришёл после того, как он ушёл.' },
  // -diğinde: «когда» с лицом (урок 25).
  { v: 'gel', kind: 'converbDiginde', poss: 0, tr: 'Eve ___ kimse yoktu.', ru: 'Когда я пришёл домой, никого не было.' },
  { v: 'gel', kind: 'converbDiginde', poss: 0, needs: 'past-continuous-yordu', tr: 'Eve ___ herkes uyuyordu.', ru: 'Когда я пришёл домой, все спали.' },
  { v: 'bin', kind: 'converbDiginde', poss: 3, tr: 'Otobüse ___ yağmur başladı.', ru: 'Когда мы сели в автобус, начался дождь.' },
  { v: 'ara', kind: 'converbDiginde', poss: 1, tr: 'Beni ___ toplantıdaydım.', ru: 'Когда ты мне позвонил, я был на совещании.' },
  { v: 'oku', kind: 'converbDiginde', poss: 0, tr: 'Bu kitabı ___ çok güldüm.', ru: 'Когда я читал эту книгу, я много смеялся.' },
  { v: 'dön', kind: 'converbDiginde', poss: 5, tr: 'Tatilden ___ ev çok soğuktu.', ru: 'Когда они вернулись из отпуска, дома было очень холодно.' },
  // -ene kadar: «пока не» (урок 48).
  { v: 'gel', kind: 'converbEne', tr: 'Ben ___ kadar burada bekle.', ru: 'Жди здесь, пока я не приду.' },
  { v: 'bitir', kind: 'converbEne', tr: 'Raporu ___ kadar çıkmayız.', ru: 'Не уйдём, пока не закончим отчёт.' },
  { v: 'dön', kind: 'converbEne', tr: 'Sen ___ kadar uyumadım.', ru: 'Я не спал, пока ты не вернулся.' },
  { v: 'oku', kind: 'converbEne', tr: 'Müdür ___ kadar gönderme.', ru: 'Не отправляй, пока директор не прочитает.' },
  // -meden без önce: «не сделав» (урок 50).
  { v: 'ye', kind: 'converbMeden', rule: 'converb-meden-without', label: '"не сделав" (-meden)', tr: 'Yemek ___ ilaç içme.', ru: 'Не пей лекарство, не поев.' },
  { v: 'sor', kind: 'converbMeden', rule: 'converb-meden-without', label: '"не сделав" (-meden)', tr: 'Doktora ___ içme.', ru: 'Не пей, не спросив врача.' },
  { v: 'bekle', kind: 'converbMeden', rule: 'converb-meden-without', label: '"не сделав" (-meden)', tr: 'Bizi ___ gitti.', ru: 'Он ушёл, не дождавшись нас.' },
  { v: 'oku', kind: 'converbMeden', rule: 'converb-meden-without', label: '"не сделав" (-meden)', tr: 'Kutudaki yazıyı ___ aldım.', ru: 'Я купил, не прочитав, что написано на коробке.' },
  // -ır -maz: «как только — сразу» (урок 54).
  { v: 'gel', kind: 'converbIrMez', tr: 'Eve ___ yattım.', ru: 'Едва пришёл домой, сразу лёг.' },
  { v: 'al', kind: 'converbIrMez', tr: 'Diplomamı ___ iş buldum.', ru: 'Сразу как получил диплом, нашёл работу.' },
  { v: 'gör', kind: 'converbIrMez', tr: 'Beni ___ kaçtı.', ru: 'Едва увидел меня, убежал.' },
  { v: 'öğren', kind: 'converbIrMez', tr: 'Sonucu ___ seni ararım.', ru: 'Как только узнаю результат, сразу позвоню тебе.' },
  // -eli: «с тех пор как» (урок 64).
  { v: 'gel', kind: 'converbEli', tr: "Türkiye'ye ___ iki yıl oldu.", ru: 'С тех пор как я приехал в Турцию, прошло два года.' },
  { v: 'başla', kind: 'converbEli', tr: 'İşe ___ bir ay oldu.', ru: 'С тех пор как я начал работать, прошёл месяц.' },
  { v: 'bekle', kind: 'converbEli', tr: '___ bir saat oldu.', ru: 'Мы ждём уже час.' },
  { v: 'dön', kind: 'converbEli', tr: 'Tatilden ___ bir hafta oldu.', ru: 'С тех пор как мы вернулись из отпуска, прошла неделя.' },
  // -dıkça: «чем…, тем…», «каждый раз, когда» (урок 69).
  { v: 'çalış', kind: 'converbDikca', tr: '___ daha çok kazanıyorum.', ru: 'Чем больше работаю, тем больше зарабатываю.' },
  { v: 'gör', kind: 'converbDikca', tr: 'Seni ___ mutlu oluyorum.', ru: 'Каждый раз, когда вижу тебя, я радуюсь.' },
  { v: 'konuş', kind: 'converbDikca', tr: 'Türkçe ___ daha kolay oluyor.', ru: 'Чем больше говорю по-турецки, тем легче.' },
  { v: 'dinle', kind: 'converbDikca', tr: 'Bu şarkıyı ___ ağlıyorum.', ru: 'Каждый раз, когда слушаю эту песню, плачу.' },
  { v: 'git', kind: 'converbDikca', tr: 'Fiyatlar ___ artıyor.', ru: 'Цены всё больше растут.' },
]

const PARTICIPLES: DerivedContext[] = [
  { v: 'oku', kind: 'participleEn', tr: 'Kitap ___ kız benim kardeşim.', ru: 'Девочка, которая читает книгу, - моя сестра.' },
  { v: 'gel', kind: 'participleEn', tr: 'Dün ___ öğrenci kim?', ru: 'Кто тот студент, который пришёл вчера?' },
  { v: 'çalış', kind: 'participleEn', tr: 'Burada ___ herkes Türkçe biliyor.', ru: 'Все, кто здесь работает, знают турецкий.' },
  { v: 'bekle', kind: 'participleEn', tr: 'Otobüs ___ insanlar', ru: 'люди, которые ждут автобус' },
  { v: 'bil', kind: 'participleEn', tr: 'Türkçe ___ biri lazım.', ru: 'Нужен человек, который знает турецкий.' },
  { v: 'otur', kind: 'participleEn', tr: 'Üst katta ___ komşu çok kibar.', ru: 'Сосед, который живёт этажом выше, очень вежливый.' },
  { v: 'konuş', kind: 'participleEn', tr: 'Telefonda ___ kız kim?', ru: 'Кто эта девушка, которая говорит по телефону?' },
  { v: 'gel', kind: 'participleEn', tr: 'Partiye ___ herkes hediye getirdi.', ru: 'Все, кто пришёл на вечеринку, принесли подарки.' },
  { v: 'sev', kind: 'participleEn', tr: 'Futbolu ___ çocuklar parkta oynuyor.', ru: 'Дети, которые любят футбол, играют в парке.' },
  { v: 'oku', kind: 'participleDik', poss: 0, tr: '___ kitap çok güzel.', ru: 'Книга, которую я читаю, очень хорошая.' },
  { v: 'izle', kind: 'participleDik', poss: 1, tr: '___ film ne?', ru: 'Какой фильм ты смотришь?' },
  { v: 'gör', kind: 'participleDik', poss: 3, tr: '___ ev çok büyüktü.', ru: 'Дом, который мы видели, был очень большой.' },
  { v: 'al', kind: 'participleDik', poss: 0, tr: '___ ekmek taze.', ru: 'Хлеб, который я купил, свежий.' },
  { v: 'iç', kind: 'participleDik', poss: 2, tr: '___ çay soğuk.', ru: 'Чай, который он пьёт, холодный.' },
  { v: 'git', kind: 'participleDik', poss: 0, tr: '___ yer çok uzak.', ru: 'Место, куда я еду, очень далеко.' },
  // -dığı «то, что» + винительный (урок 31).
  { v: 'gel', kind: 'participleDik', poss: 1, case: 'acc', rule: 'participle-dik-noun-clause', label: '"то, что ..." (-diğini)', tr: '___ biliyorum.', ru: 'Я знаю, что ты пришёл.' },
  { v: 'otur', kind: 'participleDik', poss: 1, case: 'acc', rule: 'participle-dik-noun-clause', label: '"то, что ..." (-diğini)', tr: 'Nerede ___ biliyorum.', ru: 'Я знаю, где ты живёшь.' },
  { v: 'yap', kind: 'participleDik', poss: 1, case: 'acc', rule: 'participle-dik-noun-clause', label: '"то, что ..." (-diğini)', tr: 'Ne ___ anlamadım.', ru: 'Я не понял, что ты сделал.' },
  { v: 'git', kind: 'participleDik', poss: 2, case: 'acc', rule: 'participle-dik-noun-clause', label: '"то, что ..." (-diğini)', tr: 'Nereye ___ bilmiyorum.', ru: 'Не знаю, куда он ушёл.' },
  { v: 'gör', kind: 'participleDik', poss: 3, case: 'acc', rule: 'participle-dik-noun-clause', label: '"то, что ..." (-diğimizi)', tr: 'Onu ___ kimseye söyleme.', ru: 'Никому не говори, что мы его видели.' },
  // -dığı в дательном: göre, sevinmek (урок 32).
  { v: 'bil', kind: 'participleDik', poss: 0, case: 'dat', rule: 'participle-dik-case-gore', label: '"насколько я ..." (-diğime göre)', tr: '___ göre toplantı yarın.', ru: 'Насколько я знаю, совещание завтра.' },
  { v: 'anla', kind: 'participleDik', poss: 0, case: 'dat', rule: 'participle-dik-case-gore', label: '"как я ..." (-diğime göre)', tr: '___ göre her şey yolunda.', ru: 'Как я понял, всё в порядке.' },
  { v: 'oku', kind: 'participleDik', poss: 0, case: 'dat', rule: 'participle-dik-case-gore', label: '"судя по тому, что я ..." (-diğime göre)', tr: '___ göre yarın kar yağacak.', ru: 'Судя по тому, что я читал, завтра пойдёт снег.' },
  { v: 'gel', kind: 'participleDik', poss: 1, case: 'dat', rule: 'participle-dik-case-gore', label: '"рад, что ..." (-diğine)', tr: '___ çok sevindim.', ru: 'Я очень рад, что ты пришёл.' },
  // -acağı: будущее «то, что буду» и «который буду» (урок 34).
  { v: 'yap', kind: 'participleEcek', poss: 0, case: 'acc', tr: 'Ne ___ bilmiyorum.', ru: 'Не знаю, что буду делать.' },
  { v: 'git', kind: 'participleEcek', poss: 2, case: 'acc', tr: 'Nereye ___ bilmiyorum.', ru: 'Не знаю, куда он пойдёт.' },
  { v: 'gel', kind: 'participleEcek', poss: 1, case: 'acc', tr: '___ biliyordum.', ru: 'Я знал, что ты придёшь.' },
  { v: 'oku', kind: 'participleEcek', poss: 0, tr: '___ kitap çok kalın.', ru: 'Книга, которую я буду читать, очень толстая.' },
  { v: 'al', kind: 'participleEcek', poss: 3, tr: '___ ev çok büyük.', ru: 'Дом, который мы купим, очень большой.' },
  // -mesi gereken: «который нужно» (урок 68).
  { v: 'yap', kind: 'verbalNounMe', poss: 4, tr: '___ gereken ilk şey bu.', ru: 'Вот первое, что вам нужно сделать.' },
  { v: 'bil', kind: 'verbalNounMe', poss: 3, tr: '___ gereken her şey burada.', ru: 'Всё, что нам нужно знать, - здесь.' },
  { v: 'oku', kind: 'verbalNounMe', poss: 1, tr: '___ gereken kitap bu.', ru: 'Вот книга, которую тебе нужно прочитать.' },
  { v: 'bitir', kind: 'verbalNounMe', poss: 0, tr: '___ gereken iş çok.', ru: 'Работы, которую мне нужно закончить, много.' },
  { v: 'gönder', kind: 'verbalNounMe', poss: 2, tr: '___ gereken mesaj bu.', ru: 'Вот сообщение, которое ему нужно отправить.' },
]

const derivedRule = (it: DerivedContext) => it.rule ?? DERIVED_RULE[it.kind]

// Сложность у деепричастий/причастий — в самой форме, поэтому уровень не
// меняет выбор: все контексты одинаково «предложенческие».
function contextDerived(topicId: string, list: DerivedContext[], ctx?: GenCtx): AffixExercise {
  // Фокус урока: рамки правила этого урока чаще (как govPool), иначе на уроке
  // -meden önce шаг почти целиком из -ince и -ken.
  const pool = passedOnly(list, derivedRule, ctx).filter((x) => isPassed(ctx, x.needs))
  const focused = pool.filter((x) => ctx?.focus?.has(derivedRule(x) ?? ''))
  const it = pick(focused.length && Math.random() < 0.75 ? focused : pool)
  const v = vb(it.v)
  const form = derivedForm(v, it.kind, { poss: it.poss, case: it.case })
  const label = labels(it.label ?? DERIVED_RU[it.kind], it.poss !== undefined && PRON_LOW[it.poss])
  return withRules(affix(topicId, form, verbBase(v), label, { tr: it.tr, ru: it.ru }), derivedRule(it))
}

// ── Варианты сверх основного времени темы ───────────────────────────────────
// Тема-время (ability, necessity, evidential…) плюс формы из более поздних
// уроков: «не смог», «надо было», «говорят, будет». Вариант берём, только
// если его урок пройден; отрицательные (neg) — на второй ступени.
type Variant = { rule: string; neg?: boolean; make: (d: Difficulty, ctx?: GenCtx) => Exercise }

// Вариант-форма глагола: 1–2 — собрать форму во фразе, 3 — из настоящего.
type VerbVariant = {
  rule: string
  // Правило, на котором форма стоит сверх своего: «смог» -ebildi несёт и -ebil.
  also?: string
  // Подпись задания: «не смог (-eme + -di)»; функция - если подпись зависит
  // от лица («не смогу» у ben, «не смогут» у onlar).
  label: string | ((p: Person) => string)
  neg?: boolean
  persons?: Person[]
  form: (v: Verb, p: Person) => WordForm
  ru: (v: Verb, p: Person) => string | null
}

function verbVariant(topicId: string, f: VerbVariant): Variant {
  const labelOf = (p: Person) => (typeof f.label === 'string' ? f.label : f.label(p))
  return {
    rule: f.rule,
    neg: f.neg,
    make: (d, ctx) => {
      const p = pick(f.persons ?? ALL)
      if (d < 3) {
        const v = pick(known(VERBS, ctx))
        // У глагола без дополнения носитель «Sen ___.» с переводом: по одной
        // подписи «говорят, будет ...» не понять, что собирается (düşünecekmişsin).
        const ru = f.ru(v, p)
        const context = v.complement
          ? { tr: `${PRON[p]} ${v.complement.tr} ___.`, ru: capitalize(ru ?? who(p, verbHint(v))) }
          : ru ? { tr: `${PRON[p]} ___.`, ru: capitalize(ru) } : undefined
        return withRules(affix(topicId, f.form(v, p), verbBase(v), labels(PRON_LOW[p], labelOf(p)), context), f.rule, f.also, v.complement?.rule)
      }
      const v = pick(known(withComplement, ctx))
      const form = f.form(v, p)
      const answer = `${PRON[p]} ${v.complement!.tr} ${form.word}.`
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId,
        source: verbSentence(v, 'present', p),
        sourceRu: verbPhraseRu(v, 'present', p) ?? verbHint(v),
        instruction: `Скажите: ${labelOf(p)}`,
        answer,
        variants: [withoutPronoun(answer)],
        breakdown: breakdownFrom(form),
      }, f.rule, f.also, v.complement?.rule)
    },
  }
}

// Основной генератор темы + пройденные варианты: каждый вариант выпадает так
// же часто, как основное время. Первая ступень — без отрицательных вариантов,
// вторая — отрицательные, если они уже пройдены.
function withVariants(base: (d: Difficulty, ctx?: GenCtx) => Exercise, variants: Variant[]) {
  return (d: Difficulty, ctx?: GenCtx): Exercise => {
    const open = passedOnly(variants, (f) => f.rule, ctx)
    const neg = open.filter((f) => f.neg)
    const pool = d === 1 ? open.filter((f) => !f.neg) : d === 2 && neg.length ? neg : open
    if (!pool.length || Math.random() < 1 / (pool.length + 1)) return base(d, ctx)
    return pick(pool).make(d, ctx)
  }
}

// Задание на слово с «связкой» (hastaydım, hastaymış, hastaysan, hasta
// olmalısın): 1–2 — собрать форму, 3 — из «я — …» в нужную форму.
function copulaExercise(
  topicId: string,
  d: Difficulty,
  args: { w: NounLike & PredicateWord; p: Person; form: WordForm; label: string; ru: string; instruction: string; rules: (string | undefined)[] },
): Exercise {
  const { w, p, form } = args
  if (d < 3) return withRules(affix(topicId, form, { tr: w.tr, ru: w.ru }, labels(PRON_LOW[p], args.label), { tr: `${PRON[p]} ___.`, ru: args.ru }), ...args.rules)
  const answer = `${PRON[p]} ${form.word}.`
  return withRules<TransformExercise>({
    kind: 'transform',
    topicId,
    source: `${PRON[p]} ${predicate(w, p).word}.`,
    sourceRu: predicateRu(w, p),
    instruction: args.instruction,
    answer,
    variants: [withoutPronoun(answer)],
    breakdown: breakdownFrom(form),
  }, ...args.rules, copulaRule(p))
}

// «Я, наверное, устал»: вставить «наверное» после местоимения.
const guessRu = (w: PredicateWord, p: Person) => capitalize(predicateRu(w, p).replace(/^(\S+) /, '$1, наверное, '))

// Состояния и места для «связок» на имени: hastaydım, evdeymiş, burada olmalı.
const HERE: NounLike & PredicateWord = { tr: 'burada', ru: 'здесь' }
const AT_HOME: NounLike & PredicateWord = { tr: 'evde', ru: 'дома' }
const stateWords = states.filter((x) => !x.soft)

// Фраза с пропуском и переводом для заданий на слова из готового списка.
// alt — другие верные ответы целиком для «переведи»: под русскую фразу
// подходит и другой глагол из карточек.
type Frame = { tr: string; ru: string; alt?: string[] }

// Задание по рамке: 1–2 — собрать форму в пропуске, 3 — перевод с русского.
function frameExercise(topicId: string, d: Difficulty, f: Frame, form: WordForm, base: { tr: string; ru: string }, label: string, ...rules: (string | undefined)[]): Exercise {
  if (d < 3) return withRules(affix(topicId, form, base, label, f), ...rules)
  const answer = f.tr.replace('___', form.word)
  return withRules(translate(topicId, { ru: f.ru, answer: f.tr.startsWith('___') ? capitalize(answer) : answer, form, variants: f.alt }), ...rules)
}

// ── Данные новых тем и вариантов (тикет 07 .scratch/grammar-unify) ──────────

// «Был» у слова: o / ben / sen — в уроке 14c (uzundu, hastaydım, hastaydın),
// все лица, «был дома» и değildim — в A2, урок 2.
const wasRule = (p: Person) => (p <= 2 ? 'past-copula-ydi' : 'past-copula-ydi-full')
const WAS_RU = ['я был', 'ты был', 'он был', 'мы были', 'вы были', 'они были'] as const
// «Вчера я устал»: у слов с глагольным сказуемым (ruWas) «был» не нужен.
const wasRu = (w: PredicateWord, p: Person, neg = false) =>
  w.ruWas
    ? `${['я', 'ты', 'он', 'мы', 'вы', 'они'][p]} ${neg ? 'не ' : ''}${p >= 3 ? (w.ruWasPl ?? w.ruWas) : w.ruWas}`
    : `${neg ? WAS_RU[p].replace(' был', ' не был') : WAS_RU[p]} ${p >= 3 ? (w.ruPredPl ?? w.ruPl ?? w.ru) : (w.ruPred ?? w.ru)}`
// «Вчера был …» — только то, что проходит: «вчера был богат» звучит странно.
const WAS_STATES = ['yorgun', 'aç', 'hazır', 'hasta', 'mutlu', 'iyi'].map(adj)
const VAR_YOK: (Frame & { w: 'var' | 'yok' })[] = [
  { w: 'var', tr: 'Dün maç ___.', ru: 'Вчера был матч.' },
  { w: 'yok', tr: 'Dün ders ___.', ru: 'Вчера не было урока.' },
  { w: 'var', tr: 'Dün çok iş ___.', ru: 'Вчера было много работы.' },
  { w: 'yok', tr: 'Dün otobüs ___.', ru: 'Вчера не было автобуса.' },
  { w: 'var', tr: 'Dün sınav ___.', ru: 'Вчера был экзамен.' },
  { w: 'yok', tr: 'Dün taksi ___.', ru: 'Вчера не было такси.' },
  { w: 'var', tr: 'Dün parkta konser ___.', ru: 'Вчера в парке был концерт.' },
  { w: 'yok', tr: 'Dün evde ekmek ___.', ru: 'Вчера дома не было хлеба.' },
  { w: 'var', tr: 'Dün çok trafik ___.', ru: 'Вчера было много пробок.' },
  { w: 'yok', tr: 'Sabah su ___.', ru: 'Утром не было воды.' },
  { w: 'var', tr: 'Geçen hafta tatil ___.', ru: 'На прошлой неделе были каникулы.' },
  { w: 'yok', tr: 'Dün internet ___.', ru: 'Вчера не было интернета.' },
  { w: 'var', tr: 'Dün çok misafir ___.', ru: 'Вчера было много гостей.' },
  { w: 'yok', tr: 'Dün zaman ___.', ru: 'Вчера не было времени.' },
]

// «Было / не было» (vardi-yoktu): 1 - собрать vardı / yoktu в рамке, 2 - вписать
// окончание к var / yok, 3 - перевести фразу с русского.
function varYokExercise(d: Difficulty): Exercise {
  const f = pick(VAR_YOK)
  const w = { tr: f.w, ru: f.w === 'var' ? 'есть' : 'нет' }
  const form = copulaForm(w, 2, 'was')
  const label = f.w === 'var' ? 'было, имелось' : 'не было'
  if (d === 1) return withRules(affix('past-copula', form, w, label, f), 'vardi-yoktu')
  const answer = fillFrame(f.tr, form.word)
  if (d === 2) return withRules(fillEnding('past-copula', { word: f.w, ru: f.ru, answer, form, ask: label }), 'vardi-yoktu')
  return withRules(translateEnding('past-copula', { ru: f.ru, answer, form }), 'vardi-yoktu')
}
// «Был дома / в школе»: место в местном падеже.
const WAS_PLACES: (NounLike & PredicateWord)[] = (
  [
    ['ev', 'дома'],
    ['okul', 'в школе'],
    ['ofis', 'в офисе'],
    ['otel', 'в отеле'],
    ['park', 'в парке'],
    ['hastane', 'в больнице'],
  ] as const
).map(([tr, ru]) => ({ tr: nounForm(n(tr), { case: 'loc' }).word, ru }))

// Местоимения: «кому?» — урок 3 A2, остальные падежи — таблица урока 5.
const PRON_CASES: PronounCase[] = ['dat', 'acc', 'loc', 'abl', 'ins']
const pronCaseRule = (c: PronounCase) => (c === 'dat' ? 'pronoun-dative' : 'pronoun-case-table')
// Русское местоимение под вопрос фразы: {dat} — «мне», {at} — «у меня».
const RU_PRON: Record<string, readonly string[]> = {
  acc: ['меня', 'тебя', 'его', 'нас', 'вас', 'их'],
  dat: ['мне', 'тебе', 'ему', 'нам', 'вам', 'им'],
  at: ['у меня', 'у тебя', 'у него', 'у нас', 'у вас', 'у них'],
  from: ['от меня', 'от тебя', 'от него', 'от нас', 'от вас', 'от них'],
  ins: ['со мной', 'с тобой', 'с ним', 'с нами', 'с вами', 'с ними'],
}
// not — лица, с которыми фраза звучит странно («я отправил мне»); rule — фраза
// открывается с этим правилом (korkmak с -den объяснён в уроке 106, тема с 69).
const PRON_FRAMES: Record<PronounCase, (Frame & { not?: Person[]; rule?: string })[]> = {
  dat: [
    { tr: 'Annem ___ çanta aldı.', ru: 'Мама купила {dat} сумку.' },
    { tr: 'Garson ___ çay getirdi.', ru: 'Официант принёс {dat} чай.' },
    { tr: '___ mesaj gönderdim.', ru: 'Я отправил {dat} сообщение.', not: [0, 3] },
  ],
  acc: [
    { tr: 'Annem ___ bekliyor.', ru: 'Мама ждёт {acc}.' },
    { tr: 'Dün ___ gördüm.', ru: 'Вчера я видел {acc}.', not: [0, 3] },
    { tr: 'Dün ___ aradım.', ru: 'Вчера я звонил {dat}.', not: [0, 3] },
  ],
  loc: [
    { tr: 'Anahtar ___.', ru: 'Ключ {at} (при себе).' },
    { tr: 'Bu akşam ___ kalıyorum.', ru: 'Сегодня вечером я остаюсь {at}.', not: [0, 3] },
  ],
  abl: [
    { tr: 'Kardeşim ___ mesaj aldı.', ru: 'Мой брат получил сообщение {from}.' },
    { tr: 'Kardeşim ___ korkuyor.', ru: 'Мой брат боится {acc}.', rule: 'verbs-ablative' },
    { tr: '___ bir mesaj aldım.', ru: 'Я получил сообщение {from}.', not: [0, 3] },
  ],
  ins: [
    { tr: 'Annem ___ geliyor.', ru: 'Мама идёт сюда {ins}.' },
    { tr: '___ konuşmak istiyorum.', ru: 'Я хочу поговорить {ins}.', not: [0, 3] },
  ],
}
// Таблица урока 5: строки — вопросы, колонки — лица.
const PRON_ROWS: [PronounCase, string][] = [
  ['acc', 'кого?'],
  ['dat', 'кому?'],
  ['loc', 'у кого?\nпри ком?'],
  ['abl', 'от кого?'],
  ['ins', 'с кем?'],
]
const pronounTable = (persons: Person[]) => ({
  head: ['', ...persons.map((p) => PRONOUNS[p])],
  rows: PRON_ROWS.map(([c, q]) => [q, ...persons.map((p) => cell(pronounForm(p, c)))]),
})

// Понудительный: контекст из урока, глагол из CAUS_VERBS. self — та же мысль
// «сам» (без каузатива): для третьей ступени «сам → чужими руками».
type CausFrame = Frame & { v: string; rule: string; p?: Person; tense?: 'past' | 'present' | 'future'; passive?: boolean; self?: string }
const causVerb = (stem: string) => CAUS_VERBS.find((x) => x.stem === stem)!
const CAUS_FRAMES: CausFrame[] = [
  { v: 'kes', p: 0, rule: 'causative-dir', tr: 'Saçımı ___.', ru: 'Я подстригся (в парикмахерской).', self: 'Я сам отрезал себе волосы.' },
  { v: 'tamir et', p: 0, rule: 'causative-dir', tr: 'Arabamı ___.', ru: 'Я отдал машину в ремонт.', self: 'Я сам починил машину.' },
  { v: 'yaz', p: 0, rule: 'causative-dir', tr: 'Mektubu ___.', ru: 'Я распечатал письмо.', self: 'Я написал письмо.' },
  { v: 'çek', p: 0, rule: 'causative-dir', tr: 'Bir fotoğraf ___.', ru: 'Я сфотографировался.', self: 'Я сделал снимок.' },
  { v: 'yap', p: 2, rule: 'causative-dir', tr: 'Bu evi babam ___.', ru: 'Папа построил этот дом (нанял строителей).', self: 'Этот дом папа построил сам.' },
  { v: 'yap', p: 2, tense: 'present', rule: 'causative-dir', tr: 'Müdür bize her akşam fazla mesai ___.', ru: 'Директор каждый вечер заставляет нас задерживаться на работе.' },
  { v: 'kontrol et', p: 0, tense: 'future', rule: 'causative-dir', tr: 'Arabamı yarın ___.', ru: 'Завтра отдам машину на проверку.' },
  { v: 'bekle', p: 2, rule: 'causative-t-passive', tr: 'Müdür bizi bir saat ___.', ru: 'Директор заставил нас ждать час.' },
  { v: 'anla', p: 2, rule: 'causative-t-passive', tr: 'Öğretmen kuralı ___.', ru: 'Учитель объяснил правило.' },
  { v: 'düş', p: 0, rule: 'causative-t-passive', tr: 'Telefonu ___.', ru: 'Я уронил телефон.' },
  { v: 'bit', p: 3, rule: 'causative-t-passive', tr: 'Projeyi ___.', ru: 'Мы закончили проект.' },
  { v: 'gör', p: 0, rule: 'causative-t-passive', tr: 'Ona evi ___.', ru: 'Я показал ему дом.' },
  { v: 'öğren', p: 2, rule: 'causative-t-passive', tr: 'Annem bana yemek yapmayı ___.', ru: 'Мама научила меня готовить.' },
  { v: 'çık', p: 2, passive: true, rule: 'causative-t-passive', tr: 'Ahmet işten ___.', ru: 'Ахмета уволили.' },
  { v: 'bekle', p: 3, passive: true, rule: 'causative-t-passive', tr: 'Bankada bir saat ___.', ru: 'В банке нас заставили ждать час.' },
  { v: 'yüksel', p: 2, passive: true, rule: 'causative-t-passive', tr: 'Ayşe geçen ay ___.', ru: 'Айше повысили в прошлом месяце.', alt: ['Ayşe geçen ay terfi etti.'] },
  { v: 'düş', p: 2, passive: true, rule: 'causative-t-passive', tr: 'Maaşlar ___.', ru: 'Зарплаты снизили.' },
  { v: 'gül', p: 2, rule: 'causative-emotion', tr: 'Film beni çok ___.', ru: 'Фильм меня очень рассмешил.' },
  { v: 'ağla', p: 2, rule: 'causative-emotion', tr: 'Son sahne herkesi ___.', ru: 'Последняя сцена довела всех до слёз.' },
  { v: 'kork', p: 2, rule: 'causative-emotion', tr: 'Bu sahne beni ___.', ru: 'Эта сцена меня напугала.' },
  { v: 'sevin', p: 2, rule: 'causative-emotion', tr: 'Haberin beni çok ___.', ru: 'Твоя новость меня очень обрадовала.' },
  { v: 'kız', p: 2, tense: 'present', rule: 'causative-emotion', tr: 'Bu gürültü beni ___.', ru: 'Этот шум меня злит.' },
  { v: 'şaşır', p: 2, rule: 'causative-emotion', tr: 'Filmin sonu beni ___.', ru: 'Концовка фильма меня удивила.' },
  { v: 'düşün', p: 2, tense: 'present', rule: 'causative-emotion', tr: 'Bu film insanı ___.', ru: 'Этот фильм заставляет задуматься.' },
]

// -sa на имени: «если ты болен» (B1, урок 14).
const NOUN_IF: (Frame & { w: NounLike & { ru: string }; p: Person })[] = [
  { w: adj('hasta'), p: 1, tr: '___, işe gitme.', ru: 'Если ты болен, не ходи на работу.' },
  { w: adj('yorgun'), p: 1, tr: '___, biraz uyu.', ru: 'Если ты устал, поспи немного.' },
  { w: adj('aç'), p: 4, tr: '___, yemek yaparım.', ru: 'Если вы голодны, я приготовлю поесть.' },
  { w: AT_HOME, p: 1, tr: '___, sana geliyorum.', ru: 'Если ты дома, я к тебе иду.' },
  { w: adj('hazır'), p: 4, tr: '___, başlıyoruz.', ru: 'Если вы готовы, мы начинаем.' },
  { w: adj('pahalı'), p: 2, tr: '___, almayız.', ru: 'Если это дорого, мы не купим.' },
  { w: adj('hasta'), p: 5, tr: '___, bugün gelmezler.', ru: 'Если они больны, сегодня не придут.' },
]
// -se bile «даже если» (урок 47) и -se mi? «а не сделать ли» (урок 65).
type SeFrame = Frame & { v: string; p: Person; neg?: boolean }
const CONCESSIVE: SeFrame[] = [
  { v: 'gel', p: 1, tr: 'Geç ___ bile seni beklerim.', ru: 'Даже если ты придёшь поздно, я тебя подожду.' },
  { v: 'çalış', p: 0, tr: 'Çok ___ bile bu işi bitiremem.', ru: 'Даже если я буду много работать, мне не закончить эту работу.' },
  { v: 'ara', p: 2, tr: 'Beni ___ bile açmam.', ru: 'Даже если он мне позвонит, я не возьму трубку.' },
  { v: 'sor', p: 4, tr: 'Bana ___ bile söylemem.', ru: 'Даже если вы меня спросите, я не скажу.' },
  { v: 'yap', p: 4, tr: 'İndirim ___ bile almayız.', ru: 'Даже если вы сделаете скидку, мы не купим.' },
  { v: 'gel', p: 1, neg: true, tr: 'Sen ___ bile başlayacağız.', ru: 'Даже если ты не придёшь, мы начнём.' },
]
const DILEMMA: SeFrame[] = [
  { v: 'ara', p: 0, tr: 'Acaba annemi ___?', ru: 'Может, позвонить маме?' },
  { v: 'bin', p: 3, tr: 'Taksiye ___?', ru: 'Может, возьмём такси?' },
  { v: 'git', p: 0, neg: true, tr: 'Bugün işe ___?', ru: 'Может, не ходить сегодня на работу?' },
  { v: 'aç', p: 0, tr: 'Pencereyi ___?', ru: 'Может, открыть окно?' },
  { v: 'ver', p: 0, tr: 'Valizi ___?', ru: 'Может, сдать чемодан?' },
  { v: 'kal', p: 3, tr: 'Bu akşam evde ___?', ru: 'Может, останемся сегодня вечером дома?' },
]
// Форма + частица mi отдельным словом: versem mi.
function withMi(form: WordForm): WordForm {
  const q = questionParticle(form.word, null)
  return { word: `${form.word} ${q.word}`, morphs: [...form.morphs, { text: ' ', label: '', options: [], why: '' }, ...q.morphs] }
}

// ── Вписать окончание и перевод с русского в темах A1 (тикеты 17, 18) ─────────
// Порядок видов в шаге урока (.scratch/a1-pace): 1 - выбрать окончание, 2 -
// выбрать или вписать, 3 - превратить форму или перевести с русского. Во всех
// печатных видах основа вписана, ученик пишет окончание (lib/grammar/drill-kinds.ts).

const half = () => Math.random() < 0.5
// Русское множественное отличается от единственного: «такси» - и одно, и много.
const hasRuPlural = (w: Noun) => !!w.ruPl && w.ruPl !== w.ru
// Вопрос падежа одним словом - для инструкций.
const CASE_ASK: Record<Case, string> = { loc: 'где?', dat: 'куда?', abl: 'откуда?', ins: 'с кем? на чём?', acc: 'что?', gen: 'чей?' }
// Фраза с пропуском в начале: слово с заглавной.
const fillFrame = (tr: string, word: string) => (tr.startsWith('___') ? capitalize(tr.replace('___', word)) : tr.replace('___', word))

// Множественное число: «книги» - kitap + lar. В вопросе (после урока частицы
// mi) - «Это книги?» - Kitaplar mı?
function pluralTyped(topicId: string, w: Noun, kind: 'fill' | 'transform' | 'translate', q = false): TransformExercise {
  const form = nounForm(w, { plural: true })
  const parts = breakdownFrom(form)
  const last = parts.length - 1
  parts[last] = { ...parts[last], why: harmonyWhy2(w.tr, parts[last].text.includes('e') ? 'e' : 'a') }
  const ruPl = w.ruPl ?? w.ru
  const answer = q ? `${capitalize(form.word)} ${questionParticle(form.word, null).word}?` : form.word
  const ru = q ? `Это ${ruPl}?` : ruPl
  if (kind === 'fill') return fillEnding(topicId, { word: w.tr, ru, answer, form, ask: 'много (-ler / -lar)', parts })
  if (kind === 'translate') return translateEnding(topicId, { ru, answer, form, parts })
  return {
    kind: 'transform',
    topicId,
    source: q ? `${capitalize(w.tr)} ${questionParticle(w.tr, null).word}?` : w.tr,
    sourceRu: q ? `Это ${w.ru}?` : w.ru,
    instruction: 'Скажите про много',
    answer,
    variants: [],
    breakdown: parts,
    answerRu: ru,
    baseRu: w.ru,
  }
}

// Частица mi на существительном: «Это дом?» - Ev mi?
function questionTyped(topicId: string, w: Noun, kind: 'fill' | 'transform' | 'translate'): TransformExercise {
  const q = questionParticle(w.tr, null).morphs[0]
  const parts = [{ text: q.text, label: 'частица mi', why: `mi - отдельное слово, но гармонирует с "${markLastVowel(w.tr)}": ${harmonyWhy4(w.tr, q.text[1])}` }]
  const answer = `${capitalize(w.tr)} ${q.text}?`
  const form: WordForm = { word: q.text, morphs: [{ text: q.text, label: 'частица mi', options: [], why: parts[0].why }] }
  const ru = `Это ${w.ru}?`
  if (kind === 'fill') return fillEnding(topicId, { word: w.tr, ru, answer, form, ask: 'вопрос', what: 'частицу', parts })
  if (kind === 'translate') return translateEnding(topicId, { ru, answer, form, parts })
  return { kind: 'transform', topicId, source: `${capitalize(w.tr)}.`, sourceRu: `Это ${w.ru}.`, instruction: 'Задайте вопрос', answer, variants: [], breakdown: parts, answerRu: ru, baseRu: w.ru }
}

// «Я - ...»: «Мы устали.» - Biz yorgun + uz.
function copulaTyped(w: NounLike & PredicateWord, p: Person, kind: 'fill' | 'translate'): TransformExercise {
  const form = predicate(w, p)
  const answer = `${PRON[p]} ${form.word}.`
  const ru = sentenceRu(predicateRu(w, p))
  const args = { ru, answer, form, variants: [withoutPronoun(answer)] }
  const ex = kind === 'fill' ? fillEnding('copula', { ...args, word: w.tr, ask: PERSON_RU[p] }) : translateEnding('copula', args)
  return withRules(ex, copulaRule(p))
}

// değil с лицом. Вписать - окончание на değil; перевод - değil целиком: его
// поставить и есть правило («Он не устал» - O yorgun değil).
function degilTyped(w: NounLike & PredicateWord, p: Person, kind: 'fill' | 'translate'): TransformExercise {
  const form = predicate(w, p, { negative: true })
  const answer = `${PRON[p]} ${w.tr} ${form.word}.`
  const ru = sentenceRu(predicateRu(w, p, { negative: true }))
  const variants = [withoutPronoun(answer)]
  if (kind === 'fill') return withRules(fillEnding('negation-degil', { word: 'değil', ru, answer, form, ask: PERSON_RU[p], variants }), degilPersonRule(p))
  const parts = breakdownFrom(form)
  parts[0] = { ...parts[0], label: 'не', why: 'değil - отдельное слово после того, что отрицаем' }
  return withRules(translateEnding('negation-degil', { ru, answer, form, variants, parts }), degilPersonRule(p))
}

// Вопрос с mi к именному сказуемому: «Ты устал?» - Sen yorgun musun?
function miTyped(w: NounLike & PredicateWord, p: Person, kind: 'fill' | 'translate'): TransformExercise {
  const form = questionParticle(w.tr, p === 2 ? null : p)
  const answer = `${PRON[p]} ${w.tr} ${form.word}?`
  const ru = sentenceRu(predicateRu(w, p, { question: true }))
  const variants = [withoutPronoun(answer)]
  const ex =
    kind === 'fill'
      ? fillEnding('question-mi', { word: w.tr, ru, answer, form, ask: labels('вопрос', p !== 2 && PERSON_RU[p]), what: 'частицу', variants })
      : translateEnding('question-mi', { ru, answer, form, variants })
  return withRules(ex, MI_INTRO, miPersonRule(p))
}

// Глагол во времени: вписать окончание к словарной форме (gelmek) или
// перевести фразу. Отрицание и вопрос - только после своих уроков.
// «Мой / твой»: вписать окончание, сменить лицо или сказать про много,
// перевести «моя машина» или, после урока «у меня есть», - Arabam var.
const HAVE = ['araba', 'telefon', 'bilgisayar', 'kedi', 'çanta', 'kalem', 'defter', 'bilet', 'harita', 'valiz', 'kardeş', 'arkadaş', 'bahçe', 'saat']
function possTyped(w: Noun, p: Person, kind: 'fill' | 'transform' | 'translate', ctx?: GenCtx): TransformExercise {
  const form = nounForm(w, { poss: p })
  const ru = `${possRu(p, w.g)} ${w.ru}`
  if (kind === 'fill') return withRules(fillEnding('possessive', { word: w.tr, ru, answer: form.word, form, ask: POSS_RU[p] }), possRule(p))
  if (kind === 'translate') {
    if (p <= 1 && HAVE.includes(w.tr) && isPassed(ctx, 'var-yok-questions') && half()) {
      const tail = p === 0 ? 'var.' : 'var mı?'
      const answer = `${capitalize(form.word)} ${tail}`
      const ex = translateEnding('possessive', {
        ru: p === 0 ? `У меня есть ${w.ru}.` : `У тебя есть ${w.ru}?`,
        answer,
        form,
        variants: [`${p === 0 ? 'Benim' : 'Senin'} ${answer}`],
      })
      return withRules(ex, possRule(p), 'var-yok-questions')
    }
    return withRules(translateEnding('possessive', { ru, answer: form.word, form }), possRule(p))
  }
  const others = personsBy(possRule, ctx).filter((x) => x !== p)
  if (others.length && (!hasRuPlural(w) || half())) {
    const p2 = pick(others)
    const to = nounForm(w, { poss: p2 })
    // Сверху словарное слово (masa - стол), а не форма другого лица: "masan -
    // твой стол" под заданием "мой стол" путала (задача 88 из Telegram, 11.10).
    const ex: TransformExercise = {
      kind: 'transform',
      topicId: 'possessive',
      source: w.tr,
      sourceRu: w.ru,
      instruction: `Скажите "${possRu(p2, w.g)} ${w.ru}"`,
      answer: to.word,
      variants: [],
      breakdown: breakdownFrom(to),
      answerRu: `${possRu(p2, w.g)} ${w.ru}`,
      baseRu: w.ru,
    }
    return withRules(ex, possRule(p), possRule(p2))
  }
  // «Их» -ları - и «их машина», и «их машины»: про много тут сказать нечего.
  if (!hasRuPlural(w) || p === 5) return withRules(translateEnding('possessive', { ru, answer: form.word, form }), possRule(p))
  // Много: сначала -ler / -lar, потом «мой» (так и сказано в объяснении темы).
  const many = nounForm(w, { plural: true, poss: p })
  const manyRu = `${possRu(p, 'pl')} ${w.ruPl}`
  const ex: TransformExercise = {
    kind: 'transform',
    topicId: 'possessive',
    source: form.word,
    sourceRu: ru,
    instruction: `Скажите про много: "${manyRu}"`,
    answer: many.word,
    variants: [],
    breakdown: breakdownFrom(many),
    answerRu: manyRu,
    baseRu: w.ru,
  }
  return withRules(ex, possRule(p), 'plural-suffix-preview')
}

// ask.q - вопрос с этим лицом (тема «Вопрос с mi»).
function verbTyped(topicId: string, tense: 'present' | 'past', kind: 'fill' | 'translate', ctx?: GenCtx, ask: { q?: Person } = {}): TransformExercise {
  const v = pick(known(withComplement, ctx))
  const negRule = NEG_RULE[tense]!
  const canNeg = ask.q === undefined && isPassed(ctx, negRule)
  const neg = canNeg && Math.random() < (ctx?.focus?.has(negRule) ? 0.75 : 0.3)
  // Вопрос: у -yor лицо переезжает на mi (до урока лиц - только «он»), у -di остаётся на глаголе.
  const qRule = tense === 'past' ? 'past-tense-di-negative-question' : 'question-particle-mi-intro'
  const q = ask.q !== undefined || (kind === 'translate' && !neg && isPassed(ctx, qRule) && Math.random() < 0.3)
  const persons: Person[] = q && tense === 'present' ? personsBy(miPersonRule, ctx, [0, 1, 2, 3, 4]) : ALL
  const p = ask.q ?? pick(persons)
  const opts = { neg, q }
  const answer = verbSentence(v, tense, p, opts)
  const ru = sentenceRu(verbPhraseRu(v, tense, p, { negative: neg, question: q }) ?? verbHint(v))
  const form = verbForm(v, tense, p, neg)
  const parts = verbBreakdown(v, tense, p, opts)
  const variants = [withoutPronoun(answer)]
  const ex =
    kind === 'fill'
      ? fillEnding(topicId, { word: derivedForm(v, 'infinitive').word, ru, answer, form, ask: labels(PERSON_RU[p], neg && 'не'), variants, parts })
      : translateEnding(topicId, { ru, answer, form, variants, parts })
  return withRules(ex, TENSE_BASE[tense], v.complement?.rule, neg && negRule, q && qRule, q && tense === 'present' && miPersonRule(p))
}

// Формы темы «могу» сверх -ebilir: «не могу сейчас» (-eme + -iyor) - своё
// правило, остальные стоят на -ebil (also) или на -eme («не смог»).
// Подпись по лицу задания: у onlar «не смогут», а не «не смогу» (тикет 21).
const CANNOT_RU = ['не смогу', 'не сможешь', 'не сможет', 'не сможем', 'не сможете', 'не смогут']
const CANNOT_NOW_RU = ['не могу', 'не можешь', 'не может', 'не можем', 'не можете', 'не могут']
const byNumber = (one: string, many: string, tail: string) => (p: Person) => `${p < 3 ? one : many} ${tail}`
const ABILITY_KINDS: [AbilityKind, string, string | ((p: Person) => string)][] = [
  ['cannot', 'aorist-negative-mez', (p) => `${CANNOT_RU[p]} (-eme + -mez)`],
  ['cannotNow', 'ability-negative-eme', (p) => `${CANNOT_NOW_RU[p]} сейчас (-eme + -iyor)`],
  ['mayNot', 'ability-possibility', 'может и не ... (-me + -ebilir)'],
  ['could', 'ability-past', byNumber('смог', 'смогли', '(-ebil + -di)')],
  ['couldNot', 'ability-past', byNumber('не смог', 'не смогли', '(-eme + -di)')],
  ['couldHave', 'past-possibility-ebilirdi', byNumber('мог бы', 'могли бы', '(-ebilir + -di)')],
  ['couldNotHave', 'past-possibility-ebilirdi', byNumber('не смог бы', 'не смогли бы', '(-emez + -di)')],
]
const ABILITY_VARIANT: Record<AbilityKind, Variant> = Object.fromEntries(
  ABILITY_KINDS.map(([kind, rule, label]) => [
    kind,
    verbVariant('ability', {
      rule,
      also: kind === 'cannotNow' ? undefined : kind === 'cannot' || kind === 'couldNot' ? 'ability-negative-eme' : TENSE_BASE.ability,
      label,
      neg: kind === 'cannot' || kind.endsWith('Now') || kind.includes('Not'),
      // «Может и не…» — о других: о себе так не гадают.
      persons: kind === 'mayNot' ? [2, 5] : undefined,
      form: (v, p) => abilityForm(v, kind, p),
      ru: (v, p) => abilityPhraseRu(v, kind, p),
    }),
  ]),
) as Record<AbilityKind, Variant>

const abilityGenerate = withVariants(
  tenseGenerator('ability', 'ability', { from: 'present', instruction: 'Скажите "может / могу" (-ebil)', d2Verbs: () => vowelStemVerbs, allowNegative: false }),
  Object.values(ABILITY_VARIANT),
)

// «Не могу сейчас» (-eme + -iyor): 1 - собрать форму во фразе, 2 - вписать
// окончание к словарной форме, 3 - перевести с русского или сказать из «могу».
function cannotNowExercise(d: Difficulty, ctx?: GenCtx): Exercise {
  const variant = ABILITY_VARIANT.cannotNow
  if (d === 1) return variant.make(1, ctx)
  const p = pick(ALL)
  const pool = known(withComplement, ctx).filter((v) => abilityPhraseRu(v, 'cannotNow', p))
  if (!pool.length || (d === 3 && half())) return variant.make(d, ctx)
  const v = pick(pool)
  const form = abilityForm(v, 'cannotNow', p)
  const answer = `${PRON[p]} ${v.complement!.tr} ${form.word}.`
  const ru = sentenceRu(abilityPhraseRu(v, 'cannotNow', p)!)
  const variants = [withoutPronoun(answer)]
  const ex =
    d === 2
      ? fillEnding('ability', { word: derivedForm(v, 'infinitive').word, ru, answer, form, ask: labels(PERSON_RU[p], `${CANNOT_NOW_RU[p]} сейчас`), variants })
      : translateEnding('ability', { ru, answer, form, variants })
  return withRules(ex, 'ability-negative-eme', v.complement?.rule)
}

// Правила гармонии (реестр): часть 1 (e / a) и часть 2 (i / ı / u / ü) - тот же урок 10.
const HARMONY_2 = 'vowel-harmony-2'
const HARMONY_4 = 'vowel-harmony'
const PLURAL = 'plural-suffix-preview'
const INFINITIVE = 'verb-infinitive-preview'
const COMPARATIVE = 'comparative-daha-den'
// Правила темы «падеж глагола» и то из них, что открывает тему (первое по курсу).
const CASE_CHOICE_RULES = new Set(RULES.filter((r) => r.topic === 'case-choice').map((r) => r.id))
const CASE_CHOICE_OPENER = [...CASE_CHOICE_RULES].map((id) => RULE_BY_ID.get(id)!).sort((a, b) => lessonRank(a.lesson) - lessonRank(b.lesson))[0].id


// ── Широкое время: предложения и обещания (П50), общие истины (П53) ──────────
// Правила aorist-offers-promises (урок a2-l046) и aorist-general-truths
// (a2-l052) тренируются своими рамками: формы считает движок (verbForm,
// verbQuestion), фразы и перевод заданы, чтобы предложение звучало как в жизни.

type AoristFrame = { v: VerbLike & { ru: string }; before: string; ru: string; p: Person; q?: boolean }

// Вежливое предложение или просьба: Çay içer misiniz? Обещание: Yarın gelirim.
const AORIST_OFFERS: AoristFrame[] = [
  { v: { stem: 'iç', ru: 'пить' }, before: 'Çay', ru: 'Чаю выпьете?', p: 4, q: true },
  { v: { stem: 'al', aorist: 'ir', ru: 'брать' }, before: 'Bir şey', ru: 'Что-нибудь возьмёте?', p: 4, q: true },
  { v: { stem: 'otur', ru: 'сидеть' }, before: 'Biraz', ru: 'Присядете ненадолго?', p: 4, q: true },
  { v: { stem: 'bekle', ru: 'ждать' }, before: 'Biraz', ru: 'Подождёте немного?', p: 4, q: true },
  { v: { stem: 'ye', narrowBeforeY: 'yi', ru: 'есть' }, before: 'Pasta', ru: 'Торт будете?', p: 1, q: true },
  { v: { stem: 'aç', ru: 'открывать' }, before: 'Pencereyi', ru: 'Откроете окно?', p: 4, q: true },
  { v: { stem: 'kapat', ru: 'закрывать' }, before: 'Kapıyı', ru: 'Закроешь дверь?', p: 1, q: true },
  { v: { stem: 'ver', aorist: 'ir', ru: 'давать' }, before: 'Tuzu', ru: 'Передадите соль?', p: 4, q: true },
  { v: { stem: 'getir', ru: 'приносить' }, before: 'Hesabı', ru: 'Принесёте счёт?', p: 4, q: true },
  { v: { stem: 'dinle', ru: 'слушать' }, before: 'Beni', ru: 'Послушаешь меня?', p: 1, q: true },
  { v: { stem: 'gel', aorist: 'ir', ru: 'приходить' }, before: 'Yarın', ru: 'Завтра приду.', p: 0 },
  { v: { stem: 'ara', ru: 'звонить' }, before: 'Akşam', ru: 'Вечером позвоню.', p: 0 },
  { v: { stem: 'yap', ru: 'делать' }, before: 'Yemeği ben', ru: 'Еду приготовлю я.', p: 0 },
  { v: { stem: 'getir', ru: 'приносить' }, before: 'Kitabı yarın', ru: 'Книгу принесу завтра.', p: 0 },
  { v: { stem: 'bekle', ru: 'ждать' }, before: 'Seni burada', ru: 'Подожду тебя здесь.', p: 0 },
  { v: { stem: 'al', aorist: 'ir', ru: 'покупать' }, before: 'Ekmeği ben', ru: 'Хлеб куплю я.', p: 0 },
  { v: { stem: 'gönder', ru: 'отправлять' }, before: 'Fotoğrafı akşam', ru: 'Фото отправлю вечером.', p: 0 },
  { v: { stem: 'sor', ru: 'спрашивать' }, before: 'Ona', ru: 'Я у него спрошу.', p: 0 },
  { v: { stem: 'gel', aorist: 'ir', ru: 'приходить' }, before: 'Yarın', ru: 'Завтра придём.', p: 3 },
  { v: { stem: 'ara', ru: 'звонить' }, before: 'Seni', ru: 'Мы тебе позвоним.', p: 3 },
]

// Природа вещей: подлежащее во множественном, глагол в 3-м лице единственного.
const AORIST_TRUTHS: AoristFrame[] = [
  { v: { stem: 'uç', ru: 'летать' }, before: 'Kuşlar', ru: 'Птицы летают.', p: 2 },
  { v: { stem: 'yüz', ru: 'плавать' }, before: 'Balıklar', ru: 'Рыбы плавают.', p: 2 },
  { v: { stem: 'iç', ru: 'пить' }, before: 'Kediler süt', ru: 'Кошки пьют молоко.', p: 2 },
  { v: { stem: 'kayna', ru: 'кипеть' }, before: 'Su yüz derecede', ru: 'Вода кипит при ста градусах.', p: 2 },
  { v: { stem: 'doğ', ru: 'восходить' }, before: 'Güneş doğudan', ru: 'Солнце встаёт на востоке.', p: 2 },
  { v: { stem: 'bat', ru: 'садиться (о солнце)' }, before: 'Güneş batıdan', ru: 'Солнце садится на западе.', p: 2 },
  { v: { stem: 'büyü', ru: 'расти' }, before: 'Çocuklar çabuk', ru: 'Дети быстро растут.', p: 2 },
  { v: { stem: 'eri', ru: 'таять' }, before: 'Buz sıcakta', ru: 'Лёд тает в тепле.', p: 2 },
  { v: { stem: 'iç', ru: 'пить' }, before: 'Türkler çok çay', ru: 'Турки пьют много чая.', p: 2 },
  { v: { stem: 'uyu', ru: 'спать' }, before: 'İnsanlar geceleri', ru: 'Люди спят по ночам.', p: 2 },
  { v: { stem: 'ağla', ru: 'плакать' }, before: 'Bebekler çok', ru: 'Младенцы много плачут.', p: 2 },
  { v: { stem: 'ol', aorist: 'ir', ru: 'быть, становиться' }, before: 'Kış soğuk', ru: 'Зима бывает холодной.', p: 2 },
  { v: { stem: 'yağ', ru: 'идти (о дожде)' }, before: 'Kışın çok yağmur', ru: 'Зимой идёт много дождей.', p: 2 },
  { v: { stem: 'sev', ru: 'любить' }, before: 'Köpekler koşmayı', ru: 'Собаки любят бегать.', p: 2 },
]

function aoristFrame(d: Difficulty, fr: AoristFrame, rule: string): Exercise {
  const form = verbForm(fr.v, 'aorist', fr.p)
  const word = fr.q ? verbQuestion(fr.v, 'aorist', fr.p) : form.word
  const answer = `${fr.before} ${word}${fr.q ? '?' : '.'}`
  const parts = fr.q ? verbBreakdown({ ...fr.v, complement: null }, 'aorist', fr.p, { q: true }) : breakdownFrom(form)
  const rules = [TENSE_BASE.aorist, rule, fr.q && 'question-particle-mi-intro', fr.q && miPersonRule(fr.p)] as const
  const ask = labels(fr.p === 2 ? 'он, она, оно' : PRON_LOW[fr.p], 'широкое', fr.q && 'вопрос')
  if (d === 1) {
    // В вопросе лицо уезжает на частицу: собираем только içer, частица в рамке.
    const base = fr.q ? verbForm(fr.v, 'aorist', 2) : form
    const tail = fr.q ? ` ${word.split(' ')[1]}?` : '.'
    return withRules(affix('aorist', base, { tr: fr.v.stem, ru: fr.v.ru }, fr.q ? labels('широкое', 'вопрос') : ask, { tr: `${fr.before} ___${tail}`, ru: fr.ru }), ...rules)
  }
  if (d === 2) return withRules(fillEnding('aorist', { word: derivedForm(fr.v, 'infinitive').word, ru: fr.ru, answer, form, ask, parts }), ...rules)
  return withRules(translateEnding('aorist', { ru: fr.ru, answer, form, parts }), ...rules)
}

const CORE_TOPICS: Topic[] = [
  // ── A1 ─────────────────────────────────────────────────────────────────────
  // Первые две темы — гармония гласных: на ней держатся все аффиксы, поэтому
  // она объясняется раньше всего и открыта без уроков.
  {
    id: 'harmony-2',
    level: 'A1',
    title: 'Гармония гласных, часть 1: e / a',
    formula: 'e i ö ü - e   ·   a ı o u - a',
    alwaysOpen: true,
    visual: { kind: 2, afterRule: 3 },
    warmup: 2,
    rule: [
      'В турецком слова строятся как конструктор: к слову по очереди приклеиваются окончания - аффиксы. [e]v (дом) - [e]vl[e]r (дома) - [e]vl[e]r[i]m (мои дома).',
      'Гласная в аффиксе не постоянная - она подстраивается под слово. Это и есть гармония гласных: аффикс "звучит в тон" слову. Её нужно знать для любого аффикса, поэтому начинаем с неё.',
      'В турецком 8 гласных, и они делятся на две группы. Передние: e, i, ö, ü - язык продвинут вперёд. Задние: a, ı, o, u - язык оттянут назад. Буква ı (без точки) звучит примерно как русское "ы".',
      'Эти пары вы знаете по русскому, это твёрдые и мягкие гласные: а - я, ы - и, о - ё, у - ю. Твёрдые a, ı, o, u задние, мягкие e, i, ö, ü передние. Послушайте пары в таблице выше.',
      'Вторая подсказка - точки: i, ö, ü с точками - все передние. a, ı, o, u без точек - все задние. Единственное исключение - e: точек нет, но она передняя (мягкая, как "э"). Цветом в приложении: передние - голубые, задние - золотые.',
      'Правило: смотрим на ПОСЛЕДНЮЮ гласную слова. Она передняя (e, i, ö, ü) - в аффиксе будет "e". Задняя (a, ı, o, u) - в аффиксе будет "a".',
      'Потренируемся на аффиксе -l[e]r / -l[a]r - он значит "много" (множественное число). Сейчас важен только выбор гласной; подробно о множественном числе - в отдельной теме.',
      '[e]v - [e]vl[e]r (последняя гласная [e] - передняя). k[ı]z - k[ı]zl[a]r ([ı] - задняя). g[ö]z - g[ö]zl[e]r ([ö] - передняя). ok[u]l - ok[u]ll[a]r ([u] - задняя).',
      'Если гласных в слове несколько, смотрим только на последнюю: kit[a]p - kit[a]pl[a]r (последняя - [a]), otob[ü]s - otob[ü]sl[e]r (последняя - [ü]).',
    ],
    examples: () => [
      example(nounForm(n('ev'), { plural: true }), 'дома'),
      example(nounForm(n('okul'), { plural: true }), 'школы'),
      example(nounForm(n('kitap'), { plural: true }), 'книги'),
      example(nounForm(n('otobüs'), { plural: true }), 'автобусы'),
    ],
    // 1 - выбрать -ler / -lar, 2 - выбрать или вписать, 3 - превратить или перевести.
    generate: (d, ctx) => {
      const pool = knownWords(d === 1 ? oneVowel : d === 2 ? mixedVowels : harmonyNouns, ctx)
      const counted = pool.filter(hasRuPlural)
      if (d === 1 || !counted.length) return withRules(pluralHarmony('harmony-2', pick(pool)), HARMONY_2)
      if (d === 2) return withRules(half() ? pluralHarmony('harmony-2', pick(pool)) : pluralTyped('harmony-2', pick(counted), 'fill'), HARMONY_2)
      return withRules(pluralTyped('harmony-2', pick(counted), half() ? 'transform' : 'translate'), HARMONY_2)
    },
  },
  {
    id: 'harmony-4',
    level: 'A1',
    title: 'Гармония гласных, часть 2: i / ı / u / ü',
    formula: 'e i - i  ·  a ı - ı  ·  o u - u  ·  ö ü - ü',
    alwaysOpen: true,
    visual: { kind: 4, afterRule: 4 },
    warmup: 4,
    rule: [
      'Во многих аффиксах вариантов гласной четыре: i, ı, u, ü. Правило то же: смотрим на последнюю гласную слова, но проверяем в ней два признака.',
      'Признак 1 - ряд, как в первой части: передняя (e, i, ö, ü) или задняя (a, ı, o, u).',
      'Признак 2 - губы: округлая гласная (o ö u ü) произносится с губами трубочкой, неокруглая (a e ı i) - без этого.',
      'Складываем: передняя неокруглая (e, i) - "i". Задняя неокруглая (a, ı) - "ı". Задняя округлая (o, u) - "u". Передняя округлая (ö, ü) - "ü".',
      'Потренируемся на вопросительной частице mi - это русское "ли?" / вопросительная интонация. Она пишется отдельным словом, но гласная в ней меняется по этому правилу: [E]v m[i]? Arab[a] m[ı]? Ok[u]l m[u]? G[ö]z m[ü]?',
      'Чаще всего путают ı и i: после a и ı нужна "ı" без точки (k[ı]z m[ı]?), а после e и i "i" с точкой ([e]v m[i]?).',
    ],
    examples: () => [questionHarmony('harmony-4', n('ev')), questionHarmony('harmony-4', n('araba')), questionHarmony('harmony-4', n('okul')), questionHarmony('harmony-4', n('göz'))].map(
      (ex, i) => ({
        parts: ex.slots.map((s) => ({ text: s.answer, label: s.label, why: s.why })),
        ru: ['дом?', 'машина?', 'школа?', 'глаз?'][i],
      }),
    ),
    // 3 — смешанное: сначала -ler/-lar (2 варианта), потом mi (4 варианта).
    // Множественное число - только после его урока (a1-l014b); до него третья ступень без -ler.
    // 2 - ещё и вписать частицу, 3 - превратить в вопрос или перевести с русского.
    generate: (d, ctx) => {
      const typed = d === 1 ? null : d === 2 ? (half() ? 'fill' : null) : pick(['old', 'transform', 'translate'] as const)
      if (typed && typed !== 'old') return withRules(questionTyped('harmony-4', pick(knownWords(d === 2 ? mixedVowels : harmonyNouns, ctx)), typed), HARMONY_4)
      const plural = d === 3 && isPassed(ctx, 'plural-suffix-preview')
      // Несчитаемые (çay, ekmek) во множественном не спрашивают.
      const pool = knownWords(d === 1 ? oneVowel : d === 2 ? mixedVowels : harmonyNouns, ctx).filter((w) => !plural || w.ruPl)
      return withRules(questionHarmony('harmony-4', pick(pool), plural), HARMONY_4, plural && 'plural-suffix-preview')
    },
  },
  {
    id: 'copula',
    level: 'A1',
    title: '"Я - ...": личные аффиксы',
    // Игра на окончание лица (владелец 19.10.2026): на итоге тренажёра темы.
    speedGame: '/grammar/game/cop',
    formula: 'слово + лицо: yorgunum, yorgunsun, yorgunuz · после гласной y: mutluyum',
    rule: [
      '"Я врач" по-турецки - одно слово: dokt[o]r[u]m. Слова "есть / являюсь" нет. Вместо него к слову приклеивается окончание, которое показывает, кто это: я, ты, мы, вы.',
      {
        text: 'Окончания по лицам - в таблице. Он / она - просто слово, без окончания: doktor - он врач.',
        table: (ctx) => ({
          head: ['', 'dokt[o]r\nврач', 'öğretm[e]n\nучитель'],
          rows: byPerson([(p) => cell(predicate(n('doktor'), p)), (p) => cell(predicate(n('öğretmen'), p))], personsBy(copulaRule, ctx)),
          note: 'Сравните колонки: окончания одни и те же, меняется только гласная - после o она u, после e она i. У "они" вариантов два: -lar / -ler.',
        }),
      },
      {
        text: 'Гласная окончания подстраивается под последнюю гласную слова - 4 варианта, как в теме "Гармония, часть 2":',
        table: () => ({
          head: ['последняя гласная', 'я ...', 'ты ...'],
          rows: [adj('zengin'), adj('hazır'), n('doktor'), n('müdür')].map((w, i) => [H4[i], cell(predicate(w, 0)), cell(predicate(w, 1))]),
          note: 'zengin - богатый, hazır - готов, doktor - врач, müdür - директор.',
        }),
      },
      {
        text: 'Если слово кончается на гласную, в "я" и "мы" между словом и окончанием появляется буква y - две гласные подряд не ставят. В "ты" и "вы" y не нужна.',
        table: (ctx) => ({
          head: ['', 'hast[a]\nболен', 'öğrenc[i]\nстудент'],
          rows: byPerson([(p) => cell(predicate(adj('hasta'), p)), (p) => cell(predicate(n('öğrenci'), p))], personsBy(copulaRule, ctx)),
          accent: ['buffer'],
        }),
      },
      'sen - "ты" для друзей и родных. siz - "вы" для нескольких людей и вежливое "Вы" одному человеку: Siz dokt[o]rs[u]n[u]z. - Вы врач.',
      'Местоимение можно не говорить, лицо и так видно по окончанию: Ben dokt[o]r[u]m = Dokt[o]r[u]m.',
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        [example(predicate(n('doktor'), 0), 'я - врач (o - u)'), copulaRule(0)],
        [example(predicate(n('öğretmen'), 1), 'ты - учитель'), copulaRule(1)],
        [example(predicate(adj('hasta'), 3), 'мы больны - после гласной "y"'), copulaRule(3)],
        [example(predicate(n('doktor'), 4), 'вы - врач (вежливо) / вы - врачи'), copulaRule(4)],
        [example(predicate(n('öğretmen'), 5), 'они - учителя'), copulaRule(5)],
      ]),
    generate: (d, ctx) => {
      // Лица — только пройденные: до таблицы лиц есть лишь sen / siz (урок 5).
      const persons = personsBy(copulaRule, ctx, [0, 1, 3, 4, 5])
      const p = pick(persons)
      // Вписать окончание (2) и перевод с русского (3) - на тех же словах.
      if ((d === 2 || (d === 3 && persons.length < 2)) && half()) return copulaTyped(pick(knownWords(predicates.filter((x) => !x.soft), ctx)), p, 'fill')
      if (d === 3 && half()) return copulaTyped(pick(knownWords(predicates.filter((x) => !x.soft), ctx)), p, 'translate')
      if (d <= 2 || persons.length < 2) {
        // 1 — слова на согласную (без связки y), 2 — на гласную (связка y).
        // Слова с чередованием (kitap → kitabı) — не здесь: оно объясняется позже.
        const plain = knownWords(predicates.filter((x) => !x.soft), ctx)
        const pool = d === 1 ? plain.filter((x) => /[^aeıioöuü]$/.test(x.tr)) : plain.filter((x) => /[aeıioöuü]$/.test(x.tr))
        const w = pick(pool.length ? pool : plain)
        return withRules(affix('copula', predicate(w, p), w, labels(PRON_LOW[p], `"${predicateRu(w, p)}"`), { tr: `${PRON[p]} ___.`, ru: predicateRu(w, p) }), copulaRule(p))
      }
      // Чередование (genç → gencim) объясняется позже — и здесь тоже без него.
      const w = pick(knownWords(predicates.filter((x) => !x.soft), ctx))
      const p2 = pick(persons.filter((x) => x !== p))
      const answer = `${PRON[p2]} ${predicate(w, p2).word}.`
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'copula',
        source: `${PRON[p]} ${predicate(w, p).word}.`,
        sourceRu: predicateRu(w, p),
        instruction: `Скажите то же от лица "${PRON_LOW[p2]}" (${PERSON_RU[p2]})`,
        answer,
        variants: [withoutPronoun(answer)],
        breakdown: breakdownFrom(predicate(w, p2)),
        answerRu: predicateRu(w, p2),
        baseRu: w.ru,
      }, copulaRule(p), copulaRule(p2))
    },
  },
  {
    id: 'plural',
    level: 'A1',
    title: 'Множественное число -ler / -lar',
    formula: 'слово + -ler (после e i ö ü) / -lar (после a ı o u)',
    rule: [
      {
        text: 'Много предметов - окончание -l[e]r или -l[a]r. Какое из двух, решает последняя гласная слова.',
        table: () => ({
          head: ['последняя гласная', 'окончание', 'примеры'],
          rows: [
            [H2[0], '-l[e]r', [cell(nounForm(n('ev'), { plural: true })), ', ', cell(nounForm(n('göz'), { plural: true }))]],
            [H2[1], '-l[a]r', [cell(nounForm(n('araba'), { plural: true })), ', ', cell(nounForm(n('okul'), { plural: true }))]],
          ],
          note: 'Передние гласные e, i, ö, ü дают -ler, задние a, ı, o, u дают -lar.',
        }),
      },
      {
        text: 'Если гласных несколько - смотрим только на последнюю:',
        table: () => ({
          head: ['слово', 'последняя', 'много'],
          rows: [
            ['kit[a]p\nкнига', '[a]', cell(nounForm(n('kitap'), { plural: true }))],
            ['otob[ü]s\nавтобус', '[ü]', cell(nounForm(n('otobüs'), { plural: true }))],
            ['bilgisay[a]r\nкомпьютер', '[a]', cell(nounForm(n('bilgisayar'), { plural: true }))],
            ['telef[o]n\nтелефон', '[o]', cell(nounForm(n('telefon'), { plural: true }))],
          ],
        }),
      },
      'Несколько заимствованных слов - исключения: гласная в них задняя, а окончание переднее: sa[a]t - sa[a]tl[e]r (часы), k[a]lp - k[a]lpl[e]r (сердца). Их просто запоминаем.',
      'После числа множественное число не нужно: iki [e]v - два дома (не evler), üç arab[a] - три машины.',
      'В вопросе частица mi смотрит на последнюю гласную уже с окончанием: Evl[e]r m[i]? Arabal[a]r m[ı]?',
    ],
    examples: () => [
      example(nounForm(n('ev'), { plural: true }), 'дома'),
      example(nounForm(n('araba'), { plural: true }), 'машины'),
      example(nounForm(n('saat'), { plural: true }), 'часы (saat - исключение: аффиксы переднего ряда)'),
    ],
    generate: (d, ctx) => {
      if (d === 1) {
        const w = pick(knownWords(NOUNS, ctx))
        return withRules(affix('plural', nounForm(w, { plural: true }), w, 'множественное число'), PLURAL)
      }
      // Вписать (2), превратить и перевести (3): только счётные слова, у них есть
      // русское множественное. С частицей mi - после её урока.
      const counted = knownWords(NOUNS.filter((w) => hasRuPlural(w) && !w.front), ctx)
      const q = isPassed(ctx, 'question-particle-mi-intro') && half()
      const typed = d === 2 ? (half() ? 'fill' : null) : pick(['old', 'transform', 'translate'] as const)
      if (typed && typed !== 'old') return withRules(pluralTyped('plural', pick(counted), typed, q && typed !== 'fill'), PLURAL, q && typed !== 'fill' && MI_INTRO)
      // Только то, что уже объяснено: 2 — слова с разными гласными и
      // исключения (saat), 3 — множественное число в вопросе с mi (до урока
      // частицы mi — ступень 2).
      if (d === 2 || !isPassed(ctx, 'question-particle-mi-intro')) {
        const w = pick(knownWords([...mixedVowels, n('saat')], ctx))
        return withRules(affix('plural', nounForm(w, { plural: true }), w, 'множественное число'), PLURAL)
      }
      return withRules(questionHarmony('plural', pick(knownWords(harmonyNouns, ctx).filter((w) => w.ruPl)), true), PLURAL, MI_INTRO)
    },
  },
  {
    id: 'negation-degil',
    level: 'A1',
    title: 'Отрицание değil',
    formula: 'слово + değil + лицо',
    // Разметка [i]: красятся только гласные, о которых идёт речь (иначе
    // VowelText красит все подряд, и правило тонет в цвете).
    rule: [
      'Чтобы сказать "не врач", "не устал", после слова ставим отдельное слово değil - "не". Сначала слово, потом değil: Doktor değ[i]l. - Не врач. Yorgun değ[i]l. - Не устал.',
      {
        text: 'Окончание "я / ты / мы / вы" переезжает на değil. Последняя гласная в değil всегда [i], поэтому и в окончании всегда i, даже если в самом слове a или o:',
        rule: 'degil-person',
        table: () => ({
          head: ['', 'голоден', 'не голоден'],
          rows: byPerson([(p) => cell(predicate(adj('aç'), p)), (p) => cell(predicate(adj('aç'), p, { negative: true }), 'aç ')], [0, 1, 3, 4]),
          note: 'В левой колонке гласная по слову aç - ı, в правой - по değil, всегда i.',
        }),
      },
      'Он / она / они - просто değil, без окончания: O doktor değ[i]l. - Он не врач.',
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(predicate(n('doktor'), 2, { negative: true }), 'он не врач', 'doktor '),
        [example(predicate(adj('yorgun'), 0, { negative: true }), 'я не устал', 'yorgun '), 'degil-person'],
        [example(predicate(n('öğrenci'), 4, { negative: true }), 'вы не студенты', 'öğrenci '), 'degil-person'],
      ]),
    generate: (d, ctx) => {
      const w = pick(knownWords(predicates, ctx))
      // Без «они»: последнее правило говорит «просто değil, без окончания»,
      // а вариант değiller в заданиях противоречил бы ему (прогон 2026-09-17).
      // Лицо на değil — своё правило; до него только «он»: O doktor değil.
      // Выбирать в değil тогда нечего — сразу «Сделайте отрицание».
      const persons = personsBy(degilPersonRule, ctx, [0, 1, 3, 4])
      const p: Person = persons.length ? pick(persons) : 2
      if (d === 1 && p !== 2) {
        const ex = affix('negation-degil', predicate(w, p, { negative: true }), { tr: 'değil', ru: 'не' }, labels(PRON_LOW[p], 'не'), {
          tr: `${PRON[p]} ${w.tr} ___.`,
          ru: predicateRu(w, p, { negative: true }),
        })
        return withRules(ex, degilPersonRule(p))
      }
      // Вписать окончание на değil (у «он» окончания нет) и перевод с русского.
      if (d === 2 && p !== 2 && half()) return degilTyped(w, p, 'fill')
      if (d === 3 && half()) return degilTyped(w, p, 'translate')
      const neg = predicate(w, p, { negative: true })
      const answer = `${PRON[p]} ${w.tr} ${neg.word}.`
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'negation-degil',
        source: `${PRON[p]} ${predicate(w, p).word}.`,
        sourceRu: predicateRu(w, p),
        instruction: 'Сделайте отрицание',
        answer,
        variants: [withoutPronoun(answer)],
        breakdown: breakdownFrom(neg),
      }, degilPersonRule(p))
    },
  },
  {
    id: 'question-mi',
    level: 'A1',
    title: 'Вопрос с частицей mi',
    formula: 'слово + mı / mi / mu / mü отдельно · с лицом: doktor musun?',
    rule: [
      {
        text: 'Вопрос "да / нет" - частица mi после слова, о котором спрашиваем. Пишется отдельно, но гласная в ней подстраивается под слово, 4 варианта:',
        table: () => ({
          head: ['последняя гласная', 'вопрос', ''],
          rows: [
            [H4[0], cell(questionParticle('ev', null), '[e]v ', '?'), 'дом?'],
            [H4[1], cell(questionParticle('araba', null), 'arab[a] ', '?'), 'машина?'],
            [H4[2], cell(questionParticle('okul', null), 'ok[u]l ', '?'), 'школа?'],
            [H4[3], cell(questionParticle('göz', null), 'g[ö]z ', '?'), 'глаз?'],
          ],
        }),
      },
      {
        text: 'Окончание "ты" и "вы" прирастает к частице, а не к слову. Гласная в mi по-прежнему смотрит на слово:',
        rule: 'question-mi-sen-siz',
        table: () => ({
          head: ['', 'в порядке', 'в порядке?'],
          rows: byPerson([(p) => cell(predicate(adj('iyi'), p)), (p) => cell(questionParticle('iyi', p), '[i]y[i] ', '?')], [1, 4]),
        }),
      },
      {
        text: 'Окончание "я / ты / мы / вы" переезжает со слова на частицу. Частица кончается на гласную, поэтому в "я" и "мы" между ней и окончанием ставится y. Он / она - без окончания:',
        rule: 'question-mi-person',
        table: () => ({
          head: ['', 'голоден', 'голоден?'],
          rows: byPerson([(p) => cell(predicate(adj('aç'), p)), (p) => cell(questionParticle('aç', p), '[a]ç ', '?')], [0, 1, 2, 3, 4]),
          accent: ['buffer'],
        }),
      },
      { text: 'Исключение - прошедшее время на -di (о нём в своей теме): там окончание остаётся на глаголе: Geld[i]n m[i]?', rule: 'past-tense-di-negative-question' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(questionParticle('doktor', null), 'он врач?', 'doktor '),
        [example(questionParticle('doktor', 1), 'ты врач?', 'doktor '), 'question-mi-sen-siz'],
        [example(questionParticle('hasta', 0), 'я болен?', 'hasta '), 'question-mi-person'],
        [example(questionParticle('geliyor', 4), 'вы идёте?', 'geliyor '), 'question-mi-person'],
      ]),
    generate: (d, ctx) => {
      // Лицо на mi — своё правило; до него только «он» без окончания: O doktor mu?
      const p = pick(personsBy(miPersonRule, ctx, [0, 1, 2, 3, 4]))
      if (d === 1) {
        const w = pick(knownWords(predicates, ctx))
        const q = questionParticle(w.tr, p === 2 ? null : p)
        const ex = affix('question-mi', q, { tr: 'mi', ru: 'частица вопроса' }, labels('вопрос', PRON_LOW[p]), { tr: `${PRON[p]} ${w.tr} ___?`, ru: predicateRu(w, p, { question: true }) })
        return withRules(ex, MI_INTRO, miPersonRule(p))
      }
      // Вписать частицу с лицом (2) и перевод с русского (3); у глагола - после урока -iyor.
      if (d === 2 && half()) return miTyped(pick(knownWords(predicates, ctx)), p, 'fill')
      if (d === 3 && half()) {
        if (isPassed(ctx, 'present-continuous-yor') && half()) return withRules(verbTyped('question-mi', 'present', 'translate', ctx, { q: p }), 'present-continuous-yor')
        return miTyped(pick(knownWords(predicates, ctx)), p, 'translate')
      }
      // Вопрос к глаголу — после урока -iyor, иначе ступень ниже.
      if (d === 2 || !isPassed(ctx, 'present-continuous-yor')) {
        const w = pick(knownWords(predicates, ctx))
        const q = questionParticle(w.tr, p === 2 ? null : p)
        const answer = `${PRON[p]} ${w.tr} ${q.word}?`
        return withRules<TransformExercise>({
          kind: 'transform',
          topicId: 'question-mi',
          source: `${PRON[p]} ${predicate(w, p).word}.`,
          sourceRu: predicateRu(w, p),
          instruction: 'Задайте вопрос',
          answer,
          variants: [withoutPronoun(answer)],
          breakdown: breakdownFrom(q),
        }, MI_INTRO, miPersonRule(p))
      }
      // Только настоящее время: будущее и прошедшее — отдельные темы дальше по курсу.
      const v = pick(known(withComplement, ctx))
      return withRules(transformVerb('question-mi', v, { tense: 'present', p }, { tense: 'present', p, q: true }, 'Задайте вопрос'), MI_INTRO, miPersonRule(p))
    },
    speedGame: '/grammar/game/mi',
    chatGoal: { focus: 'вопросы с частицей mi', detect: 'вопрос "да/нет" с частицей mi/mı/mu/mü (Geliyor musun? Doktor musun?)' },
  },
  {
    id: 'possessive',
    level: 'A1',
    title: '"Мой", "твой": annem, annen',
    // Полная парадигма (annesi, annemiz...) вернётся с правилом A2 possessive-suffix-intro.
    formula: 'annem · annen · после согласной: kardeşim, kardeşin',
    rule: [
      '"Мой дом" - [e]v[i]m: чей это предмет, показывает окончание на самом предмете.',
      {
        text: 'Окончание зависит от того, чей предмет, и от последней буквы слова. После согласной окончание начинается с гласной, после гласной эта гласная не нужна:',
        // До A2 в таблице только "мой" и "твой" (урок 31, .scratch/a1-pace).
        table: (ctx) => ({
          head: ['', '[e]v · дом\nна согласную', 'arab[a] · машина\nна гласную'],
          rows: personsBy(possRule, ctx).map((p) => [POSS_RU[p], cell(nounForm(n('ev'), { poss: p })), cell(nounForm(n('araba'), { poss: p }))]),
          accent: ['buffer'],
        }),
      },
      {
        text: 'Гласная окончания - по последней гласной слова, 4 варианта:',
        table: (ctx) => {
          const second: Person = isPassed(ctx, possRule(3)) ? 3 : 1
          return {
            head: ['последняя гласная', 'мой', POSS_RU[second]],
            rows: [n('ev'), { tr: 'kız' }, n('okul'), n('göz')].map((w, i) => [H4[i], cell(nounForm(w, { poss: 0 })), cell(nounForm(w, { poss: second }))]),
            note: 'ev - дом, kız - дочь, okul - школа, göz - глаз.',
          }
        },
      },
      'Слова "мой, твой" (benim, senin) добавляют, только чтобы подчеркнуть: benim [e]v[i]m - мой дом, ничей другой.',
      'Если предметов много, сначала идёт -ler / -lar, потом "мой": [e]vl[e]r[i]m - мои дома, arab[a]l[a]r[ı]n - твои машины.',
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('ev'), { poss: 0 }), 'мой дом'),
        example(nounForm(n('araba'), { poss: 1 }), 'твоя машина'),
        [example(nounForm(n('araba'), { poss: 2 }), 'его машина'), possRule(2)],
        [example(nounForm(n('okul'), { poss: 3 }), 'наша школа'), possRule(3)],
        example(nounForm(n('ev'), { plural: true, poss: 0 }), 'мои дома'),
      ]),
    // Слова с чередованием (kitap → kitabım) — не здесь: оно объясняется в
    // следующей теме. Падежи после притяжательного — в темах падежей.
    generate: (d, ctx) => {
      // До A2 только "мой" и "твой": остальные лица ждут правила possessive-suffix-intro.
      const p = pick(personsBy(possRule, ctx))
      const plain = knownWords(NOUNS.filter((x) => !x.soft), ctx)
      // Вписать (2), превратить и перевести (3) - см. possTyped.
      if (d === 2 && half()) return possTyped(pick(plain), p, 'fill', ctx)
      if (d === 3) {
        const typed = pick(['old', 'transform', 'translate'] as const)
        if (typed !== 'old') return possTyped(pick(plain), p, typed, ctx)
      }
      if (d === 1) {
        const w = pick(plain)
        return withRules(affix('possessive', nounForm(w, { poss: p }), w, POSS_RU[p]), possRule(p))
      }
      if (d === 2) {
        const vowel = plain.filter((x) => vowelFinal.includes(x))
        const w = pick(Math.random() < 0.6 && vowel.length ? vowel : plain)
        return withRules(affix('possessive', nounForm(w, { poss: p }), w, POSS_RU[p]), possRule(p))
      }
      const w = pick(plain)
      return withRules(affix('possessive', nounForm(w, { plural: true, poss: p }), w, labels('множественное число', POSS_RU[p])), possRule(p), 'plural-suffix-preview')
    },
  },
  {
    id: 'softening',
    level: 'A2',
    title: 'Чередование согласных: kitap - kitabı',
    formula: 'p - b · ç - c · t - d · k - ğ (перед гласной)',
    rule: [
      {
        text: 'Если слово кончается на p, ç, t, k и после него идёт окончание, начинающееся с гласной, последняя согласная смягчается:',
        table: (ctx) => {
          // «Его» (ağacı) — после урока притяжательных, до него — «мой» (ağacım).
          const his: Person = isPassed(ctx, possRule(2)) ? 2 : 0
          return {
            head: ['', 'слово', 'перед гласной'],
            rows: [
              ['p - b', 'kitap\nкнига', cell(nounForm(n('kitap'), { poss: 0 }))],
              ['ç - c', 'ağaç\nдерево', cell(nounForm(n('ağaç'), { poss: his }))],
              ['t - d', 'kağıt\nбумага', cell(nounForm(n('kağıt'), { poss: his }))],
              ['k - ğ', 'çocuk\nребёнок', cell(nounForm(n('çocuk'), { poss: his }))],
              ['nk - ng', 'renk\nцвет', cell(nounForm(n('renk'), { poss: his }))],
            ],
            accent: ['changed'],
          }
        },
      },
      {
        text: 'Перед согласной ничего не меняется. Смягчение только на стыке "согласная + гласная":',
        table: () => ({
          head: ['', 'перед гласной', 'перед согласной'],
          rows: [n('kitap'), n('çocuk'), n('ağaç')].map((w) => [w.tr, cell(nounForm(w, { poss: 0 })), cell(nounForm(w, { plural: true }))]),
          accent: ['changed'],
        }),
      },
      'Короткие слова из одного слога обычно не меняются: s[ü]t - s[ü]t[ü], t[o]p - t[o]p[u]. Но глаголы git и et меняются: g[i]d[i]yorum, [e]d[i]yorum.',
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('kitap'), { poss: 0 }), 'моя книга'),
        [example(nounForm(n('çocuk'), { case: 'acc' }), 'ребёнка (винительный падеж)'), GOV_CASE_RULE.acc],
        [example(nounForm(n('kitap'), { case: 'loc' }), 'в книге - перед согласной без изменений'), GOV_CASE_RULE.loc],
      ]),
    generate: (d, ctx) => {
      // Аффиксы на гласную здесь — только притяжательные (уже объяснены);
      // падежи -ı / -a идут дальше по курсу. -ları на согласную — ловушка: без чередования.
      // Смягчение объясняется раньше урока притяжательных — до него только
      // «мой / твой» (adım, adın); не хватает вариантов на ступени — ступень ниже.
      const w = pick(knownWords(softNouns, ctx))
      const d2 = personsBy(possRule, ctx, [3, 4, 5])
      const cases = passedOnly<GovCase>(['loc', 'abl'], (c) => GOV_CASE_RULE[c], ctx)
      const step = d === 3 && !cases.length ? 2 : d
      if (step === 1 || (step === 2 && !d2.length)) {
        const p = pick(personsBy(possRule, ctx, [0, 1, 2]))
        return withRules(affix('softening', nounForm(w, { poss: p }), w, POSS_RU[p]), 'consonant-softening', possRule(p))
      }
      if (step === 2) {
        const p = pick(d2)
        return withRules(affix('softening', nounForm(w, { poss: p }), w, POSS_RU[p]), 'consonant-softening', possRule(p))
      }
      // Ловушка наоборот: перед согласной смягчать НЕ надо — даём выбрать основу.
      const c = pick(cases)
      const ex = affix('softening', nounForm(w, { case: c }), w, `${CASE_RU[c]} · основа меняется?`)
      ex.slots[0] = { ...ex.slots[0], options: [w.tr, w.soft!], why: 'перед согласной аффикса основа не меняется' }
      return withRules(ex, 'consonant-softening', GOV_CASE_RULE[c])
    },
  },
  {
    id: 'locative',
    level: 'A1',
    title: 'Где? -de / -da / -te / -ta',
    formula: '-de · -da · после глухих -te · -ta: evde, mutfakta',
    rule: [
      'Где? - окончание -d[e] / -d[a]: [e]vd[e] - дома, ok[u]ld[a] - в школе. Гласная - по последней гласной слова.',
      {
        text: 'После глухих согласных ç, f, h, k, p, s, ş, t буква d превращается в t. Запомнить глухие помогает фраза "fıstıkçı şahap" - в ней все восемь. Вариантов получается четыре:',
        table: () => ({
          head: ['', H2[0], H2[1]],
          rows: [
            ['обычно', cell(nounForm(n('ev'), { case: 'loc' })), cell(nounForm(n('okul'), { case: 'loc' }))],
            ['после глухой\nç f h k p s ş t', cell(nounForm(n('otobüs'), { case: 'loc' })), cell(nounForm(n('park'), { case: 'loc' }))],
          ],
          accent: ['changed'],
          note: 'evde - дома, okulda - в школе, otobüste - в автобусе, parkta - в парке.',
        }),
      },
      { text: 'Если перед этим стоит "его / её" (-ı / -i), между ними появляется n: [e]v[i]nd[e] - в его доме, arab[a]s[ı]nd[a] - в его машине.', rule: 'possessive-n-buffer' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('ev'), { case: 'loc' }), 'дома, в доме'),
        example(nounForm(n('park'), { case: 'loc' }), 'в парке'),
        [example(nounForm(n('araba'), { poss: 2, case: 'loc' }), 'в его машине'), 'possessive-n-buffer'],
      ]),
    dtWarmup: 'DA',
    generate: caseGenerator('locative', 'loc', () => hardFinal, undefined, true),
    chatGoal: { focus: 'местный падеж -de/-da (где?)', detect: 'слово в местном падеже -de/-da/-te/-ta (evde, parkta)' },
  },
  {
    id: 'dative',
    level: 'A1',
    title: 'Куда? Кому? -e / -a',
    formula: '-e · -a · после гласной -ye · -ya: eve, bankaya',
    rule: [
      'Куда? Кому? - окончание -[e] / -[a]: [e]v[e] - домой, ok[u]l[a] - в школу.',
      {
        text: 'После гласной вставляется y - две гласные подряд не ставят:',
        table: () => ({
          head: ['', H2[0], H2[1]],
          rows: [
            ['после согласной', cell(nounForm(n('ev'), { case: 'dat' })), cell(nounForm(n('okul'), { case: 'dat' }))],
            ['после гласной', cell(nounForm(n('müze'), { case: 'dat' })), cell(nounForm(n('araba'), { case: 'dat' }))],
          ],
          accent: ['buffer'],
          note: 'eve - домой, okula - в школу, müzeye - в музей, arabaya - в машину.',
        }),
      },
      { text: 'Окончание начинается с гласной, поэтому p, ç, t, k смягчаются: kit[a]p - kit[a]b[a], sok[a]k - sok[a]ğ[a].', rule: 'consonant-softening' },
      { text: 'После "его / её" появляется n: [e]v[i]n[e] - в его дом, arab[a]s[ı]n[a] - в его машину.', rule: 'possessive-n-buffer' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('okul'), { case: 'dat' }), 'в школу'),
        example(nounForm(n('müze'), { case: 'dat' }), 'в музей'),
        [example(nounForm(n('sokak'), { case: 'dat' }), 'на улицу'), 'consonant-softening'],
      ]),
    generate: caseGenerator('dative', 'dat', () => [...vowelFinal, ...softNouns], undefined, true),
    chatGoal: { focus: 'дательный падеж -e/-a (куда? кому?)', detect: 'слово в дательном падеже -e/-a/-ye/-ya (okula, müzeye)' },
  },
  {
    id: 'ablative',
    level: 'A1',
    title: 'Откуда? -den / -dan / -ten / -tan',
    formula: '-den · -dan · после глухих -ten · -tan: evden, mutfaktan',
    rule: [
      'Откуда? - окончание -d[e]n / -d[a]n: [e]vd[e]n - из дома, ok[u]ld[a]n - из школы.',
      {
        text: 'После глухих согласных (ç, f, h, k, p, s, ş, t) - -t[e]n / -t[a]n. Вариантов получается четыре:',
        table: () => ({
          head: ['', H2[0], H2[1]],
          rows: [
            ['обычно', cell(nounForm(n('ev'), { case: 'abl' })), cell(nounForm(n('okul'), { case: 'abl' }))],
            ['после глухой\nç f h k p s ş t', cell(nounForm(n('otobüs'), { case: 'abl' })), cell(nounForm(n('park'), { case: 'abl' }))],
          ],
          accent: ['changed'],
          note: 'evden - из дома, okuldan - из школы, otobüsten - из автобуса, parktan - из парка.',
        }),
      },
      { text: 'То же окончание - при сравнении "больше, чем": [e]vd[e]n büyük - больше дома.', rule: 'comparative-daha-den' },
      { text: 'После "его / её" появляется n: [e]v[i]nd[e]n, arab[a]s[ı]nd[a]n.', rule: 'possessive-n-buffer' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('ev'), { case: 'abl' }), 'из дома'),
        example(nounForm(n('otobüs'), { case: 'abl' }), 'из автобуса'),
        [example(nounForm(n('okul'), { poss: 2, case: 'abl' }), 'из его школы'), 'possessive-n-buffer'],
      ]),
    dtWarmup: 'DAn',
    generate: caseGenerator('ablative', 'abl', () => hardFinal, undefined, true),
  },
  {
    id: 'accusative',
    level: 'A2',
    title: 'Винительный падеж -ı / -i / -u / -ü',
    formula: '-ı · -i · -u · -ü · после гласной -yı · -yi · -yu · -yü: evi, odayı',
    rule: [
      'Окончание -[ı] / -[i] / -[u] / -[ü] ставится на предмет, о котором говорим как о конкретном: Kit[a]b[ı] okuyorum - читаю (эту) книгу. Без окончания - вообще: Kitap okuyorum - читаю книгу, занимаюсь чтением.',
      {
        text: 'Гласная - по последней гласной слова, 4 варианта. После гласной вставляется y:',
        table: () => ({
          head: ['последняя гласная', 'после согласной', 'после гласной'],
          rows: [
            [H4[0], cell(nounForm(n('ev'), { case: 'acc' })), cell(nounForm(n('kedi'), { case: 'acc' }))],
            [H4[1], cell(nounForm(n('çay'), { case: 'acc' })), cell(nounForm(n('araba'), { case: 'acc' }))],
            [H4[2], cell(nounForm(n('okul'), { case: 'acc' })), cell(nounForm({ tr: 'radyo' }, { case: 'acc' }))],
            [H4[3], cell(nounForm(n('göz'), { case: 'acc' })), cell(nounForm(n('köprü'), { case: 'acc' }))],
          ],
          accent: ['buffer'],
          note: 'ev - дом, kedi - кошка, çay - чай, araba - машина, okul - школа, radyo - радио, göz - глаз, köprü - мост.',
        }),
      },
      'p, ç, t, k перед окончанием смягчаются: kit[a]p - kit[a]b[ı], çoc[u]k - çoc[u]ğ[u].',
      { text: 'После "его / её" появляется n: [e]v[i]n[i] - его дом (кого? что?), arab[a]s[ı]n[ı].', rule: 'accusative-possessive' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('kitap'), { case: 'acc' }), 'книгу (эту)', '', ' okuyorum'),
        example(nounForm(n('araba'), { case: 'acc' }), 'машину (эту)', '', ' görüyorum'),
        [example(nounForm(n('ev'), { poss: 2, case: 'acc' }), 'его дом (винительный падеж)'), 'accusative-possessive'],
      ]),
    // Половина третьей ступени — контраст «вообще» и «тот самый»: правило про
    // него есть в объяснении, а в заданиях до сих пор не было.
    // Притяжательное + винительный (evini) — свой урок; до него на третьей
    // ступени только контраст.
    generate: (d, ctx) =>
      d === 3 && (Math.random() < 0.5 || !isPassed(ctx, 'accusative-possessive'))
        ? accusativeContrast()
        : caseGenerator('accusative', 'acc', () => [...vowelFinal, ...softNouns], 'accusative-possessive')(d, ctx),
    chatGoal: { focus: 'винительный падеж на конкретном объекте', detect: 'конкретное прямое дополнение в винительном падеже -ı/-i/-u/-ü (kitabı, arabayı)' },
  },
  {
    id: 'genitive',
    level: 'A2',
    title: 'Чей? Родительный падеж и изафет',
    formula: 'чей: -ın, после гласной -nın · что: -ı, после гласной -sı: masanın rengi',
    rule: [
      {
        text: 'Чей? - окончание -[ı]n / -[i]n / -[u]n / -[ü]n, гласная по последней гласной слова. После гласной - с n впереди:',
        table: () => ({
          head: ['последняя гласная', 'после согласной', 'после гласной'],
          rows: [
            [H4[0], cell(nounForm(n('ev'), { case: 'gen' })), cell(nounForm(n('kedi'), { case: 'gen' }))],
            [H4[1], cell(nounForm({ tr: 'kız' }, { case: 'gen' })), cell(nounForm(n('araba'), { case: 'gen' }))],
            [H4[2], cell(nounForm(n('okul'), { case: 'gen' })), cell(nounForm({ tr: 'radyo' }, { case: 'gen' }))],
            [H4[3], cell(nounForm(n('göz'), { case: 'gen' })), cell(nounForm(n('köprü'), { case: 'gen' }))],
          ],
          accent: ['buffer'],
        }),
      },
      {
        text: 'Сочетание "что-то чего-то" строится из двух окончаний: владелец получает "чей?", предмет - "его" (-ı / -sı):',
        table: () => ({
          head: ['чей?', 'что?', ''],
          rows: [
            [cell(nounForm(n('ev'), { case: 'gen' })), cell(nounForm(n('kapı'), { poss: 2 })), 'дверь дома'],
            [cell(nounForm(n('araba'), { case: 'gen' })), cell(nounForm(n('renk'), { poss: 2 })), 'цвет машины'],
            [cell(nounForm(n('okul'), { case: 'gen' })), cell(nounForm(n('bahçe'), { poss: 2 })), 'сад школы'],
          ],
          accent: ['buffer'],
        }),
      },
      {
        text: 'У местоимений свои формы - их просто запоминаем:',
        table: () => ({
          head: ['кто', 'чей', ''],
          rows: [
            ['ben', 'benim', 'мой'],
            ['sen', 'senin', 'твой'],
            ['o', 'onun', 'его / её'],
            ['biz', 'bizim', 'наш'],
            ['siz', 'sizin', 'ваш'],
            ['onlar', 'onların', 'их'],
          ],
        }),
      },
    ],
    examples: () => [
      example(nounForm(n('ev'), { case: 'gen' }), 'дверь дома', '', ' kapısı'),
      example(nounForm(n('araba'), { case: 'gen' }), 'цвет машины', '', ' rengi'),
    ],
    generate: (d, ctx) => {
      // Изафет с людьми и вещами (урок 15 A2) — после него; до него ступень 2.
      if (d <= 2 || !isPassed(ctx, 'izafet-genitive-people')) return caseGenerator('genitive', 'gen', () => vowelFinal)(Math.min(d, 2) as Difficulty, ctx)
      // Вторая половина изафета: предмет с притяжательным 3-го лица.
      const owner = pick(knownWords(owners, ctx))
      // Цвет хвалят у машины или комнаты, а не у сада или музея; у сада нет окна.
      const thing = pick([
        { w: n('kapı'), adjTr: 'açık', adjRu: 'открыта' },
        { w: n('renk'), adjTr: 'güzel', adjRu: 'красивый', only: ['araba', 'otobüs', 'ev', 'oda'] },
        { w: n('pencere'), adjTr: 'büyük', adjRu: 'большое', not: ['bahçe'] },
      ].filter((t) => (!t.only || t.only.includes(owner.tr)) && !t.not?.includes(owner.tr)))
      const ownerGen = nounForm(owner, { case: 'gen' }).word
      const ex = affix('genitive', nounForm(thing.w, { poss: 2 }), thing.w, 'изафет: предмет + -(s)ı', {
        tr: `${capitalize(ownerGen)} ___ ${thing.adjTr}.`,
        ru: `${capitalize(thing.w.ru)} ${RU_GEN[owner.tr]} ${thing.adjRu}.`,
      })
      return withRules(ex, CASE_BASE.gen, 'izafet-genitive-people')
    },
  },
  {
    id: 'instrumental',
    level: 'A1',
    title: 'С кем? Чем? -le / -la',
    formula: '-le · -la · после гласной -yle · -yla: otobüsle, taksiyle',
    rule: [
      'С кем? Чем? На чём? - окончание -l[e] / -l[a]: otob[ü]sl[e] - на автобусе, arkad[a]şl[a] - с другом. Это сокращение слова ile - "с".',
      {
        text: 'Вариантов гласной только два, e или a. После гласной вставляется y:',
        table: () => ({
          head: ['', H2[0], H2[1]],
          rows: [
            ['после согласной', cell(nounForm(n('tren'), { case: 'ins' })), cell(nounForm(n('arkadaş'), { case: 'ins' }))],
            ['после гласной', cell(nounForm(n('taksi'), { case: 'ins' })), cell(nounForm(n('araba'), { case: 'ins' }))],
          ],
          accent: ['buffer'],
          note: 'trenle - на поезде, arkadaşla - с другом, taksiyle - на такси, arabayla - на машине.',
        }),
      },
    ],
    examples: () => [
      example(nounForm(n('otobüs'), { case: 'ins' }), 'на автобусе'),
      example(nounForm(n('araba'), { case: 'ins' }), 'на машине'),
      example(nounForm(n('anne'), { poss: 0, case: 'ins' }), 'с моей мамой'),
    ],
    generate: caseGenerator('instrumental', 'ins', () => vowelFinal, undefined, true),
  },
  {
    id: 'case-choice',
    level: 'A2',
    title: 'Какой падеж просит глагол',
    formula: 'bakmak + -e · beklemek + -i · korkmak + -den',
    rule: [
      'Падежи вы уже собираете. Осталось понять, какой нужен. Выбирает его глагол, по смыслу не угадать.',
      'В русском ровно так же, просто мы этого не замечаем: "смотрю НА море" - винительный, "боюсь собакИ" - родительный, "помогаю другУ" - дательный. Никакого правила нет: пара "глагол + падеж" запоминается целиком.',
      'Турецкие пары отличаются от русских, но их немного, и половина совпадает с нашими.',
      {
        text: 'Сначала напомним, кто есть кто:',
        table: (ctx) => ({
          head: ['окончание', 'вопрос', 'пример'],
          rows: [
            ['-e / -a', 'куда? кому?', govCell(n('okul'), 'dat', ' gidiyorum', 'иду в школу')],
            ['-ı / -i / -u / -ü', 'кого? что?', govCell(n('otobüs'), 'acc', ' bekliyorum', 'жду автобус')],
            ['-den / -dan', 'откуда? от чего?', govCell(n('ev'), 'abl', ' çıkıyorum', 'выхожу из дома')],
            ['-de / -da', 'где?', govCell(n('ofis'), 'loc', ' çalışıyorum', 'работаю в офисе')],
            ['-le / -la', 'с кем? с чем?', govCell(n('tren'), 'ins', ' gidiyorum', 'еду на поезде')],
            // Родительный — свой урок (A2, 13), тема открывается раньше.
            ...(isPassed(ctx, 'genitive-intro') ? [['-in / -ın', 'чей?', govCell(n('ev'), 'gen', ' kapısı', 'дверь дома')]] : []),
          ],
          note: 'Шестой падеж, родительный -in, в этой теме не участвует: он связывает два существительных ("дверь дома"), глагол его не требует.',
        }),
      },
      {
        text: 'Глаголы, где турецкий спрашивает так же, как мы. Тут думать не о чем - ставьте то же, что поставили бы по-русски:',
        table: (ctx) => ({
          head: ['глагол', 'вопрос по-турецки', 'пример'],
          rows: passedGovVerbs(GOV_SAME, ctx).map((v) => [`${v.head}\n${v.ru}`, v.ask, govExampleCell(v)]),
        }),
      },
      {
        text: 'А вот здесь русский подводит: привычное окончание будет ошибкой. Эту таблицу и стоит выучить - она и есть вся тема:',
        table: (ctx) => ({
          head: ['глагол', 'по-русски', 'по-турецки'],
          rows: passedGovVerbs(GOV_TRAPS, ctx).map((v) => [`${v.head}\n${v.ru}`, v.trap!.says, govExampleCell(v)]),
          note: 'Если сомневаетесь, спросите так, как спросил бы турок. Для bakmak это "смотреть КУДА?", поэтому -e.',
        }),
      },
      {
        text: 'Послелоги ведут себя как глаголы: тоже требуют падежа, причём всегда одного и того же:',
        rule: 'postpositions-time-purpose',
        table: (ctx) => ({
          head: ['послелог', 'просит', 'пример'],
          rows: passedGovVerbs(GOV_POSTS, ctx).map((v) => [`${v.head}\n${v.ru}`, GOV_CELL[v.c], govExampleCell(v)]),
          note: 'Послелог запоминается вместе с падежом: -den sonra, -den önce, -e kadar.',
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(nounForm(n('deniz'), { case: 'dat' }), 'смотрю на море', '', ' bakıyorum'),
        example(nounForm(n('otobüs'), { case: 'dat' }), 'сажусь в автобус', '', ' biniyorum'),
        [example(nounForm({ tr: 'müzik', soft: 'müziğ' }, { case: 'abl' }), 'мне нравится музыка', '', ' hoşlanıyorum'), 'verbs-ablative'],
      ]),
    speedGame: '/grammar/game/gov',
    // Третья ступень — перевод: окончание не выбирают из клеток, а вспоминают.
    generate: (d, ctx) => {
      const pool = govPool(d, ctx)
      // Перевод - только фразы, чьи слова ученик уже видел карточками; если таких
      // нет, то же задание с клетками, где слово дано (тикет 21).
      const known = d === 3 ? pool.filter((it) => govWordsKnown(it, ctx)) : []
      const item = pick(known.length ? known : pool)
      // Правила темы - готовые куски «глагол + падеж» (kind formula). У глагола,
      // чей падеж объяснён уроком самого падежа (dative-case-e-a), правила темы
      // нет - тогда задание несёт правило, которое тему открывает.
      const ex = known.length ? govTranslate('case-choice', item) : govExercise('case-choice', item, ctx)
      return withRules(ex, !ex.rules?.some((r) => CASE_CHOICE_RULES.has(r)) && CASE_CHOICE_OPENER)
    },
    chatGoal: {
      focus: 'падеж, которого просит глагол',
      detect: 'дополнение в падеже, которого требует глагол (denize bakıyorum, otobüse biniyorum, müzikten hoşlanıyorum)',
    },
  },
  {
    id: 'pronoun-cases',
    level: 'A2',
    title: 'Местоимения в падежах: bana, beni, bende, benden, benimle',
    formula: 'ben - beni · bana · bende · benden · benimle',
    rule: [
      {
        text: '"Кому?" - то же -e, что и "куда?". Только два первых слова чуть меняются: ben - bana, sen - sana, а у o вставляется n: ona. Дальше всё ровно: bize, size, onlara. Annem bana çanta aldı. - Мама купила мне сумку.',
        table: () => ({ head: ['кто?', 'кому?'], rows: ALL.map((p) => [WHO[p], cell(pronounForm(p, 'dat'))]) }),
      },
      { text: 'Где русский обманывает: "я позвонил тебе" - а по-турецки звонят "кого": Seni aradım, не "Sana aradım".', rule: 'pronoun-dative' },
      {
        text: 'Местоимение меняется так же, как существительное: ev - evi, eve, evde, evden, evle, и точно так же ben - beni, bana, bende, benden, benimle. Вся система - одна таблица:',
        rule: 'pronoun-case-table',
        table: () => pronounTable([0, 1, 2]),
      },
      { text: 'Мы, вы, они - без сюрпризов:', rule: 'pronoun-case-table', table: () => pronounTable([3, 4, 5]) },
      { text: 'Сюрпризов всего три: bana и sana; n у o - onu, ona, onda, ondan; "с кем" строится от benim, которое вы знаете по benim adım: benimle, seninle, onunla, bizimle, sizinle - и только onlarla просто onlar + la.', rule: 'pronoun-case-table' },
      { text: 'Bende - "у меня, при мне": Anahtar bende - "ключ у меня", лежит в кармане. Не путайте с Anahtarım var - "у меня есть ключ", он мой.', rule: 'pronoun-case-table' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(pronounForm(0, 'dat'), 'мне - ben - bana', 'Annem ', ' çanta aldı.'),
        example(pronounForm(2, 'dat'), 'ему - у o вставляется n'),
        [example(pronounForm(0, 'loc'), 'ключ у меня', 'Anahtar '), 'pronoun-case-table'],
        [example(pronounForm(0, 'ins'), 'со мной - от benim'), 'pronoun-case-table'],
      ]),
    generate: (d, ctx) => {
      const c = pick(passedOnly(PRON_CASES, pronCaseRule, ctx))
      // 1 — правильные формы (bize, beni), 2 — неправильные (bana, onu, benimle), 3 — перевод.
      const want = ALL.filter((p) => (d === 1 ? !isOddPronoun(p, c) : d === 2 ? isOddPronoun(p, c) : true))
      const p = pick(want.length ? want : ALL)
      const frames = PRON_FRAMES[c].filter((f) => !f.not?.includes(p) && isPassed(ctx, f.rule))
      const f = pick(frames)
      const frame = { tr: f.tr, ru: f.ru.replace(/\{(\w+)\}/, (_, k: string) => RU_PRON[k][p]) }
      return frameExercise('pronoun-cases', d, frame, pronounForm(p, c), { tr: PRONOUNS[p], ru: PERSON_RU[p] }, labels(PRONOUNS[p], CASE_RU[c]), pronCaseRule(c))
    },
    chatGoal: { focus: 'местоимения в падежах', detect: 'местоимение с падежом (bana, seni, onda, benden, seninle)' },
  },
  {
    id: 'infinitive',
    level: 'A1',
    title: 'Инфинитив -mek / -mak',
    formula: 'основа + -mek / -mak',
    rule: [
      {
        text: 'Неопределённая форма ("делать") - основа + -m[e]k / -m[a]k. В словаре глаголы даны именно так.',
        table: () => ({
          head: ['последняя гласная', 'окончание', 'примеры'],
          rows: [
            [H2[0], '-m[e]k', [cell(derivedForm(vb('gel'), 'infinitive')), ', ', cell(derivedForm(vb('gör'), 'infinitive'))]],
            [H2[1], '-m[a]k', [cell(derivedForm(vb('yap'), 'infinitive')), ', ', cell(derivedForm(vb('oku'), 'infinitive'))]],
          ],
          note: 'gelmek - приходить, görmek - видеть, yapmak - делать, okumak - читать.',
        }),
      },
      'Основа - это глагол без -mek / -mak: g[e]l-, y[a]p-. Именно к основе приклеиваются все окончания времён.',
      '"Хочу делать" - неопределённая форма + istiyorum: Türkçe öğr[e]nm[e]k istiyorum. - Хочу учить турецкий.',
    ],
    examples: () => [example(derivedForm(vb('gel'), 'infinitive'), 'приходить'), example(derivedForm(vb('oku'), 'infinitive'), 'читать', 'kitap ', ' istiyorum')],
    generate: (d, ctx) => {
      // iste + istiyorum дало бы "хочу хотеть кофе".
      const v = pick(known(withComplement.filter((x) => x.stem !== 'iste'), ctx))
      // Вписать (2) и перевести (3): «Я хочу читать книгу.» - Kitap oku + mak istiyorum.
      if ((d === 2 || d === 3) && half()) {
        const inf = derivedForm(v, 'infinitive')
        const answer = `${capitalize(v.complement!.tr)} ${inf.word} istiyorum.`
        const ru = `Я хочу ${verbInfRu(v) ?? `${v.ru} ${v.complement!.ru}`}.`
        const variants = [`Ben ${v.complement!.tr} ${inf.word} istiyorum.`]
        const ex = d === 2 ? fillEnding('infinitive', { word: `${v.stem}-`, ru, answer, form: inf, ask: 'словарная форма (-mek / -mak)', variants }) : translateEnding('infinitive', { ru, answer, form: inf, variants })
        return withRules(ex, INFINITIVE, v.complement?.rule)
      }
      if (d <= 2) {
        return withRules(affix('infinitive', derivedForm(v, 'infinitive'), verbBase(v), 'инфинитив', {
          tr: `${capitalize(v.complement!.tr)} ___ istiyorum.`,
          ru: `Я хочу ${verbInfRu(v) ?? `${v.ru} ${v.complement!.ru}`}.`,
        }), INFINITIVE, v.complement?.rule)
      }
      const inf = derivedForm(v, 'infinitive')
      const answer = `Ben ${v.complement!.tr} ${inf.word} istiyorum.`
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'infinitive',
        source: verbSentence(v, 'present', 0),
        sourceRu: verbPhraseRu(v, 'present', 0) ?? verbHint(v),
        instruction: 'Скажите "я хочу ..." (istemek)',
        answer,
        variants: [withoutPronoun(answer)],
        breakdown: breakdownFrom(inf),
      }, INFINITIVE, v.complement?.rule)
    },
  },
  {
    id: 'present',
    level: 'A1',
    title: 'Настоящее время -iyor',
    formula: 'основа + -iyor + лицо: geliyorum, yapıyorsun',
    rule: [
      'Сейчас или в эти дни - основа + -[i]yor + окончание лица: g[e]l[i]yor[u]m - я иду, y[a]p[ı]yors[u]n - ты делаешь.',
      {
        text: 'Гласная перед -yor подстраивается под основу, 4 варианта. Если основа уже кончается на ı, i, u или ü - сразу -yor:',
        table: () => ({
          head: ['последняя гласная', 'основа', 'сейчас'],
          rows: [
            [H4[0], 'gel-\nприходить', cell(verbForm(vb('gel'), 'present', 2))],
            [H4[1], 'yap-\nделать', cell(verbForm(vb('yap'), 'present', 2))],
            [H4[2], 'konuş-\nговорить', cell(verbForm(vb('konuş'), 'present', 2))],
            [H4[3], 'gör-\nвидеть', cell(verbForm(vb('gör'), 'present', 2))],
            ['на гласную', 'oku-\nчитать', cell(verbForm(vb('oku'), 'present', 2))],
          ],
        }),
      },
      {
        text: 'Если основа кончается на e или a, эта гласная выпадает и меняется на i / ı:',
        table: () => ({
          head: ['основа', 'сейчас', ''],
          rows: [
            ['bekl[e]-', cell(verbForm(vb('bekle'), 'present', 2)), 'ждёт'],
            ['anl[a]-', cell(verbForm(vb('anla'), 'present', 2)), 'понимает'],
            ['dinl[e]-', cell(verbForm(vb('dinle'), 'present', 2)), 'слушает'],
          ],
        }),
      },
      { text: 'Отрицание -me / -ma перед -yor тоже меняет гласную: g[e]lm[i]yor - не идёт, y[a]pm[ı]yor - не делает. У git конечная t становится d: g[i]d[i]yor.', rule: 'present-negative-miyor' },
      {
        text: 'Окончания лица - те же, что в iyiyim и nasılsın. После -yor гласная в них всегда u (а у "они" - a):',
        table: (ctx) => ({
          head: ['', 'пью', ...(isPassed(ctx, NEG_YOR) ? ['не пью'] : [])],
          rows: byPerson([(p) => cell(verbForm(vb('iç'), 'present', p)), ...(isPassed(ctx, NEG_YOR) ? [(p: Person) => cell(verbForm(vb('iç'), 'present', p, true))] : [])]),
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'present', 0), 'я иду'),
        example(verbForm(vb('bekle'), 'present', 1), 'ты ждёшь'),
        [example(verbForm(vb('yap'), 'present', 3, true), 'мы не делаем'), 'present-negative-miyor'],
      ]),
    generate: (d, ctx) => {
      const p = person()
      // Вписать окончание к gelmek (2), перевести фразу с русского (3).
      if (d === 2 && half()) return verbTyped('present', 'present', 'fill', ctx)
      if (d === 3 && half()) return verbTyped('present', 'present', 'translate', ctx)
      if (d === 3) {
        const v = pick(known(withComplement, ctx))
        // Отрицание -mıyor — свой урок; вопрос с лицом на mi — тоже (до него — «он»).
        const negOk = isPassed(ctx, NEG_YOR)
        const mode = pick(['person', 'q', ...(negOk ? (['neg'] as const) : []), ...(negOk && ctx?.focus?.has(NEG_YOR) ? (['neg', 'neg', 'neg'] as const) : [])] as const)
        if (mode === 'person') {
          const p2 = person([p])
          return transformVerb('present', v, { tense: 'present', p }, { tense: 'present', p: p2 }, `Скажите от лица "${PRON_LOW[p2]}" (${PERSON_RU[p2]})`)
        }
        if (mode === 'neg') return withRules(transformVerb('present', v, { tense: 'present', p }, { tense: 'present', p, neg: true }, 'Сделайте отрицание'), NEG_YOR)
        const pq = pick(personsBy(miPersonRule, ctx, [0, 1, 2, 3, 4]))
        return withRules(transformVerb('present', v, { tense: 'present', p: pq }, { tense: 'present', p: pq, q: true }, 'Задайте вопрос'), miPersonRule(pq))
      }
      return tenseGenerator('present', 'present', { from: 'present', instruction: '', d2Verbs: () => [...vowelStemVerbs, vb('git')] })(d, ctx)
    },
    chatGoal: { focus: 'настоящее время на -iyor', detect: 'глагол в настоящем времени на -iyor (geliyorum, yapıyorsun)' },
  },
  {
    id: 'past',
    level: 'A1',
    title: 'Прошедшее время -di',
    formula: 'основа + -di, после глухих -ti + лицо: geldim, çalıştım',
    rule: [
      'Прошедшее время - о том, что вы видели сами или знаете точно: основа + -d[ı] / -d[i] / -d[u] / -d[ü] + окончание лица: g[e]ld[i]m - я пришёл, y[a]pt[ı]n - ты сделал.',
      {
        text: 'Окончания лица здесь короткие, свои: -m (я), -n (ты), - (он), -k (мы), -nız (вы), -lar (они):',
        table: () => ({
          head: ['', 'gel\nпришёл', 'oku\nпрочитал'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'past', p)), (p) => cell(verbForm(vb('oku'), 'past', p))]),
        }),
      },
      {
        text: 'После глухих согласных ç, f, h, k, p, s, ş, t буква d превращается в t:',
        table: () => ({
          head: ['основа', 'я ...', 'он ...'],
          rows: [vb('yap'), vb('git'), vb('iç'), vb('konuş')].map((v) => [`${v.stem}-\n${v.ru}`, cell(verbForm(v, 'past', 0)), cell(verbForm(v, 'past', 2))]),
          accent: ['changed'],
        }),
      },
      { text: 'Отрицание - -me / -ma перед -di: g[e]lm[e]d[i]m - я не пришёл, y[a]pm[a]d[ı]k - мы не сделали.', rule: 'past-tense-di-negative-question' },
      { text: 'В вопросе окончание лица остаётся на глаголе, на mi оно не переходит: Geld[i]n m[i]? - Ты пришёл?', rule: 'past-tense-di-negative-question' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'past', 0), 'я пришёл'),
        example(verbForm(vb('yap'), 'past', 3), 'мы сделали'),
        [example(verbForm(vb('oku'), 'past', 1, true), 'ты не читал'), 'past-tense-di-negative-question'],
      ]),
    dtWarmup: 'DI',
    // Вписать окончание (2) и перевод с русского (3) - половина заданий ступени.
    generate: (d, ctx) =>
      d > 1 && half()
        ? verbTyped('past', 'past', d === 2 ? 'fill' : 'translate', ctx)
        : tenseGenerator('past', 'past', { from: 'present', instruction: 'Поставьте в прошедшее время (-di)', d2Verbs: () => hardStemVerbs })(d, ctx),
    speedGame: '/grammar/game/di',
    chatGoal: { focus: 'прошедшее время на -di', detect: 'глагол в прошедшем времени на -dı/-di/-du/-dü/-tı... (geldim, yaptın)' },
  },
  {
    id: 'past-copula',
    level: 'A2',
    title: 'Был / было: -ydı, vardı / yoktu',
    formula: 'слово + -dı, после гласной -ydı + лицо · var - vardı · yok - yoktu',
    rule: [
      {
        text: '"Был" у слова - окончание -dı / -di / -du / -dü прямо к слову, без всякого "быть": uzun - uzundu - "был длинным". Правило то же, что у глаголов: гласная по последней гласной слова, после глухих согласных d оглушается в t: genç - gençti.',
        table: () => ({
          head: ['слово', 'был'],
          rows: [adj('yorgun'), adj('zengin'), adj('hazır'), adj('genç')].map((w) => [`${w.tr}\n${w.ru}`, cell(copulaForm(w, 2, 'was'))]),
          accent: ['changed'],
        }),
      },
      {
        text: 'Если слово кончается на гласную, между словом и окончанием встаёт связка y - иначе две гласные подряд: hasta - hastaydı, iyi - iyiydi.',
        table: () => ({
          head: ['слово', 'был'],
          rows: [adj('hasta'), adj('iyi'), adj('mutlu'), adj('yaşlı')].map((w) => [`${w.tr}\n${w.ru}`, cell(copulaForm(w, 2, 'was'))]),
          accent: ['buffer'],
        }),
      },
      { text: '"Есть" и "нет" в прошедшем: var - vardı - "был, имелся", yok - yoktu - "не было". Dün maç vardı. - Вчера был матч.', rule: 'vardi-yoktu' },
      {
        text: 'С окончанием лица: "я был" - hastaydım, "ты был" - hastaydın. Окончания короткие, как у -di.',
        table: (ctx) => ({
          head: ['', 'hasta\nболен', 'yorgun\nустал'],
          rows: byPerson([(p) => cell(copulaForm(adj('hasta'), p, 'was')), (p) => cell(copulaForm(adj('yorgun'), p, 'was'))], personsBy(wasRule, ctx)),
          accent: ['buffer'],
        }),
      },
      {
        text: 'То же окончание - после "где": evde - evdeydim - "я был дома". Отрицание - değil с тем же окончанием: evde değildim - "меня не было дома". Вопрос - mi, окончание переезжает на неё: Evde miydin? - "Ты был дома?"',
        rule: 'past-copula-ydi-full',
        table: () => ({
          head: ['', 'evde\nбыл дома', 'değil\nне был'],
          rows: byPerson([(p) => cell(copulaForm(AT_HOME, p, 'was')), (p) => cell(copulaForm(AT_HOME, p, 'was', { negative: true }))]),
          accent: ['buffer'],
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(copulaForm(adj('yorgun'), 2, 'was'), 'он был уставшим'),
        example(copulaForm(adj('hasta'), 0, 'was'), 'я был болен - после гласной "y"'),
        [example(copulaForm({ tr: 'var' }, 2, 'was'), 'было, имелось', 'Dün maç '), 'vardi-yoktu'],
        [example(copulaForm(AT_HOME, 3, 'was'), 'мы были дома'), 'past-copula-ydi-full'],
      ]),
    generate: (d, ctx) => {
      const p = pick(personsBy(wasRule, ctx))
      // var / yok (vardi-yoktu) - в том же уроке: «было / не было». Треть
      // заданий темы на каждой ступени (не меньше четверти темы, аудит тегов 08.10).
      if (isPassed(ctx, 'vardi-yoktu') && Math.random() < 1 / 3) return varYokExercise(d)
      if (d === 3) {
        const w = pick(knownWords(WAS_STATES, ctx))
        return copulaExercise('past-copula', 3, { w, p, form: copulaForm(w, p, 'was'), label: '', ru: '', instruction: 'Скажите в прошедшем: "был"', rules: [wasRule(p)] })
      }
      // 2 после A2 — «был дома» и «не был» (değildim).
      if (d === 2 && isPassed(ctx, 'past-copula-ydi-full') && Math.random() < 0.5) {
        const w = pick(WAS_PLACES)
        const neg = Math.random() < 0.4
        const base = neg ? { tr: 'değil', ru: 'не' } : w
        const context = { tr: `${PRON[p]} dün ${neg ? `${w.tr} ___` : '___'}.`, ru: `Вчера ${wasRu(w, p, neg)}.` }
        return withRules(affix('past-copula', copulaForm(w, p, 'was', { negative: neg }), base, labels(PRON_LOW[p], neg ? 'не был (değil + -di)' : 'был (-dı)'), context), wasRule(p), 'past-copula-ydi-full', 'locative-case')
      }
      // 1 — слово на согласную, 2 — на гласную (связка y).
      const was = knownWords(WAS_STATES, ctx)
      const split = was.filter((x) => /[aeıioöuü]$/.test(x.tr) === (d === 2))
      const w = pick(split.length ? split : was)
      return withRules(affix('past-copula', copulaForm(w, p, 'was'), w, labels(PRON_LOW[p], 'был (-dı)'), { tr: `${PRON[p]} dün ___.`, ru: `Вчера ${wasRu(w, p)}.` }), wasRule(p))
    },
    chatGoal: { focus: 'прошедшее у слова: "был", vardı / yoktu', detect: 'слово с -dı / -ydı или vardı / yoktu (hastaydım, evdeydik, vardı)' },
  },
  {
    id: 'future',
    level: 'A2',
    title: 'Будущее время -ecek / -acak',
    formula: 'основа + -ecek / -acak + лицо: geleceğim · после гласной y: okuyacağım',
    rule: [
      'Будущее - основа + -[e]c[e]k / -[a]c[a]k + окончание лица: g[e]l[e]c[e]ğ[i]m - я приду, y[a]p[a]c[a]ks[ı]n - ты сделаешь.',
      'После гласной вставляется y: bekl[e]y[e]c[e]k, ok[u]y[a]c[a]k.',
      {
        text: 'В "я" и "мы" окончание начинается с гласной, поэтому k становится ğ. В остальных лицах k остаётся:',
        table: () => ({
          head: ['', 'gel\nприду', 'yap\nсделаю'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'future', p)), (p) => cell(verbForm(vb('yap'), 'future', p))]),
          accent: ['changed'],
        }),
      },
      { text: 'Отрицание - -me / -ma перед окончанием, с y: g[e]lm[e]y[e]c[e]ğ[i]m - я не приду. Вопрос: Gelec[e]k m[i]s[i]n? - Ты придёшь?', rule: 'future-tense-negative' },
      'Глагол ye- (есть) перед y меняет e на i: y[i]y[e]c[e]ğ[i]m, y[i]y[e]c[e]k.',
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'future', 0), 'я приду'),
        example(verbForm(vb('oku'), 'future', 1), 'ты прочитаешь'),
        [example(verbForm(vb('yap'), 'future', 3, true), 'мы не сделаем'), 'future-tense-negative'],
      ]),
    generate: tenseGenerator('future', 'future', { from: 'present', instruction: 'Поставьте в будущее время (-ecek)', d2Verbs: () => vowelStemVerbs }),
    chatGoal: { focus: 'будущее время на -ecek', detect: 'глагол в будущем времени на -ecek/-acak (geleceğim, yapacaksın)' },
  },
  {
    id: 'necessity',
    level: 'A2',
    title: 'Долженствование -meli / -malı',
    formula: 'основа + -meli / -malı + лицо: gelmeliyim, yapmalısın',
    rule: [
      '"Должен" - основа + -m[e]l[i] / -m[a]l[ı] + окончание лица: g[e]lm[e]l[i]y[i]m - я должен прийти, y[a]pm[a]l[ı]s[ı]n - ты должен сделать.',
      {
        text: '-meli кончается на гласную, поэтому в "я" и "мы" вставляется y. В "ты" и "вы" - без y:',
        table: () => ({
          head: ['', 'gel\nдолжен прийти', 'yap\nдолжен сделать'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'necessity', p)), (p) => cell(verbForm(vb('yap'), 'necessity', p))]),
          accent: ['buffer'],
        }),
      },
      { text: 'Отрицание - -me / -ma перед -meli: g[e]lm[e]m[e]l[i]y[i]m - мне не следует приходить.', rule: 'obligation-mali-meli-review' },
      'Он / она - без окончания: O gelmel[i]. - Он должен прийти.',
      {
        text: 'У той же формы есть второй смысл - "должно быть, наверное": Grip olmalısın - "ты, похоже, заболел гриппом". Так говорят, когда делают вывод по признакам. Догадка почти всегда идёт через olmak, лицо ставим на olmalı:',
        rule: 'epistemic-meli',
        table: () => ({
          head: ['', 'yorgun olmak\nнаверное, устал'],
          rows: byPerson([(p) => cell(copulaForm(adj('yorgun'), p, 'guess'))]),
        }),
      },
      { text: 'Догадка о том, что уже случилось, - -mış olmalı: Sonuçlar çıkmış olmalı - "результаты, должно быть, уже готовы", unutmuş olmalıyım - "я, наверное, забыл". Прошлое прячется в -mış, лицо - на olmalı, а в прошедшее olmalı не ставят: не "çıktı olmalı".', rule: 'epistemic-mis-olmali' },
      {
        text: '"Надо было" - -malı плюс прошедшее -ydı, лицо в самом конце: Daha çok çalışmalıydım - "мне надо было больше заниматься" (а я не стал). "Не надо было" - отрицание внутри: söylememeliydin.',
        rule: 'past-necessity-maliydi',
        table: () => ({
          head: ['', 'çalışmak\nнадо было работать', 'gitmek\nнадо было пойти'],
          rows: byPerson([(p) => cell(compoundVerbForm(vb('çalış'), 'meliydi', p)), (p) => cell(compoundVerbForm(vb('git'), 'meliydi', p))]),
          accent: ['buffer'],
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'necessity', 0), 'я должен прийти'),
        example(verbForm(vb('çalış'), 'necessity', 1), 'ты должен работать'),
        [example(copulaForm(adj('yorgun'), 1, 'guess'), 'ты, наверное, устал'), 'epistemic-meli'],
        [example(compoundVerbForm(vb('git'), 'meliydi', 0), 'мне надо было пойти'), 'past-necessity-maliydi'],
      ]),
    generate: withVariants(tenseGenerator('necessity', 'necessity', { from: 'present', instruction: 'Скажите, что это нужно сделать (-meli)' }), [
      verbVariant('necessity', { rule: 'past-necessity-maliydi', label: 'надо было (-meli + -ydi)', form: (v, p) => compoundVerbForm(v, 'meliydi', p), ru: (v, p) => compoundPhraseRu(v, 'meliydi', p) }),
      verbVariant('necessity', { rule: 'past-necessity-maliydi', neg: true, label: 'не надо было (-me + -meli + -ydi)', form: (v, p) => compoundVerbForm(v, 'meliydi', p, true), ru: (v, p) => compoundPhraseRu(v, 'meliydi', p, true) }),
      verbVariant('necessity', { rule: 'epistemic-mis-olmali', label: 'наверное, уже ... (-miş olmalı)', form: (v, p) => compoundVerbForm(v, 'misOlmali', p), ru: (v, p) => compoundPhraseRu(v, 'misOlmali', p) }),
      {
        rule: 'epistemic-meli',
        make: (d, ctx) => {
          const w = pick(knownWords([...stateWords, AT_HOME, HERE], ctx))
          // Догадка о себе («я, наверное, женат») бессмысленна: только о других.
          const p = pick<Person>([1, 2, 4, 5])
          return copulaExercise('necessity', d, {
            w,
            p,
            form: copulaForm(w, p, 'guess'),
            label: 'наверное (olmalı)',
            ru: guessRu(w, p),
            instruction: 'Скажите это как догадку: "наверное" (olmalı)',
            rules: ['epistemic-meli'],
          })
        },
      },
    ]),
    chatGoal: { focus: 'долженствование -meli', detect: 'глагол с -meli/-malı (gitmeliyim)' },
  },
  {
    id: 'ability',
    level: 'A2',
    title: 'Возможность -ebil / -abil',
    formula: 'основа + -ebilir / -abilir + лицо: gelebilirim · после гласной y: okuyabilirim',
    rule: [
      '"Могу" - основа + -[e]bil / -[a]bil + окончание времени и лица: g[e]l[e]bilirim - я могу прийти, y[a]p[a]bilirsin - ты можешь сделать.',
      'После гласной вставляется y: ok[u]y[a]bilirim, bekl[e]y[e]bilir.',
      'bil - это глагол "знать", и в "могу" за ним идёт -ir (широкое время): gelebil[i]r, yapabil[i]r, okuyabil[i]r.',
      {
        text: '"Не могу" - своя форма, без bil: -[e]m[e] / -[a]m[a] + -yor и лицо: gelemiyorum, yapamıyorum. Сравните: yapmıyorum - "не делаю", yapamıyorum - "не могу сделать": разница в одной букве a.',
        rule: 'ability-negative-eme',
        table: () => ({
          head: ['', 'могу прийти', 'не могу прийти'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'ability', p)), (p) => cell(abilityForm(vb('gel'), 'cannotNow', p))]),
        }),
      },
      {
        text: 'С широким временем "не могу" звучит как "никак не могу, не смогу": -eme + -z, в "я" и "мы" без z: gelemem, gelemezsin, gelemez.',
        rule: 'aorist-negative-mez',
        table: () => ({
          head: ['', 'не могу сейчас', 'никак не могу'],
          rows: byPerson([(p) => cell(abilityForm(vb('gel'), 'cannotNow', p)), (p) => cell(abilityForm(vb('gel'), 'cannot', p))]),
        }),
      },
      'Глагол ye- (есть) перед y меняет e на i: y[i]y[e]bilirim.',
      { text: 'О дожде, опоздании, поломке -ebilir значит "может случиться": Yağmur yağabilir - "может пойти дождь". "Может и не..." - отрицание -me до -ebil, через y: gelmeyebilir - "может и не прийти".', rule: 'ability-possibility' },
      {
        text: '"Смог" - -ebil + -di: gelebildim, yapabildim. "Не смог" - -eme + -di, bil пропадает: gelemedim, yapamadım (не "gelebilmedim").',
        rule: 'ability-past',
        table: () => ({
          head: ['', 'смог', 'не смог'],
          rows: byPerson([(p) => cell(abilityForm(vb('gel'), 'could', p)), (p) => cell(abilityForm(vb('gel'), 'couldNot', p))]),
        }),
      },
      {
        text: '"Мог бы" (но не сделал) - -ebilir + -di: okuyabilirdim. "Не смог бы" - -emez + -di: okuyamazdım. Случилось - -ebildi, только могло случиться - -ebilirdi.',
        rule: 'past-possibility-ebilirdi',
        table: () => ({
          head: ['', 'мог бы прочитать', 'не смог бы прочитать'],
          rows: byPerson([(p) => cell(abilityForm(vb('oku'), 'couldHave', p)), (p) => cell(abilityForm(vb('oku'), 'couldNotHave', p))]),
          accent: ['buffer'],
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'ability', 0), 'я могу прийти'),
        example(verbForm(vb('oku'), 'ability', 1), 'ты можешь прочитать'),
        [example(abilityForm(vb('gel'), 'cannotNow', 0), 'я не могу прийти (сейчас)'), 'ability-negative-eme'],
        [example(abilityForm(vb('gel'), 'mayNot', 2), 'он может и не прийти'), 'ability-possibility'],
        [example(abilityForm(vb('yap'), 'couldNot', 0), 'я не смог сделать'), 'ability-past'],
        [example(abilityForm(vb('oku'), 'couldHave', 0), 'я мог бы прочитать'), 'past-possibility-ebilirdi'],
      ]),
    generate: (d, ctx) => {
      // «Не могу» -eme (ability-negative-eme) - своя форма со своим уроком: не
      // меньше четверти заданий темы (аудит тегов 08.10), в уроке правила - больше половины.
      const cannot = 'ability-negative-eme'
      if (isPassed(ctx, cannot) && Math.random() < (ctx?.focus?.has(cannot) ? 0.6 : 0.3)) return cannotNowExercise(d, ctx)
      return abilityGenerate(d, ctx)
    },
    chatGoal: { focus: 'возможность -ebil', detect: 'глагол с -ebil/-abil (gelebilirim)' },
  },
  {
    id: 'imperative',
    level: 'A2',
    title: 'Повелительное: gel, gelin',
    formula: 'ты: основа (gel, otur) · вы: -ın / -in / -un / -ün (gidin, oturun), после гласной y: bekleyin',
    rule: [
      'Просьба к "ты" - сама основа глагола: gel - иди сюда, otur - садись, bekle - подожди. "Не" - -me / -ma: gelme - не приходи.',
      'Просьба к "вы" или к незнакомому человеку - основа + -[i]n / -[ı]n / -[u]n / -[ü]n: g[e]l[i]n - приходите, [a]ç[ı]n - откройте, ok[u]y[u]n - читайте.',
      {
        text: 'Гласная - 4 варианта по основе. После гласной вставляется y:',
        table: () => ({
          head: ['последняя гласная', 'ты', 'вы', ''],
          rows: [
            [H4[0], cell(verbForm(vb('gel'), 'imperative', 1)), cell(verbForm(vb('gel'), 'imperative', 4)), 'приходи(те)'],
            [H4[1], cell(verbForm(vb('aç'), 'imperative', 1)), cell(verbForm(vb('aç'), 'imperative', 4)), 'открой(те)'],
            [H4[2], cell(verbForm(vb('otur'), 'imperative', 1)), cell(verbForm(vb('otur'), 'imperative', 4)), 'садись, садитесь'],
            [H4[3], cell(verbForm(vb('düşün'), 'imperative', 1)), cell(verbForm(vb('düşün'), 'imperative', 4)), 'подумай(те)'],
            ['на гласную', cell(verbForm(vb('bekle'), 'imperative', 1)), cell(verbForm(vb('bekle'), 'imperative', 4)), 'подожди(те)'],
          ],
          accent: ['buffer'],
        }),
      },
      'Отрицание - -me / -ma перед окончанием, с y: g[e]lm[e]y[i]n - не приходите, [a]çm[a]y[ı]n - не открывайте.',
      'Ещё официальнее - -iniz, так пишут в объявлениях: gel[i]n[i]z.',
      'Глагол ye- (есть) перед y меняет e на i: y[i]y[i]n - ешьте.',
    ],
    examples: () => [example(verbForm(vb('gel'), 'imperative', 4), 'приходите'), example(verbForm(vb('bekle'), 'imperative', 4), 'подождите'), example(verbForm(vb('aç'), 'imperative', 4, true), 'не открывайте')],
    generate: (d, ctx) => {
      // bilmek и sevmek в просьбе бессмысленны (Cevabı bilmeyin), их не берём.
      const v = pick(known((d === 2 ? [...vowelStemVerbs, ...withComplement] : withComplement).filter((x) => !x.stative), ctx))
      const neg = d === 2 && Math.random() < 0.5
      // «ты» (gel) и «вы» (gelin) - обе формы урока A2-25. У «ты» без «не» окончания нет,
      // поэтому в заданиях на окончание «ты» только с -me (gelme), голая основа - в «Превратите».
      const p: Person = neg && Math.random() < 0.5 ? 1 : 4
      if (d <= 2) {
        return withRules(affix('imperative', verbForm(v, 'imperative', p, neg), verbBase(v), labels(p === 1 ? 'ты' : 'вежливо (вы)', neg && 'не ...'), v.complement ? { tr: `${capitalize(v.complement.tr)} ___${p === 1 ? '!' : ', lütfen.'}`, ru: `${p === 1 ? 'Скажите другу' : 'Вежливо попросите'}: "${verbPhraseRu(v, 'imperative', p, { negative: neg }) ?? verbHint(v)}"` } : undefined), TENSE_BASE.imperative, v.complement?.rule)
      }
      const toFriend = Math.random() < 0.5
      const imp = verbForm(v, 'imperative', toFriend ? 1 : 4)
      const answer = `${capitalize(v.complement!.tr)} ${imp.word}.`
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'imperative',
        source: verbSentence(v, 'present', toFriend ? 1 : 4),
        sourceRu: verbPhraseRu(v, 'present', toFriend ? 1 : 4) ?? verbHint(v),
        instruction: toFriend ? 'Превратите в просьбу к другу ("ты")' : 'Превратите в вежливую просьбу',
        answer,
        variants: [`${answer.slice(0, -1)}, lütfen.`, `Lütfen ${v.complement!.tr} ${imp.word}.`],
        breakdown: breakdownFrom(imp),
      }, TENSE_BASE.imperative, v.complement?.rule)
    },
  },
  {
    id: 'aorist',
    level: 'A2',
    title: 'Широкое время (аорист) -r / -ir / -er',
    formula: 'основа + -r · -ar / -er · -ır / -ir / -ur / -ür + лицо: okur, yapar, gelir',
    rule: [
      'Широкое время - то, что делают обычно, всегда или вообще: Her gün çay iç[e]rim. - Каждый день пью чай. Так же задают вежливые вопросы: Bana yardım ed[e]r misiniz? - Не поможете мне?',
      'Окончание зависит от основы. После гласной - просто -r: ok[u]r, bekl[e]r.',
      'Основа из двух и более слогов на согласную - -[ı]r / -[i]r / -[u]r / -[ü]r: çal[ı]ş[ı]r, get[i]r[i]r, kon[u]ş[u]r.',
      'Основа из одного слога на согласную - -[e]r / -[a]r: y[a]p[a]r, g[i]d[e]r, [i]ç[e]r.',
      {
        text: 'Но 13 коротких глаголов берут -ir, их нужно запомнить: al, bil, bul, dur, gel, gör, kal, ol, öl, san, var, ver, vur. Все четыре случая вместе:',
        table: () => ({
          head: ['основа + окончание', 'примеры'],
          rows: [
            ['на гласную\n-r', [cell(verbForm(vb('oku'), 'aorist', 2)), ', ', cell(verbForm(vb('bekle'), 'aorist', 2))]],
            ['2 слога и больше\n-ır / -ir / -ur / -ür', [cell(verbForm(vb('çalış'), 'aorist', 2)), ', ', cell(verbForm(vb('getir'), 'aorist', 2))]],
            ['1 слог\n-ar / -er', [cell(verbForm(vb('yap'), 'aorist', 2)), ', ', cell(verbForm(vb('iç'), 'aorist', 2))]],
            ['13 исключений\n-ır / -ir / -ur / -ür', [cell(verbForm(vb('al'), 'aorist', 2)), ', ', cell(verbForm(vb('gör'), 'aorist', 2))]],
          ],
        }),
      },
      'Лицо - обычные окончания, как в "я врач": gelir[i]m, gelirs[i]n, gelir.',
      {
        text: 'Широкое время - ещё и вежливое предложение или просьба: Çay içer misiniz? - Чаю выпьете? Kapıyı kapatır mısın? - Закроешь дверь? И обещание: Yarın gelirim. - Завтра приду. Akşam ararım. - Вечером позвоню.',
        rule: 'aorist-offers-promises',
      },
      {
        text: 'О природе вещей, о том, что верно всегда: Kuşlar uçar. - Птицы летают. Su yüz derecede kaynar. - Вода кипит при ста градусах. Подлежащее во множественном, глагол в 3-м лице единственного: Kediler süt içer.',
        rule: 'aorist-general-truths',
      },
      {
        text: 'Отрицание - своя форма с -mez. В "я" и "мы" z пропадает: gelmem, gelmeyiz.',
        rule: 'aorist-negative-mez',
        table: () => ({
          head: ['', 'прихожу', 'не прихожу'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'aorist', p)), (p) => cell(verbForm(vb('gel'), 'aorist', p, true))]),
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('yap'), 'aorist', 2), 'он делает (обычно)'),
        example(verbForm(vb('gel'), 'aorist', 2), 'он приходит - исключение'),
        example(verbForm(vb('çalış'), 'aorist', 0), 'я работаю (обычно)'),
        [example(verbForm(vb('gel'), 'aorist', 0, true), 'я не прихожу'), 'aorist-negative-mez'],
      ]),
    generate: (d, ctx) => {
      // Предложения и обещания (П50) и общие истины (П53): после их уроков
      // четверть заданий, в фокусе урока - больше половины.
      const extras = (['aorist-offers-promises', 'aorist-general-truths'] as const).filter((r) => isPassed(ctx, r))
      if (extras.length) {
        const focused = extras.filter((r) => ctx?.focus?.has(r))
        const share = focused.length ? 0.6 : 0.25
        if (Math.random() < share) {
          const r = pick(focused.length ? focused : extras)
          return aoristFrame(d, pick(r === 'aorist-offers-promises' ? AORIST_OFFERS : AORIST_TRUTHS), r)
        }
      }
      const p = person()
      // Вторая ступень — всегда отрицание -mez: до его урока — первая.
      const step = d === 2 && !isPassed(ctx, NEG_RULE.aorist) ? 1 : d
      if (step === 1) return verbAffix('aorist', pick(known(Math.random() < 0.4 ? aoristExceptions : VERBS, ctx)), 'aorist', p)
      if (step === 2) return withRules(verbAffix('aorist', pick(known(VERBS, ctx)), 'aorist', p, true), NEG_RULE.aorist)
      const v = pick(known(withComplement, ctx))
      // «Обычно, всегда» - привычка: после её урока задание несёт и aorist-routine.
      return withRules(transformVerb('aorist', v, { tense: 'present', p }, { tense: 'aorist', p }, 'Скажите как привычку - "обычно, всегда" (широкое время)'), isPassed(ctx, 'aorist-routine') && 'aorist-routine')
    },
    chatGoal: { focus: 'широкое время (привычки)', detect: 'глагол в широком времени на -r/-ir/-er (içerim, gelir, yapmam)' },
  },

  // ── A2 ─────────────────────────────────────────────────────────────────────
  {
    id: 'tense-choice',
    level: 'A2',
    title: 'Какое время выбрать',
    formula: 'dün: -di · şimdi: -iyor · her gün: -ir · yarın: -ecek',
    rule: [
      'Собрать любое из четырёх времён вы уже умеете. Какое из них нужно, почти всегда подсказывает слово-маркер: dün, şimdi, her sabah, yarın.',
      {
        text: 'Один глагол во всех четырёх временах. Смотрите на первое слово:',
        table: (ctx) => ({
          head: ['когда', 'окончание', 'пример'],
          rows: passedTenses(ctx).map((t) => {
            const m = markersOf(t)[0]
            // Перевод — с маркером: у -iyor и -ir русский глагол один и тот же
            // («я пью»), и без «сейчас» / «каждый день» строки не различить.
            return [`${m.tr}\n${m.ru}`, TENSE_CELL[t], [cell(verbForm(vb('iç'), t, 0), 'çay '), `\n${m.ru} ${verbPhraseRu(vb('iç'), t, 0) ?? ''}`]]
          }),
        }),
      },
      'Чаще всего путают -iyor и -ir. По-русски "сейчас пью" и "пью каждое утро" звучат одним глаголом "пью", а по-турецки это разные времена.',
      {
        text: '-iyor - то, что происходит прямо сейчас или в эти дни. -ir - то, что верно вообще: привычка, расписание, характер:',
        table: () => ({
          head: ['', 'сейчас · -iyor', 'вообще · -ir'],
          rows: [vb('çalış'), vb('iç'), vb('yap')].map((v) => [
            `${v.stem}-\n${v.ru}`,
            cell(verbForm(v, 'present', 0)),
            cell(verbForm(v, 'aorist', 0)),
          ]),
          note: 'Şu anda çalışıyorum - сейчас работаю. Her gün çalışırım - работаю каждый день.',
        }),
      },
      {
        text: 'Маркеры, которые встретятся в заданиях:',
        table: (ctx) => ({
          head: ['время', 'слова-подсказки'],
          rows: passedTenses(ctx).map((t) => [`${TENSE_CELL[t]}\n${TENSE_NAME[t]}`, markersOf(t).map((m) => m.tr).join(', ')]),
          note: 'В живой речи о завтрашнем дне часто говорят и настоящим: "Yarın geliyorum" - приеду завтра. Это не ошибка, но в заданиях на маркер ставим будущее.',
        }),
      },
    ],
    // Урок «Грамматика A1: все времена вместе» идёт после аориста, и к нему
    // известны все четыре времени: настоящее (14), прошедшее (15–17),
    // будущее (41), аорист (49).
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('iç'), 'present', 0), 'сейчас я пью чай', 'Şimdi çay '),
        [example(verbForm(vb('iç'), 'aorist', 0), 'каждое утро я пью чай', 'Her sabah çay '), 'aorist-ir-intro'],
        example(verbForm(vb('al'), 'past', 0), 'вчера я купил хлеб', 'Dün ekmek '),
      ]),
    // Третья ступень — перевод: форму набирают сами, клеток нет.
    generate: (d, ctx) => {
      const item = tenseItem(d, ctx)
      // Выбор времени по слову-маркеру - правило урока 48 (A2); контраст -ir и
      // -iyor - своё правило урока 52, после него задания на эти два времени несут и его.
      const aoristVsPresent = (item.marker.t === 'aorist' || item.marker.t === 'present') && isPassed(ctx, 'aorist-vs-present') && 'aorist-vs-present'
      return withRules(d === 3 ? tenseTranslate('tense-choice', item) : tenseExercise('tense-choice', item, ctx), 'gecen-vs-gelecek-time-adverbs', aoristVsPresent)
    },
    chatGoal: {
      focus: 'время по слову-маркеру (dün, şimdi, her gün, yarın)',
      detect: 'время глагола, согласованное с маркером: dün: -di, her gün: -ir, yarın: -ecek',
    },
  },
  {
    id: 'comparative',
    level: 'A2',
    title: 'Сравнение: -den daha',
    formula: 'с чем + -den + daha + прилагательное: benden daha uzun',
    rule: [
      '"Дом больше, чем машина": то, с чем сравниваем, получает окончание "откуда" (-den / -dan), потом daha ("более") и прилагательное: Ev arab[a]d[a]n daha büyük.',
      {
        text: 'Порядок слов: сначала что сравниваем, потом с чем (с -den), потом daha и прилагательное:',
        table: () => ({
          head: ['что', 'чем (-den)', 'более', 'какое'],
          rows: [
            ['Ev', cell(nounForm(n('araba'), { case: 'abl' })), 'daha', 'büyük'],
            ['Kedi', cell(nounForm(n('köpek'), { case: 'abl' })), 'daha', 'küçük'],
            ['Tren', cell(nounForm(n('otobüs'), { case: 'abl' })), 'daha', 'hızlı'],
          ],
          accent: ['changed'],
          note: 'Дом больше машины. Кошка меньше собаки. Поезд быстрее автобуса.',
        }),
      },
      'daha можно опустить, смысл тот же: Ev arab[a]d[a]n büyük.',
      '"Самый" - слово en перед прилагательным: en büyük [e]v - самый большой дом.',
    ],
    examples: () => [example(nounForm(n('araba'), { case: 'abl' }), 'Дом больше, чем машина.', 'Ev ', ' daha büyük.')],
    generate: (d, ctx) => {
      // Случайные пары давали «Море моложе, чем тетрадь» — только осмысленные сравнения.
      const [aTr, bTr, adTr] = pick(knownItems(COMPARISONS, (t) => t, ctx))
      const a = n(aTr)
      const b = n(bTr)
      const ad = adj(adTr)
      const abl = nounForm(b, { case: 'abl' })
      const sentence = `${capitalize(a.tr)} ${abl.word} daha ${ad.tr}.`
      const ru = `${capitalize(a.ru)} ${ad.ruComp}, чем ${b.ru}.`
      if (d <= 2) return withRules(affix('comparative', abl, b, `сравнение: ${CASE_RU.abl}`, { tr: `${capitalize(a.tr)} ___ daha ${ad.tr}.`, ru }), COMPARATIVE, CASE_BASE.abl)
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'comparative',
        source: ru,
        instruction: 'Переведите на турецкий',
        answer: sentence,
        variants: [`${capitalize(a.tr)} ${abl.word} ${ad.tr}.`],
        breakdown: breakdownFrom(abl),
      }, COMPARATIVE, CASE_BASE.abl)
    },
  },
  {
    id: 'li-siz',
    level: 'A2',
    title: '"С" и "без": -li / -siz',
    formula: '-lı / -li / -lu / -lü · -sız / -siz / -suz / -süz: güneşli, şekersiz',
    rule: [
      '-l[i] / -l[ı] / -l[u] / -l[ü] - "с чем-то": şek[e]rl[i] çay - чай с сахаром, bahç[e]l[i] ev - дом с садом, t[u]zl[u] - солёный.',
      { text: '-s[i]z / -s[ı]z / -s[u]z / -s[ü]z - "без": şek[e]rs[i]z - без сахара, [i]şs[i]z - безработный, t[u]zs[u]z - несолёный.', rule: 'suffix-li-siz-productivity' },
      {
        text: 'Гласная - по последней гласной слова, 4 варианта, как у вопроса mi:',
        table: (ctx) => {
          // -siz — свой урок (A2): до него колонки «без …» нет.
          const siz = isPassed(ctx, SUFFIX_RULE.sIz)
          return {
            head: ['последняя гласная', 'с ...', ...(siz ? ['без ...'] : [])],
            rows: (['şeker', 'para', 'tuz', 'süt'] as const).map((tr, i) => {
              const base = { tr, ru: '' }
              return [H4[i], cell(derivForm({ base, suffix: 'lI', label: 'с ...', ru: '' })), ...(siz ? [cell(derivForm({ base, suffix: 'sIz', label: 'без ...', ru: '' }))] : [])]
            }),
            note: 'şeker - сахар, para - деньги, tuz - соль, süt - молоко.',
          }
        },
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [example(derivForm(LI_SIZ[0]), 'чай с сахаром', '', ' çay'), [example(derivForm(LI_SIZ[11]), 'безработный'), SUFFIX_RULE.sIz], example(derivForm(LI_SIZ[2]), 'кофе с молоком', '', ' kahve')]),
    generate: (d, ctx) => derivExercise('li-siz', LI_SIZ, d, ctx),
  },
  {
    id: 'lik-ci',
    level: 'A2',
    title: 'Словообразование: -lik и -ci',
    formula: '-lik: gözlük · -ci, после глухих -çi: kitapçı',
    rule: [
      '-l[i]k / -l[ı]k / -l[u]k / -l[ü]k делает из слова понятие или предмет "для этого": [i]y[i] - [i]y[i]l[i]k (доброта), g[ö]z - g[ö]zl[ü]k (очки), kit[a]p - kit[a]pl[ı]k (книжная полка).',
      { text: '-c[i] / -c[ı] / -c[u] / -c[ü] - человек по занятию: bal[ı]k - bal[ı]kç[ı] (рыбак), gazet[e] - gazet[e]c[i] (журналист), s[ü]t - s[ü]tç[ü] (молочник). После глухих согласных (ç, f, h, k, p, s, ş, t) c превращается в ç.', rule: 'productive-suffixes-ci-lik-sal-das' },
      {
        text: 'Гласная - 4 варианта, по последней гласной слова:',
        table: (ctx) => {
          // -ci — свой урок (B1): до него колонки -ci нет.
          const ci = isPassed(ctx, SUFFIX_RULE.CI)
          const rows: [number, number, number][] = [[0, 2, 17], [1, 1, 11], [2, 8, 15], [3, 0, 16]]
          return {
            head: ['последняя гласная', '-lik', ...(ci ? ['-ci'] : [])],
            rows: rows.map(([h, lik, c]) => [H4[h], cell(derivForm(LIK_CI[lik])), ...(ci ? [cell(derivForm(LIK_CI[c]))] : [])]),
            accent: ['changed'],
            note: ci
              ? 'iyilik - доброта, gazeteci - журналист, kitaplık - книжная полка, balıkçı - рыбак, çocukluk - детство, yolcu - пассажир, gözlük - очки, sütçü - молочник.'
              : 'iyilik - доброта, kitaplık - книжная полка, çocukluk - детство, gözlük - очки.',
          }
        },
      },
    ],
    examples: (ctx) => examplesFor(ctx, [example(derivForm(LIK_CI[0]), 'очки'), example(derivForm(LIK_CI[2]), 'доброта'), [example(derivForm(LIK_CI[11]), 'рыбак'), SUFFIX_RULE.CI]]),
    generate: (d, ctx) => derivExercise('lik-ci', LIK_CI, d, ctx),
  },

  // ── B1 ─────────────────────────────────────────────────────────────────────
  {
    id: 'evidential',
    level: 'B1',
    title: 'Заочное прошедшее -miş',
    formula: 'основа + -mış / -miş / -muş / -müş + лицо: gelmişim',
    rule: [
      '-m[ı]ş / -m[i]ş / -m[u]ş / -m[ü]ş - прошедшее, которое вы сами не видели: узнали от других, догадались по следам или удивлены. Ali g[e]lm[i]ş! - Оказывается, Али пришёл!',
      {
        text: 'Сравните: -di - видел сам, -miş - знаю с чужих слов или вижу только результат:',
        table: () => ({
          head: ['', 'видел сам', 'не видел'],
          rows: [
            ['он пришёл', cell(verbForm(vb('gel'), 'past', 2)), cell(verbForm(vb('gel'), 'evidential', 2))],
            ['он сделал', cell(verbForm(vb('yap'), 'past', 2)), cell(verbForm(vb('yap'), 'evidential', 2))],
            ['он прочитал', cell(verbForm(vb('oku'), 'past', 2)), cell(verbForm(vb('oku'), 'evidential', 2))],
          ],
        }),
      },
      {
        text: 'Окончания лица - как в "я врач":',
        table: () => ({
          head: ['', 'gel\nпришёл', 'yap\nсделал'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'evidential', p)), (p) => cell(verbForm(vb('yap'), 'evidential', p))]),
        }),
      },
      { text: 'Отрицание - -me / -ma перед -miş: g[e]lm[e]m[i]ş, y[a]pm[a]m[ı]ş[ı]m. Вопрос: Gelm[i]ş m[i]?', rule: 'evidential-past-mis-negative-question' },
      {
        text: '-mış умеет и удивляться - "ну надо же!": Ne kadar büyümüşsün! - "как же ты вырос!". Удивиться можно и имени - тогда -mış ставится прямо к нему, как -dı: Ali hastaymış - "Али, оказывается, болеет", Buradaymışsın! - "а, вот ты где!". После гласной - y. И в отличие от -dı, -mış не оглушается: zayıftı, но zayıfmış.',
        rule: 'evidential-past-mis-surprise',
        table: () => ({
          head: ['', 'burada\nздесь', 'yorgun\nустал'],
          rows: byPerson([(p) => cell(copulaForm(HERE, p, 'reported')), (p) => cell(copulaForm(adj('yorgun'), p, 'reported'))]),
          accent: ['buffer'],
        }),
      },
      { text: '-iyor + -muş - "говорят, сейчас...": Herkes bu diziyi izliyormuş - "говорят, все смотрят этот сериал". После -yor всегда -muş: geliyormuş, biliyormuş.', rule: 'evidential-compound-yormus' },
      {
        text: '-ecek + -miş - прогноз с чужих слов: Yarın kar yağacakmış - "завтра, говорят, пойдёт снег". Лицо - в самом конце: gidecekmişim.',
        rule: 'evidential-future-ecekmis',
        table: () => ({
          head: ['', 'говорят, едет', 'говорят, поедет'],
          rows: byPerson([(p) => cell(compoundVerbForm(vb('git'), 'iyormus', p)), (p) => cell(compoundVerbForm(vb('git'), 'ecekmis', p))]),
          accent: ['changed'],
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'evidential', 2), 'он, оказывается, пришёл'),
        example(verbForm(vb('yap'), 'evidential', 5), 'они, говорят, сделали'),
        [example(copulaForm(HERE, 1, 'reported'), 'а, вот ты где!'), 'evidential-past-mis-surprise'],
        [example(compoundVerbForm(vb('izle'), 'iyormus', 2), 'говорят, он смотрит'), 'evidential-compound-yormus'],
        [example(compoundVerbForm(vb('gel'), 'ecekmis', 2), 'говорят, он придёт'), 'evidential-future-ecekmis'],
      ]),
    generate: withVariants(tenseGenerator('evidential', 'evidential', { from: 'past', instruction: 'Перескажите с чужих слов - "говорят, оказывается" (-miş)' }), [
      verbVariant('evidential', { rule: 'evidential-compound-yormus', label: 'говорят, сейчас ... (-iyor + -muş)', form: (v, p) => compoundVerbForm(v, 'iyormus', p), ru: (v, p) => compoundPhraseRu(v, 'iyormus', p) }),
      verbVariant('evidential', { rule: 'evidential-future-ecekmis', label: 'говорят, будет ... (-ecek + -miş)', form: (v, p) => compoundVerbForm(v, 'ecekmis', p), ru: (v, p) => compoundPhraseRu(v, 'ecekmis', p) }),
      {
        rule: 'evidential-past-mis-surprise',
        make: (d, ctx) => {
          const w = pick(knownWords([...predicates.filter((x) => !x.soft), HERE], ctx))
          const p = person()
          return copulaExercise('evidential', d, {
            w,
            p,
            form: copulaForm(w, p, 'reported'),
            label: 'оказывается (-mış на имени)',
            ru: `Оказывается, ${predicateRu(w, p)}!`,
            instruction: 'Скажите с удивлением: "оказывается..." (-mış на имени)',
            rules: ['evidential-past-mis-surprise'],
          })
        },
      },
    ]),
    chatGoal: { focus: 'заочное прошедшее -miş (пересказ, удивление)', detect: 'глагол на -mış/-miş/-muş/-müş (gelmiş, yapmışsın)' },
  },
  {
    id: 'past-continuous',
    level: 'B1',
    title: 'Прошедшее длительное -iyordu',
    formula: 'основа + -iyor + -du + лицо',
    rule: [
      'Действие шло в какой-то момент в прошлом: основа + -[i]yor + -d[u] + окончание лица: g[e]l[i]yord[u]m - я как раз шёл.',
      'Чаще всего это фон, на котором случилось что-то другое: Kitap ok[u]yord[u]m, telefon çaldı. - Я читал книгу, и тут зазвонил телефон.',
      {
        text: 'После -yor всегда -du и короткие окончания прошедшего (у "они" -lardı):',
        table: () => ({
          head: ['', 'gel\nшёл', 'oku\nчитал'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'pastContinuous', p)), (p) => cell(verbForm(vb('oku'), 'pastContinuous', p))]),
        }),
      },
      'Отрицание - как в настоящем, потом -du: g[e]lm[i]yord[u]m, y[a]pm[ı]yord[u].',
    ],
    examples: () => [example(verbForm(vb('oku'), 'pastContinuous', 0), 'я читал (в тот момент)'), example(verbForm(vb('yap'), 'pastContinuous', 5), 'они делали')],
    generate: tenseGenerator('past-continuous', 'pastContinuous', { from: 'present', instruction: 'Перенесите в прошлое: "в тот момент делал" (-iyordu)' }),
    chatGoal: { focus: 'прошедшее длительное -iyordu', detect: 'глагол на -iyordu (yapıyordum, geliyordu)' },
  },
  {
    id: 'pluperfect',
    level: 'B1',
    title: 'Давнопрошедшее -mişti',
    formula: 'основа + -miş + -ti + лицо',
    rule: [
      '"Уже сделал к тому моменту" - основа + -m[i]ş + -t[i] + окончание лица: g[e]lm[i]şt[i]m - я уже пришёл (тогда).',
      'Ben geldiğimde o g[i]tm[i]şt[i]. - Когда я пришёл, он уже ушёл.',
      {
        text: 'После ş всегда глухое -tı / -ti / -tu / -tü:',
        table: () => ({
          head: ['', 'git\nуже ушёл', 'yap\nуже сделал'],
          rows: byPerson([(p) => cell(verbForm(vb('git'), 'pluperfect', p)), (p) => cell(verbForm(vb('yap'), 'pluperfect', p))]),
          accent: ['changed'],
        }),
      },
      { text: 'Отрицание - -me / -ma перед -miş: g[e]lm[e]m[i]şt[i]m - я тогда ещё не пришёл.', rule: 'pluperfect-misti-narrative' },
    ],
    examples: () => [example(verbForm(vb('git'), 'pluperfect', 2), 'он уже ушёл'), example(verbForm(vb('yap'), 'pluperfect', 3), 'мы уже сделали')],
    generate: tenseGenerator('pluperfect', 'pluperfect', { from: 'past', instruction: 'Скажите "к тому моменту уже ..." (-mişti)' }),
  },
  {
    id: 'habitual-past',
    level: 'B1',
    title: '"Раньше, бывало": -irdi',
    formula: 'широкое время + -di + лицо: giderdim, okurdum',
    rule: [
      '"Раньше, бывало" - широкое время + -d[ı] + окончание лица: Çocukken her gün parkta oyn[a]rd[ı]m. - В детстве я каждый день играл в парке.',
      {
        text: 'Сначала широкое время по его правилам, потом -dı:',
        table: () => ({
          head: ['', 'обычно', 'раньше, бывало'],
          rows: [vb('gel'), vb('yap'), vb('oku'), vb('iç')].map((v) => [`${v.stem}-\n${v.ru}`, cell(verbForm(v, 'aorist', 0)), cell(verbForm(v, 'habitualPast', 0))]),
          note: 'Все формы - от "я": gelirim - прихожу, gelirdim - (раньше) приходил.',
        }),
      },
      { text: 'Отрицание - -mez + -di: g[e]lm[e]zd[i]m - раньше я не приходил.', rule: 'habitual-past-irdi-nostalgia' },
    ],
    examples: (ctx) => examplesFor(ctx, [example(verbForm(vb('gel'), 'habitualPast', 0), 'я (бывало) приходил'), example(verbForm(vb('yap'), 'habitualPast', 3), 'мы (раньше) делали'), [example(verbForm(vb('iç'), 'habitualPast', 0, true), 'я (раньше) не пил'), NEG_RULE.habitualPast!]]),
    generate: tenseGenerator('habitual-past', 'habitualPast', { from: 'aorist', instruction: 'Скажите "раньше, бывало ..." (-irdi)' }),
    chatGoal: { focus: '"раньше, бывало" -irdi', detect: 'глагол на аорист + -dı (giderdim, yapardık)' },
  },
  {
    id: 'future-past',
    level: 'B1',
    title: '"Собирался": -ecekti',
    formula: 'основа + -ecek + -ti + лицо',
    rule: [
      '"Собирался, но..." - основа + -[e]c[e]k + -t[i] + окончание лица: g[e]l[e]c[e]kt[i]m - я собирался прийти.',
      'Часто с ama ("но"): Gelec[e]kt[i]m ama hastalandım. - Я собирался прийти, но заболел.',
      {
        text: 'Здесь k не смягчается, потому что после неё согласная t. Сравните с будущим:',
        table: () => ({
          head: ['', 'приду', 'собирался прийти'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'future', p)), (p) => cell(verbForm(vb('gel'), 'futurePast', p))]),
          accent: ['changed'],
        }),
      },
      { text: 'Отрицание - -me / -ma, с y: g[e]lm[e]y[e]c[e]kt[i]m - я не собирался приходить.', rule: 'future-in-past-acakti-unfulfilled' },
    ],
    examples: () => [example(verbForm(vb('gel'), 'futurePast', 0), 'я собирался прийти'), example(verbForm(vb('yap'), 'futurePast', 3), 'мы собирались сделать')],
    generate: tenseGenerator('future-past', 'futurePast', { from: 'future', instruction: 'Скажите "собирался ..." (-ecekti)' }),
  },
  {
    id: 'real-conditional',
    level: 'B1',
    title: 'Условие "если": -irse',
    formula: 'широкое время + -se / -sa + лицо: gelirsem, yaparsan',
    rule: [
      '"Если случится..." - широкое время + -s[e] / -s[a] + короткие окончания лица (-m, -n, - , -k, -niz): gel[i]rs[e]m - если я приду, yap[a]rs[a]n - если ты сделаешь.',
      'Слово eğer ("если") в начале можно не ставить: (Eğer) erken gel[i]rs[e]n, sinemaya gideriz. - Если придёшь рано, пойдём в кино.',
      {
        text: 'Отрицание - -mez + -se. У "они" -ler стоит перед -se:',
        table: () => ({
          head: ['', 'если приду', 'если не приду'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'realConditional', p)), (p) => cell(verbForm(vb('gel'), 'realConditional', p, true))]),
        }),
      },
      {
        text: '"Если ты болен" - одно слово: -sa / -se садится прямо на имя, никакого "быть" не нужно: hasta - hastaysan, evde - evdeysem, yorgun - yorgunsan. После гласной - y. Это то же, что в varsa и yoksa.',
        rule: 'conditional-sa-chain',
        table: () => ({
          head: ['', 'hasta\nболен', 'yorgun\nустал'],
          rows: byPerson([(p) => cell(copulaForm(adj('hasta'), p, 'if')), (p) => cell(copulaForm(adj('yorgun'), p, 'if'))]),
          accent: ['buffer'],
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('gel'), 'realConditional', 0), 'если я приду'),
        example(verbForm(vb('yap'), 'realConditional', 1), 'если ты сделаешь'),
        example(verbForm(vb('gel'), 'realConditional', 0, true), 'если я не приду'),
        [example(copulaForm(adj('hasta'), 1, 'if'), 'если ты болен, не ходи на работу', '', ', işe gitme.'), 'conditional-sa-chain'],
      ]),
    generate: withVariants((d, ctx) => {
      // «Если я приду - я тебе сообщу» бессмысленно: условие только о другом человеке.
      const p = pick<Person>([1, 2, 4, 5])
      const v = pick(known(withComplement, ctx))
      const neg = d >= 2 && Math.random() < 0.4
      return withRules(affix('real-conditional', verbForm(v, 'realConditional', p, neg), verbBase(v), labels(PRON_LOW[p], 'если', neg && 'не'), {
        tr: `${capitalize(v.complement!.tr)} ___, ${p === 4 ? 'size' : 'sana'} haber veririm.`,
        ru: `${capitalize(verbPhraseRu(v, 'realConditional', p, { negative: neg }) ?? who(p, verbHint(v)))}, я ${p === 4 ? 'вам' : 'тебе'} сообщу.`,
      }), TENSE_BASE.realConditional, v.complement?.rule)
    }, [
      {
        rule: 'conditional-sa-chain',
        make: (d, ctx) => {
          const f = pick(knownItems(NOUN_IF, (x) => [wordKey(x.w)], ctx))
          return frameExercise('real-conditional', d, f, copulaForm(f.w, f.p, 'if'), f.w, labels(PRON_LOW[f.p], 'если (-sa на имени)'), 'conditional-sa-chain')
        },
      },
    ]),
    chatGoal: { focus: 'условие "если" -irse', detect: 'условная форма на -se/-sa (gelirsem, yaparsan)' },
  },
  {
    id: 'wish-conditional',
    level: 'B1',
    title: 'Совет и пожелание: -se, -seydi',
    formula: 'основа + -se / -sa + лицо: gitsen iyi olur · -seydi / -saydı: gelseydim',
    rule: [
      'Голое -s[e] / -s[a] без широкого времени - совет или пожелание: Erken g[i]ts[e]n iyi olur. - Хорошо бы тебе пойти пораньше. Keşke burada ols[a]n! - Если бы ты был здесь!',
      {
        text: '-s[e]yd[i] / -s[a]yd[ı] - "если бы..." о прошлом, чего уже не изменить: Erken g[e]ls[e]yd[i]m, onu görürdüm. - Если бы я пришёл раньше, я бы его увидел.',
        rule: 'unreal-conditional-saydi',
        table: () => ({
          head: ['', 'вот бы прийти', 'если бы пришёл тогда'],
          rows: byPerson([(p) => cell(verbForm(vb('gel'), 'conditional', p)), (p) => cell(verbForm(vb('gel'), 'unrealConditional', p))]),
          accent: ['buffer'],
        }),
      },
      { text: 'Во второй части ("...бы") - широкое время + -di: gör[ü]rd[ü]m, gid[e]rd[i]m.', rule: 'unreal-conditional-saydi' },
      { text: '"Даже если" - то же -se плюс bile: İndirim yapsanız bile almayız - "даже если вы сделаете скидку, мы не купим". С de звучит мягче, "пусть даже": Pahalı olsa da alırım - "пусть дорого, всё равно возьму".', rule: 'concessive-sa-bile' },
      { text: 'Сомнение вслух - -se и после него mi: Valizi versem mi? - "может, сдать чемодан?", Taksiye binsek mi? - "может, возьмём такси?". Если вариантов нет вовсе - вопросительное слово, уже без mi: Ne yapsam? - "что же мне делать?"', rule: 'conditional-sa-mi-dilemma' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(verbForm(vb('git'), 'conditional', 1), 'хорошо бы тебе пойти', 'Erken ', ' iyi olur.'),
        [example(verbForm(vb('gel'), 'unrealConditional', 0), 'если бы я пришёл (тогда)'), 'unreal-conditional-saydi'],
        [example(verbForm(vb('yap'), 'conditional', 4), 'даже если вы сделаете скидку', 'İndirim ', ' bile'), 'concessive-sa-bile'],
        [example(withMi(verbForm(vb('ver'), 'conditional', 0)), 'может, сдать чемодан?', 'Valizi ', '?'), 'conditional-sa-mi-dilemma'],
      ]),
    generate: withVariants((d, ctx) => {
      const v = pick(known(withComplement, ctx))
      if (d === 1) {
        return withRules(affix('wish-conditional', verbForm(v, 'conditional', 1), verbBase(v), labels('sen', 'совет (-se)'), {
          tr: `${capitalize(v.complement!.tr)} ___ iyi olur.`,
          ru: `Хорошо бы тебе ${verbInfRu(v) ?? verbHint(v)}.`,
        }), TENSE_BASE.conditional, v.complement?.rule)
      }
      // -seydi — свой урок; до него третья ступень — пожелание с keşke.
      if (d === 2 || !isPassed(ctx, 'unreal-conditional-saydi')) {
        const p = pick<Person>([0, 1, 3])
        return withRules(affix('wish-conditional', verbForm(v, 'conditional', p), verbBase(v), labels(PRON_LOW[p], 'пожелание (keşke)'), {
          tr: `Keşke ${v.complement!.tr} ___!`,
          ru: `Вот бы ${['мне', 'тебе', 'ему', 'нам', 'вам', 'им'][p]} ${verbInfRu(v) ?? verbHint(v)}!`,
        }), TENSE_BASE.conditional, v.complement?.rule)
      }
      const p = person()
      return withRules(affix('wish-conditional', verbForm(v, 'unrealConditional', p), verbBase(v), labels(PRON_LOW[p], TENSE_RU.unrealConditional), {
        tr: `${capitalize(v.complement!.tr)} ___, her şey farklı olurdu.`,
        ru: `${capitalize(verbPhraseRu(v, 'unrealConditional', p) ?? who(p, verbHint(v)))} - всё было бы иначе.`,
      }), v.complement?.rule, 'unreal-conditional-saydi')
    }, [
      {
        rule: 'concessive-sa-bile',
        make: (d) => {
          const f = pick(CONCESSIVE)
          const v = vb(f.v)
          return frameExercise('wish-conditional', d, f, verbForm(v, 'conditional', f.p, !!f.neg), verbBase(v), labels(PRON_LOW[f.p], f.neg ? 'даже если не (-se bile)' : 'даже если (-se bile)'), TENSE_BASE.conditional, 'concessive-sa-bile')
        },
      },
      {
        rule: 'conditional-sa-mi-dilemma',
        make: (d) => {
          const f = pick(DILEMMA)
          const v = vb(f.v)
          return frameExercise('wish-conditional', d, f, withMi(verbForm(v, 'conditional', f.p, !!f.neg)), verbBase(v), labels(PRON_LOW[f.p], f.neg ? 'может, не... (-se mi?)' : 'может, ... (-se mi?)'), TENSE_BASE.conditional, 'conditional-sa-mi-dilemma')
        },
      },
    ]),
  },
  {
    id: 'optative',
    level: 'B1',
    title: '"Давайте...": -elim / -eyim',
    formula: 'основа + -eyim / -ayım · -elim / -alım: gideyim mi? · после гласной y: okuyalım',
    rule: [
      '"Давайте ..." - основа + -[e]l[i]m / -[a]l[ı]m: g[i]d[e]l[i]m - пойдём, y[a]p[a]l[ı]m - сделаем, ok[u]y[a]l[ı]m - почитаем (после гласной - y).',
      {
        text: '"...-ка я", "можно я..." - -[e]y[i]m / -[a]y[ı]m: g[e]l[e]y[i]m - приду-ка я, b[a]k[a]y[ı]m - дай-ка посмотрю.',
        table: () => ({
          head: ['', 'давайте ...', '...-ка я'],
          rows: [vb('git'), vb('gel'), vb('yap'), vb('oku')].map((v) => [`${v.stem}-\n${v.ru}`, cell(verbForm(v, 'optative', 3)), cell(verbForm(v, 'optative', 0))]),
          accent: ['buffer', 'changed'],
        }),
      },
      'С вопросом mi - вежливое предложение: Yardım ed[e]y[i]m m[i]? - Помочь?',
      'Отрицание - -me / -ma перед окончанием, между ними y: g[i]tm[e]y[e]l[i]m - давайте не пойдём, g[e]lm[e]y[e]y[i]m - не приду-ка я.',
    ],
    examples: () => [example(verbForm(vb('git'), 'optative', 3), 'давайте пойдём'), example(verbForm(vb('oku'), 'optative', 0), 'почитаю-ка я')],
    generate: (d, ctx) => {
      const v = pick(known(withComplement, ctx))
      const p = pick<Person>([0, 3])
      if (d <= 2) {
        const neg = d === 2 && Math.random() < 0.4
        return withRules(affix('optative', verbForm(v, 'optative', p, neg), verbBase(v), p === 3 ? `давайте${neg ? ' не' : ''} ...` : `...-ка я${neg ? ' не' : ''}`, {
          tr: `${capitalize(v.complement!.tr)} ___.`,
          ru: `${p === 3 ? 'Предложите' : 'Скажите о себе'}: "${verbPhraseRu(v, 'optative', p, { negative: neg }) ?? verbHint(v)}"`,
        }), TENSE_BASE.optative, v.complement?.rule)
      }
      const opt = verbForm(v, 'optative', 3)
      const answer = `${capitalize(v.complement!.tr)} ${opt.word}.`
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'optative',
        source: verbSentence(v, 'present', 3),
        sourceRu: verbPhraseRu(v, 'present', 3) ?? verbHint(v),
        instruction: 'Превратите в предложение "давайте ..."',
        answer,
        variants: [`Biz ${v.complement!.tr} ${opt.word}.`, `Hadi ${v.complement!.tr} ${opt.word}.`],
        breakdown: breakdownFrom(opt),
      }, TENSE_BASE.optative, v.complement?.rule)
    },
    chatGoal: { focus: 'предложения "давайте..." -elim', detect: 'форма -elim/-alım или -eyim/-ayım (gidelim, yapayım)' },
  },
  {
    id: 'passive',
    level: 'B1',
    title: 'Страдательный залог -il / -in / -n',
    formula: 'основа + -il, после l -in, после гласной -n: yapıldı, okundu',
    rule: [
      'Страдательный залог - "делается кем-то": основа + -[ı]l / -[i]l: y[a]p[ı]l[ı]r - делается, [a]ç[ı]l[ı]r - открывается.',
      {
        text: 'Вариант окончания зависит от конца основы:',
        table: (ctx) => {
          // Первым в курсе идёт прошедшее (yapıldı), широкое — уроком позже.
          const t = isPassed(ctx, PASSIVE_RULE.aorist) ? 'aorist' : 'past'
          return {
          head: ['основа кончается на', 'аффикс', 'пример'],
          rows: [
            ['гласную', '-n', [cell(passiveVerbForm(vb('oku'), t)), ', ', cell(passiveVerbForm(vb('bekle'), t))]],
            ['l', '-ın / -in', [cell(passiveVerbForm(vb('bil'), t)), ', ', cell(passiveVerbForm(vb('al'), t))]],
            ['другую согласную', '-ıl / -il / -ul / -ül', [cell(passiveVerbForm(vb('yap'), t)), ', ', cell(passiveVerbForm(vb('gör'), t))]],
          ],
          note: t === 'aorist' ? 'okunur - читается, beklenir - ожидается, bilinir - известно, alınır - берётся, yapılır - делается, görülür - видно.' : 'okundu - прочитан, beklendi - ожидался, bilindi - стало известно, alındı - взят, yapıldı - сделан, görüldü - замечен.',
          }
        },
      },
      { text: 'Так говорят, когда неважно или неизвестно, кто делает: Burada Türkçe kon[u]ş[u]l[u]r. - Здесь говорят по-турецки.', rule: 'passive-voice-signage' },
      { text: 'Дальше это обычный глагол, в любом времени: Kapı aç[ı]l[ı]yor (сейчас), aç[ı]l[ı]r (обычно), aç[ı]ld[ı] (в прошлом).', rule: 'passive-voice-formal-news' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        [example(passiveVerbForm(vb('aç'), 'present'), 'дверь открывается', 'Kapı '), PASSIVE_RULE.present],
        [example(passiveVerbForm(vb('konuş'), 'aorist'), 'здесь говорят по-турецки', 'Burada Türkçe '), PASSIVE_RULE.aorist],
        [example(passiveVerbForm(vb('oku'), 'aorist'), 'книга читается', 'Kitap '), PASSIVE_RULE.aorist],
        [example(passiveVerbForm(vb('yap'), 'past'), 'еда приготовлена', 'Yemek '), PASSIVE_RULE.past],
      ]),
    generate: (d, ctx) => {
      const pool = VERBS.filter((v) => v.passiveSubject)
      const v = pick(knownWords(pool, ctx))
      const subj = v.passiveSubject!
      // Времена пассива идут в курсе по одному: -ıldı, потом -ılır, потом -ılıyor.
      const tenses = passedOnly(['present', 'aorist', 'past'] as const, (t) => PASSIVE_RULE[t], ctx)
      if (d <= 2) {
        const later = tenses.filter((t) => t !== 'present')
        const tense = d === 1 ? (tenses.includes('present') ? 'present' : pick(tenses)) : pick(later.length ? later : tenses)
        const when = tense === 'present' ? 'сейчас' : tense === 'aorist' ? 'обычно' : 'в прошлом'
        const ex = affix('passive', passiveVerbForm(v, tense), verbBase(v), labels('страдательный залог', when), { tr: `${subj.tr} ___.`, ru: `${capitalize(passivePhraseRu(v, subj, tense) ?? `${subj.ru} + "${v.ru}"`)} (${when})` })
        return withRules(ex, PASSIVE_RULE[tense])
      }
      // «Biri … -iyor» → -ılıyor; до урока -ılıyor — то же в прошедшем.
      const tense = tenses.includes('present') ? 'present' : 'past'
      const tv = pick(known(pool.filter((x) => x.complement), ctx))
      const pas = passiveVerbForm(tv, tense)
      return withRules<TransformExercise>({
        kind: 'transform',
        topicId: 'passive',
        source: `Biri ${tv.complement!.tr} ${verbForm(tv, tense, 2).word}.`,
        sourceRu: verbPhraseRu(tv, tense, 2, { subject: 'кто-то' }) ?? `Кто-то: ${verbHint(tv)}`,
        instruction: 'Скажите без "кто-то" - страдательным залогом',
        answer: `${tv.passiveSubject!.tr} ${pas.word}.`,
        variants: [],
        breakdown: breakdownFrom(pas),
      }, PASSIVE_RULE[tense], tv.complement?.rule)
    },
  },
  {
    id: 'causative',
    level: 'B1',
    title: 'Понудительный залог: yaptırmak',
    formula: 'основа + -dır / -tır: yaptır · -t: beklet · -ır / -ar: bitir, çıkar + время',
    rule: [
      {
        text: 'Arabamı tamir ettim - "я сам починил машину". Arabamı tamir ettirdim - "мне починили машину", я только отдал её мастеру. Кусочек -dır / -tır внутри глагола значит "сделать чужими руками" или "заставить сделать". Он встаёт сразу после основы, дальше - знакомые окончания:',
        table: () => ({
          head: ['сам', 'чужими руками'],
          rows: ['yap', 'yaz', 'kes', 'çek', 'tamir et'].map((s) => {
            const v = causVerb(s)
            return [cell(verbForm(v, 'past', 0)), [cell(causativeForm(v, { p: 0 })), `\n${v.causRu}`]]
          }),
          accent: ['changed'],
        }),
      },
      'Гласная - по той же гармонии, что в -dı: -dır, -dir, -dur, -dür; после глухих f, s, t, k, ç, ş, h, p d превращается в t, как в yaptım. Кто делает руками - в дательном: Arabamı ustaya tamir ettirdim.',
      {
        text: 'У части глаголов кусочек другой. Основа в два слога и больше на гласную, l или r - просто -t: bekle - beklet. Короткий список односложных - -ır / -ar: bit - bitir, çık - çıkar, его учат наизусть. Два особых: gör - göster, öğren - öğret.',
        rule: 'causative-t-passive',
        table: () => ({
          head: ['основа', 'кто-то сделал'],
          rows: ['bekle', 'anla', 'yüksel', 'bit', 'düş', 'çık', 'gör', 'öğren'].map((s) => {
            const v = causVerb(s)
            return [`${v.stem}-\n${v.ru}`, [cell(causativeForm(v, { tense: 'none' })), `\n${v.causRu}`]]
          }),
        }),
      },
      { text: 'Сверху можно добавить пассив - "с ним это сделали". После каузатива основа кончается на t или r, поэтому пассив всегда -ıl: çık - çıkar - çıkarıldı - "его уволили", bekle - beklet - bekletildik - "нас заставили ждать".', rule: 'causative-t-passive' },
      { text: 'Из глагола чувства каузатив делает "вызвать чувство": gülmek - güldürmek "смешить", ağlamak - ağlatmak "доводить до слёз", korkmak - korkutmak "пугать", sevinmek - sevindirmek "радовать". Причина - подлежащее, человек - в винительном: Film beni çok güldürdü. - Фильм меня очень рассмешил.', rule: 'causative-emotion' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(causativeForm(causVerb('kes'), { p: 0 }), 'я подстригся (у мастера)', 'Saçımı '),
        example(causativeForm(causVerb('tamir et'), { p: 0 }), 'я отдал машину в ремонт', 'Arabamı '),
        [example(causativeForm(causVerb('çık'), { passive: true }), 'его уволили', 'İşten '), 'causative-t-passive'],
        [example(causativeForm(causVerb('gül')), 'фильм меня очень рассмешил', 'Film beni çok '), 'causative-emotion'],
      ]),
    generate: (d, ctx) => {
      const open = passedOnly(CAUS_FRAMES, (f) => f.rule, ctx)
      // 1 — -dır / -tır, 2 — другие кусочки и пассив, 3 — «сам → чужими руками» или перевод.
      const hard = open.filter((f) => f.passive || causVerb(f.v).caus !== 'DIr')
      const easy = open.filter((f) => !hard.includes(f))
      const pool = d === 1 && easy.length ? easy : d === 2 && hard.length ? hard : open
      const f = pick(pool)
      const v = causVerb(f.v)
      const p = f.p ?? 2
      const form = causativeForm(v, { tense: f.tense, p, passive: f.passive })
      if (d === 3 && f.self) {
        const answer = f.tr.replace('___', form.word)
        return withRules<TransformExercise>({
          kind: 'transform',
          topicId: 'causative',
          source: f.tr.replace('___', verbForm(v, f.tense ?? 'past', p).word),
          sourceRu: f.self,
          instruction: 'Скажите, что это сделали чужими руками',
          answer,
          variants: [],
          breakdown: breakdownFrom(form),
        }, f.rule)
      }
      return frameExercise('causative', d, f, form, { tr: v.stem, ru: v.ru }, labels(PRON_LOW[p], f.passive ? `${v.causRu} + пассив` : v.causRu), f.rule)
    },
    chatGoal: { focus: 'понудительный залог: "сделать чужими руками"', detect: 'глагол с -dır/-tır, -t или -ır/-ar (yaptırdım, bekletti, çıkarıldı)' },
  },
  {
    id: 'converb-sequence',
    level: 'B1',
    title: '"И" и "делая": -ip, -erek',
    formula: 'основа + -ıp / -ip / -up / -üp · -arak / -erek: gidip, koşarak · после гласной y: okuyup',
    rule: [
      '-[ı]p / -[i]p / -[u]p / -[ü]p соединяет два действия, как "и": Eve g[e]l[i]p yemek yiyorum. - Прихожу домой и ем. Время и лицо - только у последнего глагола.',
      { text: '-[e]r[e]k / -[a]r[a]k - "делая что-то, каким образом": Müzik dinl[e]y[e]r[e]k yürüyorum. - Гуляю, слушая музыку.', rule: 'converb-erek-manner' },
      {
        text: 'Гласная в -ip - 4 варианта, в -erek - 2. После гласной вставляется y:',
        table: (ctx) => {
          const erek = isPassed(ctx, DERIVED_RULE.converbErek)
          return {
            head: ['', '-ip · "и"', ...(erek ? ['-erek · "...я"'] : [])],
            rows: [vb('gel'), vb('yap'), vb('gör'), vb('oku'), vb('dinle')].map((v) => [`${v.stem}-\n${v.ru}`, cell(derivedForm(v, 'converbIp')), ...(erek ? [cell(derivedForm(v, 'converbErek'))] : [])]),
            accent: ['buffer'],
          }
        },
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(derivedForm(vb('gel'), 'converbIp'), 'прихожу домой и ем', 'Eve ', ' yemek yiyorum.'),
        [example(derivedForm(vb('dinle'), 'converbErek'), 'гуляю, слушая музыку', 'Müzik ', ' yürüyorum.'), DERIVED_RULE.converbErek!],
      ]),
    generate: (_d, ctx) => contextDerived('converb-sequence', CONVERB_SEQ, ctx),
  },
  {
    id: 'converb-time',
    level: 'B1',
    title: 'Когда, пока, до, после: -ince, -ken, -meden, -dikten sonra',
    formula: 'gelince · yaparken · yemeden önce · bitirdikten sonra',
    rule: [
      '-[i]nce / -[ı]nca / -[u]nca / -[ü]nce - "когда, как только": Eve g[e]l[i]nce seni ararım. - Как только приду домой, позвоню тебе.',
      { text: '-ken - "в то время как", обычно после широкого времени: Yemek yap[a]rk[e]n müzik dinlerim. - Пока готовлю, слушаю музыку. У -ken гласная не меняется.', rule: 'converb-ken-simultaneity' },
      { text: '-m[e]d[e]n önce / -m[a]d[a]n önce - "до того как": Yemek y[e]m[e]d[e]n önce ellerini yıka. - Перед едой вымой руки.', rule: 'converb-meden-once-before' },
      { text: '-d[i]kt[e]n sonra / -d[ı]kt[a]n sonra - "после того как": İşi bit[i]rd[i]kt[e]n sonra çay içerim. - После работы пью чай.', rule: 'converb-diktan-sonra-after' },
      {
        text: '-diğinde - ещё одно "когда", но с лицом: Eve geldiğimde - "когда я пришёл домой". Внутри - причастие -dık, "моё" и "в": geldiğ-im-de. -ince говорит "как только - и сразу", а -diğinde просто ставит два события в одно время.',
        rule: 'converb-diginde-time',
        table: () => ({
          head: ['', 'gelmek', 'yapmak'],
          rows: byPerson([(p) => cell(derivedForm(vb('gel'), 'converbDiginde', { poss: p })), (p) => cell(derivedForm(vb('yap'), 'converbDiginde', { poss: p }))]),
          accent: ['changed'],
        }),
      },
      { text: '-ene kadar - "пока не, до тех пор, пока": Rapor bitene kadar çıkmıyoruz - "не уходим, пока отчёт не будет готов". Русское "не" здесь не переводится: Ben gelene kadar bekle - "подожди, пока я не приду".', rule: 'converb-ene-kadar' },
      { text: 'Уберите из -meden önce слово önce - и получится "не сделав, без того чтобы": Yemek yemeden ilaç içmeyin - "не пейте лекарство, не поев", sormadan - "не спросив".', rule: 'converb-meden-without' },
      { text: '"Как только - сразу же" - глагол дважды: сначала в широком времени, потом в его отрицании: çıkar çıkmaz, gelir gelmez. Лица и времени у пары нет: Ders biter bitmez çıktım - "как только урок кончился, я ушёл".', rule: 'converb-ir-mez' },
      { text: '-eli (beri) - "с тех пор как": Türkiye\'ye geleli iki yıl oldu - "с тех пор как я приехал в Турцию, прошло два года". После гласной - y: başlayalı. Лица у этой формы нет.', rule: 'converb-eli-beri' },
      { text: '-dıkça - "по мере того как, чем..., тем..." и "каждый раз, когда": Fiyatlar arttıkça daha az alıyoruz. Gittikçe - та же форма от gitmek, "всё больше, постепенно".', rule: 'converb-dikca' },
      {
        text: 'У -ince, -ken, -meden, -dikten sonra нет окончаний лица - кто делает, видно по главному глаголу. Все вместе:',
        table: (ctx) => ({
          head: ['значение', 'пример'],
          rows: passedOnly(
            [
              { rule: DERIVED_RULE.converbInce, row: ['когда, как только', cell(derivedForm(vb('gel'), 'converbInce'))] },
              { rule: DERIVED_RULE.converbKen, row: ['пока, в то время как', cell(derivedForm(vb('yap'), 'converbKen'))] },
              { rule: DERIVED_RULE.converbMeden, row: ['до того как', cell(derivedForm(vb('ye'), 'converbMeden'), '', ' önce')] },
              { rule: DERIVED_RULE.converbDiktan, row: ['после того как', cell(derivedForm(vb('bitir'), 'converbDiktan'), '', ' sonra')] },
              { rule: DERIVED_RULE.converbDiginde, row: ['когда я ...', cell(derivedForm(vb('gel'), 'converbDiginde', { poss: 0 }))] },
              { rule: DERIVED_RULE.converbEne, row: ['пока не', cell(derivedForm(vb('gel'), 'converbEne'), '', ' kadar')] },
              { rule: 'converb-meden-without', row: ['не сделав', cell(derivedForm(vb('sor'), 'converbMeden'))] },
              { rule: DERIVED_RULE.converbIrMez, row: ['как только - сразу', cell(derivedForm(vb('gel'), 'converbIrMez'))] },
              { rule: DERIVED_RULE.converbEli, row: ['с тех пор как', cell(derivedForm(vb('gel'), 'converbEli'))] },
              { rule: DERIVED_RULE.converbDikca, row: ['чем..., тем...', cell(derivedForm(vb('çalış'), 'converbDikca'))] },
            ],
            (x) => x.rule,
            ctx,
          ).map((x) => x.row),
          note: 'gelince - когда приду, yaparken - пока делаю, yemeden önce - перед едой, bitirdikten sonra - закончив.',
        }),
      },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(derivedForm(vb('gel'), 'converbInce'), 'когда приду домой', 'Eve '),
        [example(derivedForm(vb('yap'), 'converbKen'), 'пока готовлю еду', 'Yemek '), DERIVED_RULE.converbKen!],
        [example(derivedForm(vb('bitir'), 'converbDiktan'), 'после того как закончу работу', 'İşi ', ' sonra'), DERIVED_RULE.converbDiktan!],
      ]),
    generate: (_d, ctx) => contextDerived('converb-time', CONVERB_TIME, ctx),
    chatGoal: { focus: 'деепричастия времени -ince, -ken, -dikten sonra', detect: 'деепричастие -ince/-ken/-meden/-dikten sonra (gelince, yaparken)' },
  },
  {
    id: 'participles',
    level: 'A2',
    title: 'Причастия: -en, -diği, -acağı',
    formula: '-en / -an: gelen adam · -dik + "чей": okuduğum kitap · -ecek + "чей": yapacağım iş',
    rule: [
      '-[e]n / -[a]n - "который делает / сделал": g[e]l[e]n adam - человек, который пришёл; kitap ok[u]y[a]n kız - девочка, которая читает книгу (после гласной - y).',
      {
        text: '-d[i]k / -d[ı]k + окончание "чей" - "который я / ты... делаю (сделал)": ok[u]d[u]ğ[u]m kitap - книга, которую я читаю; izl[e]d[i]ğ[i]n film - фильм, который ты смотришь.',
        rule: 'participle-dik-relative-clause',
        table: () => ({
          head: ['', 'книга, которую ... читаю'],
          rows: byPerson([(p) => cell(derivedForm(vb('oku'), 'participleDik', { poss: p }), '', ' kitap')]),
          accent: ['changed'],
          note: 'Окончание - то же, что в "мой, твой": kitabım - моя книга, okuduğum - который я читаю.',
        }),
      },
      { text: 'Перед окончанием "чей" k становится ğ: -dik даёт -d[i]ğ[i]m. После глухих согласных - -tik: g[i]tt[i]ğ[i]m yer - место, куда я ходил.', rule: 'participle-dik-relative-clause' },
      {
        text: '"Я знаю, что ты пришёл" турок говорит без всякого "что": Geldiğini biliyorum - дословно "знаю твоё-пришествие". Это -dık + "чей" + винительный -i: geldiğimi - "то, что я пришёл".',
        rule: 'participle-dik-noun-clause',
        table: () => ({
          head: ['', '"то, что ... пришёл"'],
          rows: byPerson([(p) => cell(derivedForm(vb('gel'), 'participleDik', { poss: p, case: 'acc' }))]),
          accent: ['changed', 'buffer'],
        }),
      },
      { text: 'Клауза на -dığı берёт падеж, который просит соседнее слово: göre и sevinmek хотят дательный - bildiğime göre "насколько я знаю", duyduğuma göre "как я слышал", Geldiğine sevindim - "рад, что ты пришёл".', rule: 'participle-dik-case-gore' },
      { text: 'Пара для будущего - -acağı: Ne yapacağımı bilmiyorum - "не знаю, что буду делать". k перед гласной становится ğ, потом лицо и нужный падеж: yap-acağ-ım-ı. Перед существительным - тоже "который": yapacağım iş - "дело, которое я сделаю".', rule: 'participle-ecek-noun-clause' },
      { text: '"Который мне нужно": Ödemem gerekiyor - "мне нужно заплатить", отсюда ödemem gereken vergi - "налог, который мне нужно заплатить". "Кому нужно" остаётся на первом глаголе: ödemem, ödemen, ödemesi.', rule: 'participle-gereken' },
    ],
    examples: (ctx) =>
      examplesFor(ctx, [
        example(derivedForm(vb('gel'), 'participleEn'), 'человек, который пришёл', '', ' adam'),
        [example(derivedForm(vb('oku'), 'participleDik', { poss: 0 }), 'книга, которую я читаю', '', ' kitap'), 'participle-dik-relative-clause'],
        [example(derivedForm(vb('gel'), 'participleDik', { poss: 1, case: 'acc' }), 'знаю, что ты пришёл', '', ' biliyorum'), 'participle-dik-noun-clause'],
        [example(derivedForm(vb('yap'), 'participleEcek', { poss: 0, case: 'acc' }), 'не знаю, что буду делать', 'Ne ', ' bilmiyorum'), 'participle-ecek-noun-clause'],
        [example(derivedForm(vb('yap'), 'verbalNounMe', { poss: 4 }), 'то, что вам нужно сделать', '', ' gereken'), 'participle-gereken'],
      ]),
    generate: (_d, ctx) => contextDerived('participles', PARTICIPLES, ctx),
  },
  // Темы базовых правил вместо мини-тем (var-yok, present-ben, necessity-lazim,
  // infinitive-use) - в отдельном файле topics-core-2.ts.
  ...CORE_TOPICS_2,
]

// Темы-генераторы и мини-темы правил без генератора (lib/grammar/mini-topics.ts,
// тикет 06 .scratch/grammar-unify) — один каталог для карты, уроков и повторения.
// Подписи с термином из более позднего урока заменяются описанием (plainLabels).
export const TOPICS: Topic[] = [...CORE_TOPICS.map((t) => ({ ...t, generate: (d: Difficulty, ctx?: GenCtx) => plainLabels(t.generate(d, ctx), ctx) })), ...MINI_TOPICS]

// Уровни идут по порядку курса; внутри уровня — порядок массива.
export const LEVELS: Level[] = ['A1', 'A2', 'B1']

// Темы модуля «Ошибки русскоговорящих» в TOPICS не входят: шаг урока, карта
// и повторение по урокам их не берут, у них нет правила в реестре. Тренировка,
// повторение по сроку и слабые места находят их здесь, по id.
export const TOPIC_BY_ID: Record<string, Topic> = Object.fromEntries([...TOPICS, ...ERROR_TOPICS].map((t) => [t.id, t]))

export function topicById(id: string | null | undefined): Topic | null {
  return (id && TOPIC_BY_ID[id]) || null
}

// Вспомогательные функции для тем в topics-core-2.ts (тот файл импортирует их
// только внутри функций: модули зависят друг от друга по кругу).
export { pick, half, person, n, vb, adj, labels, verbBase, verbHint, withoutPronoun, byPerson, WHO, H2, H4, ALL, known, personsBy, possRule, miPersonRule, examplesFor, PRON, PRON_LOW }
